// src/constants/currency.ts
var CURRENCY_CODE_RE = /^[A-Z]{3}$/;
var AMOUNT_RULES = {
  KHR: { min: 100, step: 50 },
  USD: { min: 100, step: 25 }
};
var amountRuleFor = (currency) => AMOUNT_RULES[currency ?? "KHR"] ?? AMOUNT_RULES.KHR;
var amountFollowsRule = (amount2, currency) => {
  const r = amountRuleFor(currency);
  return Number.isInteger(amount2) && amount2 >= r.min && amount2 % r.step === 0;
};
var KNOWN_CURRENCIES = {
  KHR: { symbol: "\u17DB", minorUnits: 0, symbolPosition: "after", name: "Cambodian riel" },
  USD: { symbol: "$", minorUnits: 2, symbolPosition: "before", name: "US dollar" },
  THB: { symbol: "\u0E3F", minorUnits: 2, symbolPosition: "before", name: "Thai baht" },
  VND: { symbol: "\u20AB", minorUnits: 0, symbolPosition: "after", name: "Vietnamese dong" }
};
function currencyDisplay(code, setting) {
  return setting?.definitions?.[code] ?? KNOWN_CURRENCIES[code] ?? { symbol: code, minorUnits: 2, symbolPosition: "before", name: code };
}
function enabledCurrencies(base, setting) {
  const list = (setting?.enabled ?? []).filter((c) => CURRENCY_CODE_RE.test(c));
  return [base, ...list.filter((c) => c !== base)].filter((c, i, a) => a.indexOf(c) === i);
}
function formatCurrency(minor, code, setting, opts = {}) {
  if (minor == null || !Number.isFinite(minor)) return "\u2014";
  const d = currencyDisplay(code, setting);
  const neg = minor < 0;
  const abs = Math.abs(Math.trunc(minor));
  const scale = 10 ** d.minorUnits;
  const major = Math.floor(abs / scale);
  const frac = d.minorUnits ? "." + String(abs % scale).padStart(d.minorUnits, "0") : "";
  const body = major.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + frac;
  const withSymbol = opts.symbol === false ? body : d.symbolPosition === "before" ? `${d.symbol}${body}` : `${body}${d.symbol}`;
  return neg ? `-${withSymbol}` : withSymbol;
}
function parseMoneyInput(text, code, setting) {
  const d = currencyDisplay(code, setting);
  const t = text.replace(/[,\s]/g, "").replace(d.symbol, "");
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  const [int, frac = ""] = t.replace("-", "").split(".");
  if (frac.length > d.minorUnits) return null;
  const minor = Number(int) * 10 ** d.minorUnits + Number((frac + "0".repeat(d.minorUnits)).slice(0, d.minorUnits) || "0");
  if (!Number.isSafeInteger(minor)) return null;
  return t.startsWith("-") ? -minor : minor;
}
function convertInformational(minor, quote, base, rate2) {
  const major = minor / 10 ** quote.minorUnits;
  const v = major * rate2 * 10 ** base.minorUnits;
  return Math.sign(v) * Math.round(Math.abs(v));
}

// src/constants/enums.ts
var BET_MODES = [
  "single",
  // ចាក់ធម្មតា
  "multiply",
  // ចាក់គុណ (x)
  "roll_range",
  // អូសធម្មតា
  "roll_odd",
  // អូសសេស
  "roll_even",
  // អូសគូ
  "roll_head",
  // អូសក្បាល
  "roll_mid",
  // អូសកណ្ដាល
  "roll_tail",
  // អូសកន្ទុយ
  "roll_special",
  // អូសពិសេស
  "roll_wild",
  // អូស W: the number typed with W place-holders, every W rolls 0–9 (12W → 120…129, 1WW → 100…199)
  "roll_wild_x"
  // អូស W + គុណ: the wildcard roll, then every arrangement of each number
];
var DIGIT_LENS = [2, 3];
var SESSION_STATUSES = [
  "scheduled",
  "open",
  "closing",
  "closed",
  "resulted",
  "settled",
  "cancelled"
];
var TICKET_STATUSES = ["confirmed", "held", "cancelled", "won", "lost"];
var TICKET_CHANNELS = ["app", "web"];
var TICKET_FILTER_CHANNELS = ["app", "web", "paper"];
var CLIENT_HEADER = "x-client";
var WEBAGENT_CLIENT = "webagent";
function ticketChannelOf(clientHeader, bodyChannel) {
  const h = (Array.isArray(clientHeader) ? clientHeader[0] : clientHeader)?.trim().toLowerCase();
  if (h === WEBAGENT_CLIENT || h === "web") return "web";
  return bodyChannel === "web" ? "web" : "app";
}
var RESULT_STATUSES = ["draft", "published", "amended"];
var LEDGER_TYPES = [
  "deposit",
  "withdraw",
  "bet",
  "bet_cancel",
  "win",
  "win_reverse",
  "commission",
  "adjust"
];
var USER_STATUSES = ["active", "disabled", "locked"];
var AGENT_STATUSES = ["active", "suspended"];
var DEVICE_STATUSES = ["pending", "approved", "blocked"];
var POST_KINDS = ["single", "multi"];
var ROLE_CODES = [
  "super_admin",
  "platform_admin",
  "platform_support",
  "admin",
  "manager",
  "supervisor",
  "accountant",
  "viewer",
  "agent",
  // data entry team (owner 2026-09-18): a company user who keys in the paper of many dealers
  "data_entry",
  // one data entry function each (owner 2026-09-18): betting entry, dealer list, payment record
  "data_entry_bet",
  "data_entry_dealers",
  "data_entry_money"
];
var PLATFORM_ROLE_CODES = ["super_admin", "platform_admin", "platform_support"];
var isPlatformRoleCode = (code) => PLATFORM_ROLE_CODES.includes(code);
var USER_KINDS = ["platform", "company"];
var ROLE_SCOPES = ["platform", "company"];
var TIMEZONE = "Asia/Phnom_Penh";

// src/engine/bet-rules.ts
function legacyMultiplier(cfg, postCode2, digitLen2) {
  if (!cfg) return 1;
  if ((cfg.invalid ?? []).some((x) => x.post === postCode2 && x.digitLen === digitLen2)) return null;
  return cfg.multipliers?.[postCode2]?.[String(digitLen2)] ?? 1;
}
function singleBetType(betTypes, postCode2, digitLen2) {
  return betTypes?.find((b) => b.digitLen === digitLen2 && b.posts.length === 1 && b.posts[0] === postCode2);
}
function groupBetType(betTypes, postCodes, digitLen2) {
  const set = new Set(postCodes);
  if (set.size < 2 || !betTypes?.length) return void 0;
  return betTypes.find((b) => b.digitLen === digitLen2 && b.posts.length === set.size && b.posts.every((p) => set.has(p)));
}
function betTypeMultiplier(cfg, postCode2, digitLen2) {
  if (!cfg) return 1;
  const bt = singleBetType(cfg.betTypes, postCode2, digitLen2);
  if (!bt) return legacyMultiplier(cfg, postCode2, digitLen2);
  if (bt.invalid) return null;
  return bt.multiplier.value ?? legacyMultiplier(cfg, postCode2, digitLen2);
}
var betTypeModeOf = (line2) => line2.mode === "multiply" && String(line2.input2 ?? "").replace(/\D/g, "") ? "roll_multiply" : line2.mode;
function lineRules(cfg, postCodes, digitLen2) {
  const posts = [...new Set(postCodes)];
  const types = [];
  const legacyPosts = [];
  const limits = [];
  const legacyLimit = cfg?.maxRollNumbers ?? 0;
  for (const p of posts) {
    const bt = singleBetType(cfg?.betTypes, p, digitLen2);
    if (bt) {
      types.push(bt);
      limits.push(bt.rollLimit ?? 0);
    } else {
      legacyPosts.push(p);
      limits.push(legacyLimit);
    }
  }
  const group = groupBetType(cfg?.betTypes, posts, digitLen2);
  if (group) {
    types.push(group);
    limits.push(group.rollLimit ?? 0);
  }
  const positive = limits.filter((n) => n > 0);
  return { betTypes: types, legacyPosts, rollLimit: positive.length ? Math.min(...positive) : 0, refusedByGroup: group?.invalid ? posts[0] ?? null : null };
}
function lineAmountRules(rules, product) {
  let min = product.minAmount;
  let max = product.maxAmountPerNumber;
  const steps = [product.amountStep];
  for (const bt of rules.betTypes) {
    if (bt.minAmount !== void 0 && bt.minAmount > min) min = bt.minAmount;
    if (bt.maxAmountPerNumber !== void 0 && bt.maxAmountPerNumber < max) max = bt.maxAmountPerNumber;
    if (bt.amountStep !== void 0 && !steps.includes(bt.amountStep)) steps.push(bt.amountStep);
  }
  return { minAmount: min, maxAmountPerNumber: max, steps };
}
function modeAllowedIn(allowed, mode) {
  if (allowed.includes(mode)) return true;
  if (mode === "roll_wild") return ["roll_head", "roll_mid", "roll_tail"].some((m) => allowed.includes(m));
  if (mode === "roll_wild_x") return allowed.includes("roll_multiply") || allowed.includes("multiply") && ["roll_head", "roll_mid", "roll_tail"].some((m) => allowed.includes(m));
  return false;
}
function modeRefusedBy(rules, line2) {
  const m = betTypeModeOf(line2);
  return rules.betTypes.find((bt) => !modeAllowedIn(bt.allowedModes, m)) ?? null;
}
function priceOptionsFor(cfg) {
  if (!cfg) return {};
  const base = { multiplier: (p, d) => betTypeMultiplier(cfg, p, d), maxRollNumbers: cfg.maxRollNumbers ?? void 0 };
  if (!cfg.betTypes?.length) return base;
  return { ...base, rollLimit: (posts, d) => lineRules(cfg, posts, d).rollLimit, refusedPost: (posts, d) => lineRules(cfg, posts, d).refusedByGroup };
}
var rollLimitOf = (cfg, postCodes, digitLen2) => cfg?.betTypes?.length ? lineRules(cfg, postCodes, digitLen2).rollLimit : cfg?.maxRollNumbers ?? 0;

// src/engine/line-display.ts
var ROLL_MODE_KM = {
  roll_range: "\u17A2\u17BC\u179F\u1792\u1798\u17D2\u1798\u178F\u17B6",
  roll_odd: "\u17A2\u17BC\u179F\u179F\u17C1\u179F",
  roll_even: "\u17A2\u17BC\u179F\u1782\u17BC",
  roll_head: "\u17A2\u17BC\u179F\u1780\u17D2\u1794\u17B6\u179B",
  roll_mid: "\u17A2\u17BC\u179F\u1780\u178E\u17D2\u178A\u17B6\u179B",
  roll_tail: "\u17A2\u17BC\u179F\u1780\u1793\u17D2\u1791\u17BB\u1799",
  roll_special: "\u17A2\u17BC\u179F\u1796\u17B7\u179F\u17C1\u179F",
  roll_wild: "\u17A2\u17BC\u179F",
  roll_wild_x: "\u17A2\u17BC\u179F + \u1782\u17BB\u178E"
};
var MULTIPLY_KM = "\u1782\u17BB\u178E";
var rollModeKm = (mode) => ROLL_MODE_KM[mode ?? ""] ?? "";
var digitsOf = (s) => String(s ?? "").replace(/\D/g, "");
function lineDisplay(l) {
  const a = digitsOf(l.input1);
  const b = digitsOf(l.input2);
  const len = l.digitLen === 2 || l.digitLen === 3 ? l.digitLen : a.length === 3 || b.length === 3 ? 3 : 2;
  const pad3 = (n) => String(n).padStart(len, "0");
  const withDigit = (s, pos, digit) => s.slice(0, pos) + String(digit) + s.slice(pos + 1);
  const one = (from) => ({ from, to: null, modeKm: null });
  const mode = l.mode ?? "";
  switch (mode) {
    case "single":
      return one(a || (l.label ?? ""));
    case "multiply": {
      if (!b || b.length !== a.length) return one(`${a}x`);
      const i = [...a].findIndex((ch, k) => ch !== b[k]);
      const pos = Math.max(0, i);
      const lo = Math.min(Number(a[pos]), Number(b[pos]));
      const hi = Math.max(Number(a[pos]), Number(b[pos]));
      const roll = i === 0 ? ROLL_MODE_KM.roll_head : i === a.length - 1 ? ROLL_MODE_KM.roll_tail : ROLL_MODE_KM.roll_mid;
      return { from: `${withDigit(a, pos, lo)}x`, to: `${withDigit(a, pos, hi)}x`, modeKm: `${roll} + ${MULTIPLY_KM}` };
    }
    case "roll_range":
    case "roll_odd":
    case "roll_even": {
      if (!a || !b) return { from: a || b, to: null, modeKm: rollModeKm(mode) };
      const x = Number(a);
      const y = Number(b);
      return { from: pad3(Math.min(x, y)), to: pad3(Math.max(x, y)), modeKm: rollModeKm(mode) };
    }
    case "roll_head":
    case "roll_mid":
    case "roll_tail": {
      const pos = mode === "roll_head" ? 0 : mode === "roll_mid" ? len === 3 ? 1 : 0 : len - 1;
      if (a.length <= pos) return { from: a, to: null, modeKm: rollModeKm(mode) };
      const s = Number(a[pos]);
      const e = b.length > pos ? Number(b[pos]) : 9;
      return { from: withDigit(a, pos, Math.min(s, e)), to: withDigit(a, pos, Math.max(s, e)), modeKm: rollModeKm(mode) };
    }
    case "roll_wild":
    case "roll_wild_x": {
      const pat = String(l.input1 ?? "").toUpperCase().replace(/អ/g, "W").replace(/[^0-9W]/g, "");
      const ws = [...pat].map((ch, i) => ch === "W" ? i : -1).filter((i) => i >= 0);
      const name2 = ws.length === 1 ? ws[0] === 0 ? ROLL_MODE_KM.roll_head : ws[0] === pat.length - 1 ? ROLL_MODE_KM.roll_tail : ROLL_MODE_KM.roll_mid : ROLL_MODE_KM.roll_wild;
      return { from: pat.replace(/W/g, "0"), to: pat.replace(/W/g, "9"), modeKm: mode === "roll_wild_x" ? `${name2} + ${MULTIPLY_KM}` : name2 };
    }
    case "roll_special": {
      let lo = a ? Number(a) : 0;
      let hi = b ? Number(b) : 10 ** len - 1;
      if (lo > hi) [lo, hi] = [hi, lo];
      return { from: pad3(lo), to: pad3(hi), modeKm: ROLL_MODE_KM.roll_special };
    }
    default:
      return one(l.label || a);
  }
}
function lineLabel(l) {
  const v = lineDisplay(l);
  return [v.to === null ? v.from : `${v.from} | ${v.to}`, v.modeKm].filter(Boolean).join(" ");
}

// src/engine/expand.ts
var MAX_NUMBERS_PER_LINE = 1e3;
var MAX_MULTIPLY_DIGITS = 6;
var rollLimitMessageKm = (max) => `\u179B\u17C1\u1781\u1785\u17B6\u1780\u17CB\u17A2\u17BC\u179F\u1798\u17B7\u1793\u17A2\u17B6\u1785\u179B\u17BE\u179F\u1796\u17B8 ${max} \u179B\u17C1\u1781`;
var onlyDigits = (s) => String(s ?? "").replace(/\D/g, "");
var WILD_KM = "\u17A2";
var wildPattern = (s) => String(s ?? "").toUpperCase().replace(/អ/g, "W").replace(/[^0-9W]/g, "");
var pad = (n, w) => String(n).padStart(w, "0");
function fail(code, message) {
  return { ok: false, code, message };
}
function permutations(str) {
  const out = /* @__PURE__ */ new Set();
  const rec = (rest, cur) => {
    if (rest.length === 0) {
      out.add(cur);
      return;
    }
    for (let i = 0; i < rest.length; i++) {
      rec([...rest.slice(0, i), ...rest.slice(i + 1)], cur + rest[i]);
    }
  };
  rec(str.split(""), "");
  return [...out].sort();
}
function arrangements(digits, k) {
  const out = [];
  const rec = (rest, cur) => {
    if (cur.length === k) {
      out.push(cur);
      return;
    }
    for (let i = 0; i < rest.length; i++) {
      rec([...rest.slice(0, i), ...rest.slice(i + 1)], cur + rest[i]);
    }
  };
  rec(digits, "");
  return [...new Set(out)].sort();
}
function checkWidth(digits, digitLen2) {
  if (!digits) return fail("INVALID_NUMBER", "number is empty");
  if (digits.length !== digitLen2) {
    return fail("INVALID_NUMBER", `expected ${digitLen2} digits, got ${digits.length}`);
  }
  return null;
}
function rollPosition(digits, pos, _digitLen, end = 9) {
  let start = Number(digits[pos]);
  if (end < start) [start, end] = [end, start];
  const out = [];
  for (let d = start; d <= end; d++) {
    out.push(digits.slice(0, pos) + String(d) + digits.slice(pos + 1));
  }
  return out;
}
function withEnd(a, b, pos, digitLen2, name2, label) {
  const ea = checkWidth(a, digitLen2);
  if (ea) return ea;
  if (!b) return { ok: true, numbers: rollPosition(a, pos, digitLen2), label };
  const e = checkWidth(b, digitLen2);
  if (e) return e;
  if ([...a].some((ch, i) => i !== pos && ch !== b[i])) return fail("INVALID_NUMBER", `${name2}: only that digit may change`);
  return { ok: true, numbers: rollPosition(a, pos, digitLen2, Number(b[pos])), label };
}
function expand(input, opts = {}) {
  const digitLen2 = input.digitLen;
  if (digitLen2 !== 2 && digitLen2 !== 3) return fail("INVALID_NUMBER", "digitLen must be 2 or 3");
  const a = onlyDigits(input.input1);
  const b = onlyDigits(input.input2);
  const wild = input.mode === "roll_wild" || input.mode === "roll_wild_x";
  const pattern = wildPattern(input.input1);
  const label = lineLabel({ mode: input.mode, input1: wild ? pattern : a, input2: b, digitLen: digitLen2 });
  switch (input.mode) {
    case "roll_wild":
    case "roll_wild_x": {
      if (pattern.length !== digitLen2) return fail("INVALID_NUMBER", `expected ${digitLen2} places, got ${pattern.length}`);
      if (!pattern.includes("W")) return fail("INVALID_NUMBER", "a wildcard roll needs a W");
      let numbers = [""];
      for (const ch of pattern) numbers = numbers.flatMap((n) => ch === "W" ? Array.from({ length: 10 }, (_, d) => n + String(d)) : [n + ch]);
      if (input.mode === "roll_wild_x") numbers = [...new Set(numbers.flatMap((n) => arrangements(n.split(""), digitLen2)))].sort();
      if (opts.maxRollNumbers && numbers.length > opts.maxRollNumbers) return fail("TOO_MANY_NUMBERS", rollLimitMessageKm(opts.maxRollNumbers));
      if (numbers.length > MAX_NUMBERS_PER_LINE) return fail("TOO_MANY_NUMBERS", "too many numbers");
      return { ok: true, numbers, label };
    }
    case "single": {
      const err = checkWidth(a, digitLen2);
      if (err) return err;
      return { ok: true, numbers: [a], label };
    }
    case "multiply": {
      if (!a) return fail("INVALID_NUMBER", "number is empty");
      if (b) {
        if (a.length !== digitLen2 || b.length !== digitLen2) return fail("INVALID_NUMBER", `expected ${digitLen2} digits`);
        const diff = [...a].map((ch, i) => ch !== b[i] ? i : -1).filter((i) => i >= 0);
        if (diff.length > 1) return fail("INVALID_NUMBER", "roll + multiply: the two numbers must differ in one digit");
        const rolled = diff.length ? rollPosition(a, diff[0], digitLen2, Number(b[diff[0]])) : [a];
        const numbers2 = [...new Set(rolled.flatMap((n) => arrangements(n.split(""), digitLen2)))].sort();
        if (numbers2.length > MAX_NUMBERS_PER_LINE) return fail("TOO_MANY_NUMBERS", "too many numbers");
        return { ok: true, numbers: numbers2, label };
      }
      if (a.length < digitLen2) return fail("INVALID_NUMBER", `need at least ${digitLen2} digits`);
      if (a.length > MAX_MULTIPLY_DIGITS) return fail("INVALID_NUMBER", `multiply accepts at most ${MAX_MULTIPLY_DIGITS} digits`);
      const numbers = arrangements(a.split(""), digitLen2);
      return { ok: true, numbers, label };
    }
    case "roll_range":
    case "roll_odd":
    case "roll_even": {
      const e1 = checkWidth(a, digitLen2);
      if (e1) return e1;
      const e2 = checkWidth(b, digitLen2);
      if (e2) return e2;
      let lo = Number(a);
      let hi = Number(b);
      if (lo > hi) [lo, hi] = [hi, lo];
      const numbers = [];
      for (let n = lo; n <= hi; n++) {
        if (input.mode === "roll_odd" && n % 2 === 0) continue;
        if (input.mode === "roll_even" && n % 2 !== 0) continue;
        numbers.push(pad(n, digitLen2));
      }
      if (opts.maxRollNumbers && numbers.length > opts.maxRollNumbers) return fail("TOO_MANY_NUMBERS", rollLimitMessageKm(opts.maxRollNumbers));
      if (numbers.length > MAX_NUMBERS_PER_LINE) return fail("TOO_MANY_NUMBERS", "too many numbers");
      return { ok: true, numbers, label };
    }
    case "roll_head":
      return withEnd(a, b, 0, digitLen2, "\u1780\u17D2\u1794\u17B6\u179B", label);
    case "roll_mid":
      return withEnd(a, b, digitLen2 === 3 ? 1 : 0, digitLen2, "\u1780\u178E\u17D2\u178A\u17B6\u179B", label);
    case "roll_tail":
      return withEnd(a, b, digitLen2 - 1, digitLen2, "\u1780\u1793\u17D2\u1791\u17BB\u1799", label);
    case "roll_special": {
      const all = Array.from({ length: 10 }, (_, d) => String(d).repeat(digitLen2));
      if (!a && !b) return { ok: true, numbers: all, label };
      const e1 = a ? checkWidth(a, digitLen2) : null;
      if (e1) return e1;
      const e2 = b ? checkWidth(b, digitLen2) : null;
      if (e2) return e2;
      let lo = a ? Number(a) : 0;
      let hi = b ? Number(b) : 10 ** digitLen2 - 1;
      if (lo > hi) [lo, hi] = [hi, lo];
      const numbers = all.filter((n) => Number(n) >= lo && Number(n) <= hi);
      if (!numbers.length) return fail("INVALID_NUMBER", "no repeated-digit number in the range");
      return { ok: true, numbers, label };
    }
    default:
      return fail("INVALID_MODE", `unknown mode ${String(input.mode)}`);
  }
}
function inferDigitLen(input1) {
  const d = wildPattern(input1);
  if (d.length === 2) return 2;
  if (d.length === 3) return 3;
  return null;
}

// src/engine/pad.ts
var effectiveAmount = (amount2, cutZero, currency = "KHR") => {
  if (currency === "USD") return Math.round(Number(amount2 || 0) * 100);
  const n = Number(amount2 || 0);
  return cutZero ? n * 100 : n;
};
var AMOUNT_MIN = AMOUNT_RULES.KHR.min;
var AMOUNT_STEP = AMOUNT_RULES.KHR.step;
function amountStepError(amount2, currency = "KHR") {
  if (amountFollowsRule(amount2, currency)) return null;
  return currency === "USD" ? "\u1791\u17B9\u1780\u1794\u17D2\u179A\u17B6\u1780\u17CB\u1799\u17C9\u17B6\u1784\u178F\u17B7\u1785 $1 \u1793\u17B7\u1784\u178F\u17D2\u179A\u17BC\u179C\u1787\u17B6\u1782\u17BB\u178E\u1793\u17C3 $0.25 (1, 1.25, 1.50 \u2026)" : "\u1791\u17B9\u1780\u1794\u17D2\u179A\u17B6\u1780\u17CB\u1799\u17C9\u17B6\u1784\u178F\u17B7\u1785 100\u17DB \u1793\u17B7\u1784\u178F\u17D2\u179A\u17BC\u179C\u1787\u17B6\u1782\u17BB\u178E\u1793\u17C3 50\u17DB (100, 150, 200 \u2026)";
}
function padLine(s) {
  if (s.mode === "roll" && s.rollSub === "roll_wild") {
    const pattern = s.input1.toUpperCase().replace(/អ/g, "W").replace(/[^0-9W]/g, "");
    const digitLen3 = pattern.length === 3 ? 3 : pattern.length === 2 ? 2 : s.digitLen;
    return { mode: s.rollMultiply ? "roll_wild_x" : "roll_wild", input1: pattern, input2: null, digitLen: digitLen3, amountPerNumber: effectiveAmount(s.amount, s.cutZero, s.currency) };
  }
  const rollX = s.mode === "roll" && s.rollMultiply && ["roll_head", "roll_mid", "roll_tail"].includes(s.rollSub) && !!s.input2;
  const mode = rollX ? "multiply" : s.mode === "roll" ? s.rollSub : s.mode;
  const digitLen2 = s.mode === "multiply" ? s.digitLen : inferDigitLen(s.input1) ?? s.digitLen;
  return { mode, input1: s.input1, input2: s.mode === "roll" && s.input2 ? s.input2 : null, digitLen: digitLen2, amountPerNumber: effectiveAmount(s.amount, s.cutZero, s.currency) };
}
function padEntryFits(s) {
  const a = s.input1.toUpperCase().replace(/អ/g, "W");
  if (!a) return true;
  const wild = /W/.test(a);
  if (s.mode === "roll" && s.rollSub === "roll_wild") return /^[0-9W]{2,3}$/.test(a);
  if (wild) return false;
  if (s.mode === "multiply") return /^\d+$/.test(a) && a.length >= s.digitLen && a.length <= MAX_MULTIPLY_DIGITS;
  if (!/^\d{2,3}$/.test(a)) return false;
  if (s.mode === "roll" && s.input2 && s.input2.length !== a.length) return false;
  return true;
}
var multiplierSumOf = (postCodes, digitLen2, multiplier) => [...new Set(postCodes)].reduce((a, p) => a + (multiplier ? multiplier(p, digitLen2) ?? 0 : 1), 0);
var lineTotal = (numberCount, postCodes, digitLen2, amountPerNumber, multiplier) => numberCount * multiplierSumOf(postCodes, digitLen2, multiplier) * amountPerNumber;

// src/engine/price.ts
function priceSlip(lines, opts = {}) {
  const priced = [];
  let total = 0;
  let numberCount = 0;
  for (let i = 0; i < lines.length; i++) {
    const line2 = lines[i];
    const posts = [...new Set(line2.postCodes)];
    const ex = expand(line2, { maxRollNumbers: opts.rollLimit ? opts.rollLimit(posts, line2.digitLen) : opts.maxRollNumbers });
    if (!ex.ok) return { ok: false, error: { ...ex, lineIndex: i } };
    let multSum = 0;
    for (const p of posts) {
      const m = opts.multiplier ? opts.multiplier(p, line2.digitLen) : 1;
      if (m === null) return { ok: false, error: { ok: false, code: "INVALID_POST_DIGIT", message: `${p} ${line2.digitLen}D is not allowed`, postCode: p, lineIndex: i } };
      multSum += m;
    }
    const refused = opts.refusedPost?.(posts, line2.digitLen) ?? null;
    if (refused !== null) return { ok: false, error: { ok: false, code: "INVALID_POST_DIGIT", message: `${posts.join(" ")} ${line2.digitLen}D is not allowed`, postCode: refused, lineIndex: i } };
    const lineTotal2 = lineTotal(ex.numbers.length, posts, line2.digitLen, line2.amountPerNumber, opts.multiplier);
    priced.push({
      ...line2,
      postCodes: posts,
      numbers: ex.numbers,
      label: ex.label,
      numberCount: ex.numbers.length,
      postCount: posts.length,
      multiplierSum: multSum,
      lineTotal: lineTotal2
    });
    total += lineTotal2;
    numberCount += ex.numbers.length * posts.length;
  }
  return { ok: true, slip: { lines: priced, total, numberCount } };
}

// src/engine/result-sheet.ts
var POST_ORDER = ["B", "C", "D", "F", "I", "N", "K", "L", "O", "P", "Z", "V"];
var A_ROWS = ["A", "A1", "A2", "A3", "A4"];
var sheetDmy = (d) => d.split("-").reverse().join("/");
var line = (n) => ({ d2: n.d2 ?? n.full.slice(-2), d3: n.d3 });
var sheetDash = (l) => l.d3 ? `${l.d2} - ${l.d3}` : l.d2;
function prizeRows(sheet) {
  if (!sheet) return [];
  if (sheet.length >= 27) return sheet.slice(1, 20).map((v, i) => ({ n: i + 1, v })).reverse();
  if (sheet.length >= 17) return sheet.slice(2, 17).map((v, i) => ({ n: i + 1, v }));
  if (sheet.length === 15) return sheet.slice(2, 14).map((v, i) => ({ n: i + 1, v }));
  return [];
}
function sheetModel(r, sheetIdx, withLo) {
  const by = new Map(r.entries.map((e) => [e.postCode, e.numbers]));
  let aRows = A_ROWS.filter((c) => by.has(c) && (c !== "A" || !by.has("A1"))).map((c) => ({ label: c, lines: by.get(c).map(line) }));
  if (aRows.length === 1 && aRows[0].lines.length > 1) aRows = aRows[0].lines.map((l, i) => ({ label: `A${i + 1}`, lines: [l] }));
  const posts = POST_ORDER.filter((c) => by.has(c)).map((c) => ({ label: c, line: line(by.get(c)[0]) }));
  const pl = ["P1", "P2", "P3", "P4"].filter((c) => by.has(c)).map((c) => line(by.get(c)[0]));
  const lines = pl.length ? pl : (by.get("3P") ?? []).map(line);
  const lo = by.get("Lo") ?? [];
  const postsFirst = (r.sheets?.[0]?.length ?? 0) >= 27;
  const rows = prizeRows(r.sheets?.[0]);
  const aLines = aRows.flatMap((a) => a.lines);
  const bcd2 = posts.filter((p) => ["B", "C", "D"].includes(p.label)).map((p) => p.line);
  const tail = (s, n) => s && s.length >= n ? s.slice(-n) : null;
  const cand = (n) => {
    const a = [...aLines.map((l) => tail(l.d2, n)), ...aLines.map((l) => tail(l.d3, n))];
    const p = [...bcd2.map((l) => tail(l.d2, n)), ...bcd2.map((l) => tail(l.d3, n))];
    const rr = rows.map((x) => tail(x.v, n));
    return [...a, ...postsFirst ? [...p, ...rr] : [...rr, ...p]].filter((x) => !!x);
  };
  const orderLo = (stored, candidates) => {
    const left3 = [...stored];
    const out = [];
    for (const c of candidates) {
      const i = left3.indexOf(c);
      if (i >= 0) {
        out.push(c);
        left3.splice(i, 1);
      }
    }
    return [...out, ...left3];
  };
  return {
    title: r.productNameKm,
    subtitle: r.gameName ?? r.gameCode ?? "",
    date: sheetDmy(r.drawDate),
    aRows,
    prizes: sheetIdx >= 0 ? rows : [],
    posts,
    provinces: lines.length ? { label: lines.length >= 4 ? "4P" : "3P", lines } : null,
    lo: withLo && lo.length ? { d2: orderLo(lo.filter((n) => n.d3 === null && n.full.length === 2).map((n) => n.full), cand(2)), d3: orderLo(lo.filter((n) => n.full.length === 3).map((n) => n.full), cand(3)) } : null,
    postsFirst
  };
}
var RESULT_SHEET_FONTS = [
  { label: "Kantumruy Pro (Khmer)", family: "Kantumruy Pro, Inter, sans-serif" },
  { label: "Inter (numbers)", family: "Inter, Kantumruy Pro, sans-serif" }
];
var DEFAULT_SHEET_STYLE = {
  colors: { background: "#ffffff", border: "#000000", header: "#1d4ed8", headerText: "#ffffff", subtitle: "#15803d", date: "#dc2626", label: "#1d4ed8", number: "#000000", prize: "#4b5563", prizeNo: "#4b5563", rule: "#9ca3af", lo: "#000000" },
  fonts: { sans: "Kantumruy Pro, Inter, sans-serif", num: "Inter, Kantumruy Pro, sans-serif" },
  sizes: { title: 17, subtitle: 14, dateLabel: 15, date: 19, aLabel: 30, aNumber: 28, aStacked: 30, prizeNo: 13, prize: 20, postLabel: 30, post: 30, provinceLabel: 26, province: 24, loTitle: 15, lo2: 15, lo3: 14 },
  rows: { pad: 6, head: 36, sub: 30, date: 34, a: 44, prize: 30, post: 46, province: 34, lo: 28 },
  layout: { labelWidth: 84, aThreeDigitX: 110, postX: 12, dateX: 20, loPerRow: 10, loPerRowPrint: 8 },
  texts: { title: "", dateLabel: "\u1790\u17D2\u1784\u17C3:", footer: "" },
  show: { game: true, date: true, prizes: true, provinces: true, lo: true },
  footer: { color: "#4b5563", size: 13, row: 30 }
};
function mergeSheetStyle(over) {
  const o = over && typeof over === "object" ? over : {};
  const part = (base, v, allowBlank = false) => {
    const src = v && typeof v === "object" ? v : {};
    const out = { ...base };
    for (const k of Object.keys(base)) if (typeof src[k] === typeof base[k] && (src[k] !== "" || allowBlank) && !(typeof src[k] === "number" && !Number.isFinite(src[k]))) out[k] = src[k];
    return out;
  };
  const d = DEFAULT_SHEET_STYLE;
  return { colors: part(d.colors, o.colors), fonts: part(d.fonts, o.fonts), sizes: part(d.sizes, o.sizes), rows: part(d.rows, o.rows), layout: part(d.layout, o.layout), texts: part(d.texts, o.texts, true), show: part(d.show, o.show), footer: part(d.footer, o.footer) };
}
function drawSheet(make, model, opts = {}) {
  const st = opts.style ?? DEFAULT_SHEET_STYLE;
  let m = model;
  const W = opts.width ?? 384;
  const color2 = opts.color ?? false;
  const k = W / 384;
  const footer = Math.round((opts.footerMm ?? (color2 ? 0 : 10)) * 8 * k);
  const col = (c) => color2 ? c : "#000";
  const cs = st.colors;
  const px = (n) => Math.round(n * k);
  const labelW = px(st.layout.labelWidth);
  const R = st.rows;
  const H_HEAD = px(R.head);
  const H_SUB = px(R.sub);
  const H_DATE = px(R.date);
  const H_A = px(R.a);
  const H_N = px(R.prize);
  const H_POST = px(R.post);
  const H_PL = px(R.province);
  const H_LO = px(R.lo);
  const PAD = px(R.pad);
  const per = Math.max(1, color2 ? st.layout.loPerRow : st.layout.loPerRowPrint);
  const loRows = (list) => Math.ceil(list.length / per);
  const stackedA = m.aRows.length === 1 && m.prizes.length > 0;
  const sh = st.show ?? DEFAULT_SHEET_STYLE.show;
  const tx = st.texts ?? DEFAULT_SHEET_STYLE.texts;
  const ft = st.footer ?? DEFAULT_SHEET_STYLE.footer;
  if (!sh.prizes) m = { ...m, prizes: [] };
  if (!sh.provinces) m = { ...m, provinces: null };
  if (!sh.lo) m = { ...m, lo: null };
  const H_SUB_SHOWN = sh.game ? H_SUB : 0;
  const H_DATE_SHOWN = sh.date ? H_DATE : 0;
  const H_FOOT = tx.footer.trim() ? px(ft.row) : 0;
  let h = PAD + H_HEAD + H_SUB_SHOWN + H_DATE_SHOWN;
  for (const a of m.aRows) h += (stackedA ? 2 : 1) * a.lines.length * H_A;
  h += m.prizes.length * H_N + m.posts.length * H_POST + (m.provinces ? m.provinces.lines.length * H_PL : 0);
  if (m.lo) h += H_LO + (loRows(m.lo.d2) + loRows(m.lo.d3)) * H_LO;
  h += H_FOOT;
  h += PAD * 2;
  const { canvas, ctx: g } = make(W, h + footer);
  g.fillStyle = color2 ? cs.background : "#fff";
  g.fillRect(0, 0, W, h + footer);
  g.lineWidth = Math.max(1, px(1));
  g.strokeStyle = col(cs.border);
  g.strokeRect(0.5, PAD + 0.5, W - 1, h - PAD * 3 - 1);
  const S = st.sizes;
  const text = (t, x, y2, o) => {
    g.font = `${o.bold ? "700" : "400"} ${px(o.size)}px ${o.font === "sans" ? st.fonts.sans : st.fonts.num}`;
    g.fillStyle = col(o.col ?? cs.number);
    g.textAlign = o.align ?? "left";
    g.textBaseline = "middle";
    g.fillText(t, x, y2);
  };
  const hline = (y2, x0 = 0, x1 = W) => {
    g.strokeStyle = col(cs.rule);
    g.beginPath();
    g.moveTo(x0, y2 + 0.5);
    g.lineTo(x1, y2 + 0.5);
    g.stroke();
  };
  const vline = (x, y0, y1) => {
    g.strokeStyle = col(cs.rule);
    g.beginPath();
    g.moveTo(x + 0.5, y0);
    g.lineTo(x + 0.5, y1);
    g.stroke();
  };
  let y = PAD;
  g.fillStyle = col(cs.header);
  g.fillRect(1, y + 1, W - 2, H_HEAD - 1);
  text(tx.title.trim() || m.title, W / 2, y + H_HEAD / 2, { size: S.title, bold: true, align: "center", col: color2 ? cs.headerText : "#fff", font: "sans" });
  y += H_HEAD;
  if (sh.game) {
    text(m.subtitle, W / 2, y + H_SUB / 2, { size: S.subtitle, bold: true, align: "center", col: cs.subtitle, font: "sans" });
    y += H_SUB;
  }
  if (sh.date) {
    if (tx.dateLabel) text(tx.dateLabel, px(14), y + H_DATE / 2, { size: S.dateLabel, bold: true, col: cs.date, font: "sans" });
    text(m.date, W / 2 + px(st.layout.dateX), y + H_DATE / 2, { size: S.date, bold: true, align: "center", col: cs.date });
    y += H_DATE;
  }
  hline(y);
  for (const a of m.aRows) {
    const rows = (stackedA ? 2 : 1) * a.lines.length;
    const y0 = y;
    text(a.label, labelW / 2, y0 + rows * H_A / 2, { size: S.aLabel, bold: true, align: "center", col: cs.label });
    a.lines.forEach((l, i) => {
      if (stackedA) {
        text(l.d2, labelW + (W - labelW) / 2, y + H_A / 2, { size: S.aStacked, bold: true, align: "center" });
        y += H_A;
        hline(y, labelW);
        if (l.d3) text(l.d3, labelW + (W - labelW) / 2, y + H_A / 2, { size: S.aStacked, bold: true, align: "center" });
        y += H_A;
      } else {
        text(l.d2, labelW + px(10), y + H_A / 2, { size: S.aNumber, bold: true });
        if (l.d3) text(l.d3, labelW + px(st.layout.aThreeDigitX), y + H_A / 2, { size: S.aNumber, bold: true });
        y += H_A;
      }
      if (i < a.lines.length - 1) hline(y, labelW);
    });
    vline(labelW, y0, y);
    hline(y);
  }
  const drawPrizes = () => {
    for (const p of m.prizes) {
      text(String(p.n), labelW - px(8), y + H_N / 2, { size: S.prizeNo, align: "right", col: cs.prizeNo });
      text(p.v, W - px(10), y + H_N / 2, { size: S.prize, bold: true, align: "right", col: cs.prize });
      vline(labelW, y, y + H_N);
      y += H_N;
      hline(y);
    }
  };
  const drawPosts = () => {
    for (const p of m.posts) {
      text(p.label, labelW / 2, y + H_POST / 2, { size: S.postLabel, bold: true, align: "center", col: cs.label });
      text(sheetDash(p.line), labelW + px(st.layout.postX), y + H_POST / 2, { size: S.post, bold: true });
      vline(labelW, y, y + H_POST);
      y += H_POST;
      hline(y);
    }
  };
  if (m.postsFirst) {
    drawPosts();
    drawPrizes();
  } else {
    drawPrizes();
    drawPosts();
  }
  if (m.provinces) {
    const y0 = y;
    text(m.provinces.label, labelW / 2, y0 + m.provinces.lines.length * H_PL / 2, { size: S.provinceLabel, bold: true, align: "center", col: cs.label });
    for (const l of m.provinces.lines) {
      text(sheetDash(l), labelW + px(st.layout.postX), y + H_PL / 2, { size: S.province, bold: true });
      y += H_PL;
    }
    vline(labelW, y0, y);
    hline(y);
  }
  if (m.lo) {
    text("Lo", px(10), y + H_LO / 2, { size: S.loTitle, bold: true, col: cs.label });
    y += H_LO;
    const cell = (W - px(16)) / per;
    for (const list of [m.lo.d2, m.lo.d3]) {
      list.forEach((v, i) => text(v, px(8) + i % per * cell + cell / 2, y + Math.floor(i / per) * H_LO + H_LO / 2, { size: list === m.lo.d2 ? S.lo2 : S.lo3, bold: true, align: "center", col: cs.lo }));
      y += loRows(list) * H_LO;
    }
    hline(y);
  }
  if (H_FOOT) {
    text(tx.footer.trim(), W / 2, y + H_FOOT / 2, { size: ft.size, align: "center", col: ft.color, font: "sans" });
    y += H_FOOT;
  }
  return canvas;
}

// src/engine/sary-paste.ts
var KM_DATE = /(\d{2})\/(\d{2})\/(\d{4})/;
var PX = { 0: "P2", 1: "P2", 2: "P2", 3: "P3", 4: "P3", 5: "P1", 6: "P2" };
var PY = { 0: "P2", 1: "P2", 2: "P3", 3: "P2", 4: "P2", 5: "P1", 6: "P2" };
var clean = (s) => s.replace(/ /g, " ").trim();
function parseSaryPaste(text) {
  const lines = text.split(/\r?\n/).map(clean).filter(Boolean);
  const notes = [];
  const date = lines.map((l) => l.match(KM_DATE)).find(Boolean)?.[0] ?? null;
  const posts = {};
  const rows = /* @__PURE__ */ new Map();
  const aRows = [];
  let aPending = false;
  for (const raw of lines) {
    const l = raw.replace(/\s+/g, " ");
    let m;
    if (m = l.match(/^A\s+(\d{2})(?:\s+(\d{3}))?$/)) {
      aRows.push({ d2: m[1], d3: m[2] ?? null });
      aPending = !m[2];
      continue;
    }
    if (aPending && (m = l.match(/^(\d{3})$/))) {
      aRows[aRows.length - 1].d3 = m[1];
      aPending = false;
      continue;
    }
    if (aRows.length && !rows.size && !Object.keys(posts).length && (m = l.match(/^(\d{2})(?:\s+(\d{3}))?$/))) {
      aRows.push({ d2: m[1], d3: m[2] ?? null });
      continue;
    }
    if ((m = l.match(/^([A-Z]{1,2})\s+(\d{2})\s*-\s*(\d{3})$/)) || (m = l.match(/^([A-Z]{1,2})\s+(\d{2})\s+(\d{3})$/))) {
      posts[m[1]] = `${m[2]}-${m[3]}`;
      continue;
    }
    if (m = l.match(/^(\d{1,2})\s+(\d{3,6})$/)) {
      rows.set(Number(m[1]), m[2]);
      continue;
    }
    if (/^TC\s*\d{1,2}[:.]\d{2}$/i.test(l) || /^(3P|4P|P\d)\s*=/i.test(l)) continue;
    if (!/^ថ្ងៃ/.test(l) && !/^(បង្ហាញសារី|សារី|ឆ្នោត|Thinhnam|លេខគុណ|VN)/.test(l)) notes.push(`? ${l}`);
  }
  const north = rows.has(19) || aRows.length > 1;
  if (!posts.B) notes.push("B missing: the special prize cannot be rebuilt");
  const g = posts.B ? posts.B.replace("-", "") : "";
  if (north) {
    const sheet2 = [g];
    for (let n = 1; n <= 19; n++) {
      const v = rows.get(n);
      if (!v) notes.push(`row ${n} missing`);
      sheet2.push(v ?? "");
    }
    for (const a2 of aRows) if (a2.d3) sheet2.push(a2.d3);
    for (const a2 of aRows) sheet2.push(a2.d2);
    if (aRows.filter((a2) => a2.d3).length !== 3 || aRows.length !== 4) notes.push("A rows: expected four 2D and three 3D");
    return { kind: "vn630", date, sheets: [sheet2.filter(Boolean)], posts: {}, notes };
  }
  const a = aRows[0];
  if (!a || !a.d3) notes.push("A missing");
  const sheet = [a?.d2 ?? "", a?.d3 ?? ""];
  for (let n = 1; n <= 15; n++) {
    const v = rows.get(n);
    if (!v) notes.push(`row ${n} missing`);
    sheet.push(v ?? "");
  }
  sheet.push(g);
  const extra = {};
  for (const code of ["F", "I", "N", "K", "L", "O", "P", "Z"]) if (posts[code]) extra[code] = posts[code];
  return { kind: "vn430", date, sheets: [sheet.filter(Boolean)], posts: extra, notes };
}
var NORTH_PRIZES = [
  { key: /^Giải ĐB/i, width: 5, count: 1 },
  { key: /^Giải nhất/i, width: 5, count: 1 },
  { key: /^Giải nhì/i, width: 5, count: 2 },
  { key: /^Giải ba/i, width: 5, count: 6 },
  { key: /^Giải tư/i, width: 4, count: 4 },
  { key: /^Giải năm/i, width: 4, count: 6 },
  { key: /^Giải sáu/i, width: 3, count: 3 },
  { key: /^Giải bảy/i, width: 2, count: 4 }
];
var SOUTH_WIDTHS = [2, 3, 4, 4, 4, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 6];
var TN_WIDTHS = [2, 3, 4, 4, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5];
var toIso = (dmy) => dmy.split("/").reverse().join("-");
function sliceDrawSection(text, wantTime) {
  const lines = text.split(/\r?\n/);
  const heads = [];
  lines.forEach((l, i) => {
    if (!/KẾT QUẢ XỔ SỐ/i.test(l)) return;
    const m = l.match(/(\d{1,2})H(\d{2})/i);
    heads.push({ i, time: m ? `${m[1].padStart(2, "0")}:${m[2]}` : null });
  });
  const times = heads.map((h) => h.time).filter((x) => !!x);
  if (!wantTime || !times.length) return { text, time: null, times };
  const mins = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  let k = heads.findIndex((h) => h.time === wantTime);
  if (k < 0) {
    const want = mins(wantTime);
    heads.forEach((h, i) => {
      if (!h.time) return;
      const d = want - mins(h.time);
      if (d > 0 && d <= 30 && (k < 0 || mins(heads[k].time) < mins(h.time))) k = i;
    });
  }
  if (k < 0) return { text: "", time: null, times };
  const end = heads[k + 1]?.i ?? lines.length;
  return { text: lines.slice(heads[k].i + 1, end).join("\n"), time: wantTime, times };
}
function parseOfficialVn(text, wantDate, wantTime) {
  const section = sliceDrawSection(text, wantTime);
  if (wantTime && section.times.length && !section.time) return null;
  const lines = section.text.split(/\r?\n/).map(clean).filter(Boolean);
  const blocks = [];
  for (const l of lines) {
    const m = l.match(KM_DATE);
    if (m && /^(Thứ|Chủ|Ngày|\d{2}\/\d{2}\/\d{4})/.test(l)) blocks.push({ date: m[0], lines: [] });
    else if (blocks.length) blocks[blocks.length - 1].lines.push(l);
  }
  if (!blocks.length) return null;
  const want = wantDate ? blocks.find((b) => toIso(b.date) === wantDate) : null;
  const block2 = want ?? blocks[0];
  const notes = [];
  if (wantDate && !want) notes.push(`no block for ${wantDate}; using ${block2.date}`);
  const bl = block2.lines;
  if (bl.some((l) => /^Giải ĐB/i.test(l))) {
    const sheet = [];
    for (const p of NORTH_PRIZES) {
      const i = bl.findIndex((l) => p.key.test(l));
      if (i < 0) {
        notes.push(`${p.key.source.replace(/[\^\\/]/g, "")} missing`);
        continue;
      }
      let digits = bl[i].replace(p.key, "").replace(/\D/g, "");
      if (!digits && bl[i + 1]) digits = bl[i + 1].replace(/\D/g, "");
      for (let j = i + 2; !bl[i].replace(p.key, "").replace(/\D/g, "") && digits.length < p.width * p.count && j < bl.length && /^\d+$/.test(bl[j]); j++) digits += bl[j];
      if (digits.length !== p.width * p.count) notes.push(`${bl[i].split(/\s/)[0]} ${bl[i].split(/\s/)[1] ?? ""}: expected ${p.count} \xD7 ${p.width} digits`);
      for (let k = 0; k < p.count; k++) sheet.push(digits.slice(k * p.width, (k + 1) * p.width));
    }
    return { kind: "vn630", date: block2.date, provinces: [], sheets: [sheet.filter(Boolean)], notes };
  }
  const provinces = [];
  const sheets = [];
  for (let i = 0; i < bl.length; i++) {
    if (!/^XS[A-ZĐ]+(\s*-.*)?$/i.test(bl[i])) continue;
    const name2 = i > 0 ? bl[i - 1] : "?";
    const nums = [];
    for (let j = i + 1; j < bl.length && nums.length < 18; j++) {
      if (/^\d+$/.test(bl[j])) nums.push(bl[j]);
      else break;
    }
    const widths = nums.length === 15 ? TN_WIDTHS : SOUTH_WIDTHS;
    if (nums.length !== 18 && nums.length !== 15) notes.push(`${name2}: ${nums.length} numbers, expected 18 (Thinh Nam 13:15: 15)`);
    nums.forEach((n, k) => {
      if (widths[k] !== void 0 && n.length !== widths[k] && !(widths === SOUTH_WIDTHS && k === 5 && n.length === 5)) notes.push(`${name2} #${k + 1} ${n}: expected ${widths[k]} digits`);
    });
    provinces.push(name2);
    sheets.push(nums);
  }
  if (!sheets.length) return null;
  return { kind: sheets[0].length === 15 ? "tn" : "vn430", date: block2.date, provinces, sheets, notes };
}
function parsePastedPosts(text) {
  const out = {};
  for (const raw of text.split(/\r?\n/)) {
    const m = clean(raw).replace(/\s+/g, " ").match(/^([A-Z][A-Z0-9]{0,2})\s+(\d{2})\s*-\s*(\d{3})$/);
    if (m) out[m[1]] = `${m[2]}-${m[3]}`;
  }
  return out;
}
function pastedProvincePosts(posts, weekday) {
  const out = { P2: null, P3: null };
  const x = PX[weekday];
  const y = PY[weekday];
  if (x === "P2" || x === "P3") out[x] = posts.F ?? null;
  if ((y === "P2" || y === "P3") && !out[y]) out[y] = posts.I ?? null;
  return out;
}

// src/engine/stake-format.ts
var STAKE_UNITS = ["riel", "hundreds"];
var HUNDREDS = 100;
function formatStake(amountRiel, unit = "riel", opts = {}) {
  if (typeof amountRiel !== "number" || !Number.isFinite(amountRiel)) return "";
  const v = unit === "hundreds" ? amountRiel / HUNDREDS : Math.trunc(amountRiel);
  const fixed = opts.decimals === null || opts.decimals === void 0 ? null : Math.max(0, Math.min(2, Math.round(opts.decimals)));
  const max = fixed ?? (unit === "hundreds" ? 2 : 0);
  const min = fixed ?? 0;
  return new Intl.NumberFormat("en-US", { useGrouping: opts.separator !== false, minimumFractionDigits: min, maximumFractionDigits: max }).format(v);
}
var toStakeUnit = (amountRiel, unit) => unit === "hundreds" ? amountRiel / HUNDREDS : amountRiel;

// src/engine/ticket-render.ts
import qrcode from "qrcode-generator";

// src/money.ts
var formatRiel = (n, withSymbol = true) => {
  const v = typeof n === "bigint" ? Number(n) : typeof n === "string" ? Number(n) : n;
  const s = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.trunc(v));
  return withSymbol ? `${s}\u17DB` : s;
};
var pct = (amount2, percent) => Math.round(amount2 * percent / 100);

// src/time.ts
var dtf = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false
});
function phnomPenhParts(d) {
  const p = Object.fromEntries(dtf.formatToParts(d).map((x) => [x.type, x.value]));
  const hour = p.hour === "24" ? "00" : p.hour;
  const date = `${p.year}-${p.month}-${p.day}`;
  const weekday = (/* @__PURE__ */ new Date(`${date}T00:00:00Z`)).getUTCDay();
  return { date, time: `${hour}:${p.minute}:${p.second}`, weekday };
}
var todayPP = (now = /* @__PURE__ */ new Date()) => phnomPenhParts(now).date;
function ppLocalToDate(date, time) {
  const t = time.length === 5 ? `${time}:00` : time;
  return /* @__PURE__ */ new Date(`${date}T${t}+07:00`);
}
var addDays = (date, days) => {
  const d = /* @__PURE__ */ new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
var fmtTime12 = (d) => new Intl.DateTimeFormat("en-US", { timeZone: TIMEZONE, hour: "2-digit", minute: "2-digit", hour12: true }).format(d);
var fmtDateTime = (d) => {
  const p = phnomPenhParts(d);
  return `${p.date} ${p.time.slice(0, 5)}`;
};
var WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
var MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
var DEFAULT_DATE_FORMAT = "YYYY-MM-DD";
var DEFAULT_TIME_FORMAT = "HH:mm";
function formatDateTokens(d, fmt, fallback = DEFAULT_DATE_FORMAT) {
  const f = fmt.trim() || fallback;
  if (Number.isNaN(d.getTime())) return "";
  const p = phnomPenhParts(d);
  const [Y, Mo, D] = p.date.split("-").map(Number);
  const [H, Mi, S] = p.time.split(":").map(Number);
  const h12 = H % 12 === 0 ? 12 : H % 12;
  const two = (n) => String(n).padStart(2, "0");
  const tokens = {
    YYYY: String(Y),
    YY: two(Y % 100),
    MMMM: MONTHS[Mo - 1],
    MMM: MONTHS[Mo - 1].slice(0, 3),
    MM: two(Mo),
    M: String(Mo),
    DD: two(D),
    D: String(D),
    dddd: WEEKDAYS[p.weekday],
    ddd: WEEKDAYS[p.weekday].slice(0, 3),
    HH: two(H),
    H: String(H),
    hh: two(h12),
    h: String(h12),
    mm: two(Mi),
    m: String(Mi),
    ss: two(S),
    s: String(S),
    A: H < 12 ? "AM" : "PM",
    a: H < 12 ? "am" : "pm"
  };
  return f.replace(/\[([^\]]*)]|YYYY|YY|MMMM|MMM|MM|M|DD|D|dddd|ddd|HH|H|hh|h|mm|m|ss|s|A|a/g, (m, literal) => literal !== void 0 ? literal : tokens[m]);
}

// src/schemas/ticket-format.ts
import { z } from "zod";
var TICKET_BLOCK_TYPES = ["text", "logoText", "logoImage", "image", "kv", "divider", "spacer", "lines", "qr", "result"];
var TICKET_ALIGNS = ["left", "center", "right"];
var TICKET_VALIGNS = ["top", "middle", "bottom"];
var TICKET_SECTIONS = ["header", "body", "footer"];
var TICKET_CONDITIONS = ["always", "minTotal", "maxTotal", "won", "products", "reprint", "firstPrint"];
var TICKET_LAYOUTS = ["flow", "free"];
var TICKET_OVERFLOWS = ["wrap", "shrink", "ellipsis"];
var FREE_GRID = 4;
var MIN_ELEMENT_SIZE = 8;
var MAX_PAPER_HEIGHT = 6e3;
var TICKET_PLACEHOLDERS = {
  ticketNo: "Ticket number",
  productCode: "Lottery code (VN, TN\u2026)",
  productName: "Lottery name (Khmer)",
  drawDate: "Draw date",
  drawTime: "Draw time",
  drawAt: "Draw date + time",
  soldDate: "Sold date",
  soldTime: "Sold time",
  soldAt: "Sold date + time",
  drawDmy: "Draw DD-MM-YYYY HH:mm",
  soldDmy: "Sold DD-MM-YYYY HH:mm",
  agentCode: "Agent code",
  agentName: "Agent / account name",
  total: "Total amount",
  win: "Win amount",
  commission: "Commission",
  lineCount: "Number of lines",
  numberCount: "Number of numbers",
  status: "Status",
  printCount: "Print count (1 = first print)",
  printedDate: "Print date",
  printedTime: "Print time",
  printedAt: "Print date + time",
  checkUrl: "Result check link",
  // branding 1.7: the company brand (ticket header defaults: line 1 / line 2, else the brand display name) — empty without a brand
  companyName: "Company name (brand)",
  companyLine2: "Company header line 2 (brand)"
};
var MONEY_PLACEHOLDERS = ["total", "win", "commission"];
var DATE_PLACEHOLDERS = ["drawDate", "drawTime", "drawAt", "soldDate", "soldTime", "soldAt", "printedDate", "printedTime", "printedAt"];
var id = z.string().trim().min(1).max(40).regex(/^[A-Za-z0-9_-]+$/);
var color = z.enum(["black", "inverse"]);
var hex = z.string().trim().regex(/^(#[0-9a-fA-F]{3}|#[0-9a-fA-F]{6})?$/).default("");
var ticketConditionSchema = z.object({
  when: z.enum(TICKET_CONDITIONS).default("always"),
  /** minTotal / maxTotal threshold in riel */
  amount: z.number().int().min(0).max(1e9).default(0),
  /** products condition: product codes */
  products: z.array(z.string().trim().min(1).max(20)).max(30).default([])
});
var TICKET_FONTS = [
  { id: "kantumruy", label: "Kantumruy Pro", family: "'Kantumruy Pro', 'Khmer OS', sans-serif" },
  { id: "battambang", label: "Battambang (Khmer OS)", family: "'Battambang', 'Kantumruy Pro', sans-serif" },
  { id: "siemreap", label: "Siemreap (Khmer OS)", family: "'Siemreap', 'Kantumruy Pro', sans-serif" },
  { id: "notoKhmer", label: "Noto Sans Khmer", family: "'Noto Sans Khmer', 'Kantumruy Pro', sans-serif" },
  { id: "hanuman", label: "Hanuman", family: "'Hanuman', 'Kantumruy Pro', serif" },
  { id: "moul", label: "Moul (title)", family: "'Moul', 'Kantumruy Pro', serif" },
  { id: "inter", label: "Inter", family: "'Inter', 'Kantumruy Pro', sans-serif" },
  { id: "roboto", label: "Roboto", family: "'Roboto', 'Kantumruy Pro', sans-serif" },
  { id: "robotoMono", label: "Roboto Mono (monospace)", family: "'Roboto Mono', 'Kantumruy Pro', monospace" }
];
var fontLabel = (family) => TICKET_FONTS.find((f) => f.family === family)?.label ?? family.split(",")[0].trim().replace(/^['"]|['"]$/g, "");
var TICKET_LINE_PARTS = ["post", "rule", "row", "count", "number", "amount", "bar", "rollCount", "mode"];
var TICKET_PART_PLACEMENTS = ["above", "below", "before", "after", "end"];
var TICKET_WIN_MARKS = ["box", "bar", "band"];
var partAlign = z.enum(["auto", ...TICKET_ALIGNS]).default("auto");
var autoSize = z.number().int().min(8).max(120).nullable().default(null);
var autoBool = z.boolean().nullable().default(null);
var partOffset = z.number().int().min(-576).max(576).default(0);
var partOffsetY = z.number().int().min(-200).max(200).default(0);
var dots = z.number().int().min(0).max(200);
var partFont = z.string().trim().max(120).default("");
var placement = (d) => z.enum(TICKET_PART_PLACEMENTS).default(d);
var textPart = { size: autoSize, bold: autoBool, font: partFont, color: hex, offsetX: partOffset, offsetY: partOffsetY };
var ticketLinePartsSchema = z.object({
  /** the post label ("ABCD", "Lo"): a header row per group (default), a footer row, or on every line */
  post: z.object({
    show: z.boolean().default(true),
    placement: placement("above"),
    align: partAlign,
    /** space before each group header after the first; null = auto (size / 3) */
    spaceAbove: dots.nullable().default(null),
    spaceBelow: dots.default(0),
    ...textPart
  }).prefault({}),
  /** short rule under the post label (header / footer rows only) */
  rule: z.object({
    show: z.boolean().default(true),
    lengthPct: z.number().int().min(1).max(100).default(40),
    thickness: z.number().int().min(1).max(16).default(2),
    /** auto = the post label's alignment */
    align: partAlign,
    color: hex,
    offsetX: partOffset,
    offsetY: partOffsetY
  }).prefault({}),
  /** a bet line as a whole: the number row and a roll's extra rows */
  row: z.object({
    /** auto = the block alignment */
    align: partAlign,
    offsetX: partOffset,
    /** extra space after each line */
    spacing: dots.default(0),
    /** indent of the number row under a post header (dots) */
    indent: dots.default(0)
  }).prefault({}),
  /** ticket count = numbers × Σ post multipliers */
  count: z.object({
    show: z.boolean().default(true),
    placement: placement("before"),
    /** 'paren' = "(3)", 'x' = "x3" (not shown for 1) */
    format: z.enum(["paren", "x"]).default("paren"),
    /** own-row placement only; auto = under the number */
    align: partAlign,
    ...textPart
  }).prefault({}),
  /** the bet number (a roll's start and end rows) */
  number: z.object({
    /** how a won line prints: box behind the number, bar at the left edge, or a black band */
    winMark: z.enum(TICKET_WIN_MARKS).default("box"),
    ...textPart
  }).prefault({}),
  /** amount per number */
  amount: z.object({
    show: z.boolean().default(true),
    placement: placement("after"),
    /**
     * 'auto' (owner 2026-09-18, the default) = "10: 1000" after a number and "123x 1000" after a combination (គុណ) — the ":" and the "x"
     * stick to the number; 'x' = "x 300៛", 'colon' = ": 300៛", 'plain' = "300៛"
     */
    format: z.enum(["auto", "x", "colon", "plain"]).default("x"),
    /**
     * how the stake is written — 'hundreds' = the reference receipt's denomination (100៛ → "1", 500៛ → "5",
     * 1,000៛ → "10", `formatStake`), 'riel' = "1,000". Display only: the stored amount is always riel.
     * Templates saved before this option existed are pinned to 'riel' on parse (they printed riel).
     */
    unit: z.enum(STAKE_UNITS).default("hundreds"),
    /** riel sign ៛ (riel unit only) */
    riel: z.boolean().default(true),
    /** push the amount (and what follows it) to the right edge */
    pushRight: z.boolean().default(false),
    /** repeat the amount on a roll's end row (owner 2026-09-18: "10: 1000 … 19: 1000") */
    onEndRow: z.boolean().default(false),
    /** own-row placement only; auto = under the number */
    align: partAlign,
    ...textPart
  }).prefault({}),
  /** roll lines: the "|" between the start and end rows, centred under the number */
  bar: z.object({ show: z.boolean().default(true), ...textPart }).prefault({}),
  /** roll lines: "(20 លេខ)" beside the "|" */
  rollCount: z.object({ show: z.boolean().default(true), ...textPart }).prefault({}),
  /** the condition note under a roll ("អូសធម្មតា", "អូសកណ្ដាល + គុណ") */
  mode: z.object({
    show: z.boolean().default(true),
    placement: placement("below"),
    /** owner 2026-09-18: a roll's note sits on its "| (10 លេខ)" row — "| (10លេខ)  អូសកន្ទុយ" (then `placement` is not used for rolls) */
    onBarRow: z.boolean().default(false),
    /** own-row placement only; auto = under the number */
    align: partAlign,
    ...textPart
  }).prefault({})
});
var DEFAULT_LINE_PARTS = ticketLinePartsSchema.parse({});
var withLinePart = (parts, part, patch) => ({ ...parts, [part]: { ...parts[part], ...patch } });
var isObj = (v) => !!v && typeof v === "object" && !Array.isArray(v);
var isAuto = (v) => v === null || v === void 0 || v === "auto";
var pick = (v, fallback) => isAuto(v) ? fallback : v;
function migrateTicketBlock(raw) {
  if (!isObj(raw)) return raw;
  const parts = isObj(raw.parts) ? raw.parts : null;
  const legacy = "lineStyle" in raw || "showGroups" in raw || parts !== null && ("group" in parts || isObj(parts.count) && "position" in parts.count);
  if (!legacy) {
    if (raw.type === "lines" && parts !== null && isObj(parts.amount) && !("unit" in parts.amount)) return { ...raw, parts: { ...parts, amount: { ...parts.amount, unit: "riel" } } };
    return raw;
  }
  const { lineStyle, showGroups, parts: _p, ...rest } = raw;
  const kv = lineStyle === "kv" && raw.type === "lines";
  const part = (k) => parts && isObj(parts[k]) ? { ...parts[k] } : {};
  const post = parts && "post" in parts ? part("post") : part("group");
  const rule = part("rule");
  const row = part("row");
  const count = part("count");
  const number = part("number");
  const amount2 = part("amount");
  const rollCount = part("rollCount");
  const mode = part("mode");
  const size = typeof raw.size === "number" ? raw.size : 22;
  const bold = raw.bold === true;
  const align = typeof raw.align === "string" ? raw.align : "left";
  const groups = showGroups !== false;
  const rowAlign = pick(row.align, align);
  const pushRight = pick(amount2.pushRight, kv && rowAlign === "left");
  const fg = typeof raw.fg === "string" ? raw.fg : "";
  const valueFg = typeof raw.valueFg === "string" ? raw.valueFg : "";
  if (!("show" in post)) post.show = groups;
  if ("position" in count) {
    count.placement = pick(count.position, kv ? "end" : "before");
    delete count.position;
  } else if (isAuto(count.placement)) count.placement = kv ? "end" : "before";
  count.format = pick(count.format, kv ? "x" : "paren");
  amount2.format = pick(amount2.format, kv ? pushRight ? "plain" : "colon" : "x");
  amount2.riel = pick(amount2.riel, !kv);
  if (raw.type === "lines") amount2.unit = pick(amount2.unit, "riel");
  amount2.pushRight = pushRight;
  amount2.onEndRow = pick(amount2.onEndRow, kv);
  rule.show = pick(rule.show, !kv);
  if (kv) {
    post.size = pick(post.size, size);
    post.spaceAbove = pick(post.spaceAbove, 0);
    count.size = pick(count.size, size);
    count.bold = pick(count.bold, bold);
    rollCount.size = pick(rollCount.size, Math.max(8, Math.round(size * 0.8)));
    mode.size = pick(mode.size, Math.max(8, Math.round(size * 0.8)));
    row.indent = pushRight && rowAlign === "left" && groups ? Math.round(size / 2) : 0;
    number.winMark = pushRight ? "bar" : "band";
    if (!count.color) count.color = valueFg || fg || "#000";
  } else if (raw.type === "lines" && valueFg && !amount2.color) amount2.color = fg || "#000";
  return { ...rest, parts: { post, rule, row, count, number, amount: amount2, bar: part("bar"), rollCount, mode } };
}
function ticketFontFamilies(t) {
  const out = /* @__PURE__ */ new Set([t.fontFamily]);
  for (const b of t.blocks) {
    if (b.font) out.add(b.font);
    if (b.type !== "lines" || !b.parts) continue;
    for (const k of TICKET_LINE_PARTS) {
      const f = b.parts[k].font;
      if (f) out.add(f);
    }
  }
  return [...out];
}
var ticketBlockObjectSchema = z.object({
  id,
  type: z.enum(TICKET_BLOCK_TYPES),
  section: z.enum(TICKET_SECTIONS).default("body"),
  enabled: z.boolean().default(true),
  /** text / logoText: literal text with {placeholders}; kv: the value; qr: the encoded content */
  text: z.string().max(1e3).default(""),
  /** kv: the left label */
  label: z.string().max(200).default(""),
  align: z.enum(TICKET_ALIGNS).default("left"),
  /** font size in printer dots (22 ≈ normal on 58 mm) */
  size: z.number().int().min(8).max(120).default(22),
  bold: z.boolean().default(false),
  /** white text on a black band */
  color: color.default("black"),
  /** horizontal shift in dots (negative = left) */
  offsetX: z.number().int().min(-576).max(576).default(0),
  marginTop: z.number().int().min(0).max(200).default(0),
  marginBottom: z.number().int().min(0).max(200).default(4),
  /** flow: logoImage width / qr size / spacer height / divider thickness; free: the element box width — in dots */
  width: z.number().int().min(1).max(576).default(160),
  /** divider: 'solid' | 'dashed' | 'dotted' | 'double' */
  style: z.enum(["solid", "dashed", "dotted", "double"]).default("dashed"),
  /** logoImage / image: PNG/JPEG data URL (resized in the console), or `{companyLogo}` / `{companyLogoMark}` = the brand image (1.7) */
  image: z.string().max(4e5).nullable().default(null),
  // ---- free layout: the element box (dots). Ignored by the flow layout.
  x: z.number().int().min(0).max(576).default(0),
  y: z.number().int().min(0).max(MAX_PAPER_HEIGHT).default(0),
  /** box height; 0 = the content height (the box grows with its content and never overflows) */
  height: z.number().int().min(0).max(MAX_PAPER_HEIGHT).default(0),
  /** vertical alignment of the content inside the box */
  valign: z.enum(TICKET_VALIGNS).default("top"),
  /** text that does not fit the box: wrap (the box grows and pushes what is below), shrink to minSize then "…", or "…" */
  overflow: z.enum(TICKET_OVERFLOWS).default("wrap"),
  /** smallest text size shrink-to-fit may use */
  minSize: z.number().int().min(6).max(120).default(12),
  // ---- text style (both layouts; defaults change nothing)
  /** CSS font-family stack of this block; '' = the template font */
  font: partFont,
  /** extra dots between wrapped lines */
  lineSpacing: z.number().int().min(-20).max(100).default(0),
  /** dots added between characters (canvas letterSpacing; drawn glyph by glyph where unsupported) */
  letterSpacing: z.number().int().min(-10).max(60).default(0),
  // ---- placeholder formats of this block
  /** dayjs-style tokens for the date placeholders ('' = YYYY-MM-DD) */
  dateFormat: z.string().trim().max(40).default(""),
  /** dayjs-style tokens for the time placeholders ('' = HH:mm) */
  timeFormat: z.string().trim().max(40).default(""),
  /** money placeholders ({total} {win} {commission}): denomination, thousands separator, decimals (null = auto), riel sign */
  moneyUnit: z.enum(STAKE_UNITS).default("riel"),
  moneySeparator: z.boolean().default(true),
  moneyDecimals: z.number().int().min(0).max(2).nullable().default(null),
  moneySign: z.boolean().default(true),
  /** lines block: every part's placement / size / font / visibility (see ticketLinePartsSchema) */
  parts: ticketLinePartsSchema.prefault({}),
  /** colour view only: text colour */
  fg: hex,
  /** colour view only: kv value colour (e.g. blue win amount); lines — default amount colour */
  valueFg: hex,
  /** colour view only: lines — winning number highlight (print: inverse box) */
  highlight: hex,
  /** colour view only: lines — default colour of the small "(3)" count (parts.count.color overrides) */
  countFg: hex,
  condition: ticketConditionSchema.default({ when: "always", amount: 0, products: [] })
});
var ticketBlockSchema = z.preprocess(migrateTicketBlock, ticketBlockObjectSchema);
var ticketTemplateSchema = z.object({
  paperMm: z.union([z.literal(58), z.literal(80)]).default(58),
  /** left/right safe margin in dots */
  marginX: z.number().int().min(0).max(80).default(6),
  paddingTop: z.number().int().min(0).max(200).default(8),
  /** blank feed after the last block, in dots */
  paddingBottom: z.number().int().min(0).max(400).default(40),
  fontFamily: z.string().trim().max(80).default("'Kantumruy Pro', 'Khmer OS', sans-serif"),
  /** 1-bit threshold used when printing (higher = bolder) */
  threshold: z.number().int().min(60).max(230).default(160),
  /** 'flow' = stacked blocks (default); 'free' = every block is placed by its x / y / width / height */
  layout: z.enum(TICKET_LAYOUTS).default("flow"),
  /** free layout: paper height in dots (the flow layout computes it) */
  height: z.number().int().min(50).max(MAX_PAPER_HEIGHT).default(700),
  /** free layout: extend the paper to the lowest element + paddingBottom when the elements need more than `height` */
  autoGrow: z.boolean().default(true),
  blocks: z.array(ticketBlockSchema).min(1).max(80)
});
var block = (b) => ticketBlockSchema.parse(b);
var DEFAULT_TICKET_TEMPLATE = {
  paperMm: 58,
  marginX: 6,
  paddingTop: 8,
  paddingBottom: 40,
  fontFamily: "'Kantumruy Pro', 'Khmer OS', sans-serif",
  threshold: 160,
  layout: "flow",
  height: 700,
  autoGrow: true,
  blocks: [
    block({ id: "sold", type: "text", section: "header", text: "\u1780\u17B6\u179B\u1794\u179A\u17B7\u1785\u17D2\u1786\u17C1\u1791 : {soldDmy}", size: 20, marginBottom: 0 }),
    block({ id: "ticketNo", type: "text", section: "header", text: "\u179C\u17B7\u1780\u17D0\u1799\u1794\u17D0\u178F\u17D2\u179A : {ticketNo}", size: 20, marginBottom: 4 }),
    block({ id: "hr1", type: "divider", section: "header", style: "solid", width: 2, marginBottom: 8 }),
    // owner 2026-09-18: "10: 1000", a combination "123x 1000", a roll "10: 1000 / | (10លេខ) អូសកន្ទុយ / 19: 1000"
    block({ id: "lines", type: "lines", size: 30, bold: true, align: "center", highlight: "#fde047", countFg: "#6b7280", marginBottom: 6, parts: withLinePart(withLinePart(DEFAULT_LINE_PARTS, "amount", { format: "auto", onEndRow: true }), "mode", { onBarRow: true }) }),
    block({ id: "hr2", type: "divider", style: "solid", width: 2, marginBottom: 6 }),
    block({ id: "total", type: "kv", label: "\u179F\u179A\u17BB\u1794 :", text: "{total}", size: 30, bold: true, marginBottom: 2 }),
    block({ id: "win", type: "kv", label: "\u179A\u1784\u17D2\u179C\u17B6\u1793\u17CB :", text: "{win}", size: 30, bold: true, valueFg: "#1d4ed8", marginBottom: 4, condition: { when: "won", amount: 0, products: [] } }),
    block({ id: "reprint", type: "text", section: "footer", text: "\u1794\u17C4\u17C7\u1796\u17BB\u1798\u17D2\u1796\u17A1\u17BE\u1784\u179C\u17B7\u1789 #{printCount}", align: "center", size: 18, color: "inverse", marginBottom: 4, condition: { when: "reprint", amount: 0, products: [] } }),
    block({ id: "footer", type: "text", section: "footer", text: "\u179F\u17BC\u1798\u1796\u17B7\u1793\u17B7\u178F\u17D2\u1799\u17B2\u17D2\u1799\u1785\u17D2\u1794\u17B6\u179F\u17CB \u179F\u17CA\u17B8\u179F\u1784\u178F\u17B6\u1798\u1780\u1793\u17D2\u1791\u17BB\u1799", align: "center", size: 20, marginTop: 4, marginBottom: 2 })
  ]
};
var sectionIndex = (s) => TICKET_SECTIONS.indexOf(s);
var normalizeBlocks = (blocks) => blocks.map((b, i) => [b, i]).sort((a, b) => sectionIndex(a[0].section) - sectionIndex(b[0].section) || a[1] - b[1]).map(([b]) => b);
function moveBlock(blocks, id2, dir) {
  const bs = normalizeBlocks(blocks);
  const i = bs.findIndex((b) => b.id === id2);
  if (i < 0) return blocks;
  const cur = bs[i];
  const j = i + dir;
  const nb = bs[j];
  if (nb && nb.section === cur.section) {
    const out2 = [...bs];
    out2[i] = nb;
    out2[j] = cur;
    return out2;
  }
  const s = sectionIndex(cur.section) + dir;
  if (s < 0 || s >= TICKET_SECTIONS.length) return bs;
  const out = [...bs];
  out[i] = { ...cur, section: TICKET_SECTIONS[s] };
  return normalizeBlocks(out);
}
function reorderBlock(blocks, id2, targetId) {
  if (id2 === targetId) return blocks;
  const bs = normalizeBlocks(blocks);
  const from = bs.findIndex((b) => b.id === id2);
  const target = bs.findIndex((b) => b.id === targetId);
  if (from < 0 || target < 0) return blocks;
  const out = [...bs];
  const [m] = out.splice(from, 1);
  const t = out.findIndex((b) => b.id === targetId);
  out.splice(from < target ? t + 1 : t, 0, { ...m, section: bs[target].section });
  return normalizeBlocks(out);
}
function setBlockSection(blocks, id2, section) {
  const bs = normalizeBlocks(blocks);
  const i = bs.findIndex((b) => b.id === id2);
  if (i < 0 || bs[i].section === section) return bs;
  const up = sectionIndex(section) < sectionIndex(bs[i].section);
  const moved = { ...bs[i], section };
  const rest = bs.filter((_, k) => k !== i);
  let at = up ? rest.reduce((a, b, k) => sectionIndex(b.section) <= sectionIndex(section) ? k + 1 : a, 0) : rest.findIndex((b) => sectionIndex(b.section) >= sectionIndex(section));
  if (at < 0) at = rest.length;
  return normalizeBlocks([...rest.slice(0, at), moved, ...rest.slice(at)]);
}
var TICKET_ELEMENT_PRESETS = [
  { key: "logoImage", label: "Logo (image)", type: "logoImage", block: { section: "header", width: 200, height: 60, align: "center" } },
  { key: "logoText", label: "Logo (text)", type: "logoText", block: { section: "header", text: "LOGO", size: 34, bold: true, align: "center", width: 372, height: 48 } },
  { key: "ticketNo", label: "Ticket number", type: "text", block: { section: "header", text: "{ticketNo}", size: 22, width: 372, height: 31 } },
  { key: "date", label: "Date", type: "text", block: { section: "header", text: "{soldDate}", size: 20, dateFormat: "DD-MM-YYYY", width: 180, height: 28 } },
  { key: "time", label: "Time", type: "text", block: { section: "header", text: "{soldTime}", size: 20, timeFormat: "HH:mm", width: 120, height: 28 } },
  { key: "agent", label: "Agent", type: "text", block: { section: "header", text: "{agentCode}", size: 20, width: 180, height: 28 } },
  { key: "product", label: "Bet type / lottery", type: "text", block: { section: "header", text: "{productName}", size: 22, bold: true, align: "center", width: 372, height: 31 } },
  { key: "text", label: "Text", type: "text", block: { text: "New text", size: 22, width: 372, height: 31 } },
  { key: "lines", label: "Ticket lines", type: "lines", block: { size: 30, bold: true, align: "center", highlight: "#fde047", countFg: "#6b7280", width: 372, height: 200 } },
  { key: "total", label: "Total", type: "kv", block: { label: "\u179F\u179A\u17BB\u1794 :", text: "{total}", size: 30, bold: true, width: 372, height: 42 } },
  { key: "win", label: "Win", type: "kv", block: { label: "\u179A\u1784\u17D2\u179C\u17B6\u1793\u17CB :", text: "{win}", size: 30, bold: true, valueFg: "#1d4ed8", width: 372, height: 42, condition: { when: "won", amount: 0, products: [] } } },
  { key: "kv", label: "Label + value", type: "kv", block: { label: "Label", text: "{ticketNo}", size: 22, width: 372, height: 31 } },
  { key: "qr", label: "QR code", type: "qr", block: { text: "{checkUrl}", width: 140, height: 140, align: "center" } },
  { key: "result", label: "Result (published numbers)", type: "result", block: { size: 20, width: 372, height: 60 } },
  { key: "footer", label: "Footer / custom text", type: "text", block: { section: "footer", text: "\u179F\u17BC\u1798\u1796\u17B7\u1793\u17B7\u178F\u17D2\u1799\u17B2\u17D2\u1799\u1785\u17D2\u1794\u17B6\u179F\u17CB \u179F\u17CA\u17B8\u179F\u1784\u178F\u17B6\u1798\u1780\u1793\u17D2\u1791\u17BB\u1799", size: 20, align: "center", width: 372, height: 28 } },
  { key: "image", label: "Image", type: "image", block: { width: 200, height: 80, align: "center" } },
  { key: "divider", label: "Divider", type: "divider", block: { width: 372, height: 2, style: "solid" } },
  { key: "spacer", label: "Spacer", type: "spacer", block: { width: 16, marginBottom: 0, height: 16 } }
];
var RESIZE_HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
var snapTo = (v, grid) => grid > 1 ? Math.round(v / grid) * grid : Math.round(v);
function clampBox(b, paperW, marginX) {
  const left3 = marginX;
  const avail = Math.max(MIN_ELEMENT_SIZE, paperW - 2 * marginX);
  const width = Math.max(MIN_ELEMENT_SIZE, Math.min(b.width, avail));
  const x = Math.max(left3, Math.min(b.x, paperW - marginX - width));
  const y = Math.max(0, Math.min(b.y, MAX_PAPER_HEIGHT - MIN_ELEMENT_SIZE));
  const height = Math.max(0, Math.min(b.height, MAX_PAPER_HEIGHT - y));
  return x === b.x && y === b.y && width === b.width && height === b.height ? b : { ...b, x, y, width, height };
}
function resizeBox(b, handle, dx, dy, min = MIN_ELEMENT_SIZE) {
  let { x, y, width, height } = b;
  const h0 = height || min;
  if (handle.includes("e")) width = Math.max(min, b.width + dx);
  if (handle.includes("w")) {
    width = Math.max(min, b.width - dx);
    x = b.x + b.width - width;
  }
  if (handle.includes("s")) height = Math.max(min, h0 + dy);
  if (handle.includes("n")) {
    height = Math.max(min, h0 - dy);
    y = b.y + h0 - height;
  }
  return { ...b, x: Math.round(x), y: Math.round(y), width: Math.round(width), height: Math.round(height) };
}
var boxesOverlap = (a, b) => a.w > 0 && a.h > 0 && b.w > 0 && b.h > 0 && a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
function toFreeLayout(t, measured) {
  const W = t.paperMm === 80 ? 576 : 384;
  const blocks = t.blocks.map((b) => {
    const m = measured.boxes.find((x2) => x2.id === b.id);
    const thickness = b.type === "divider" ? Math.max(1, Math.min(b.width, 16)) : b.type === "spacer" ? b.width : 0;
    const width = b.type === "logoImage" || b.type === "image" || b.type === "qr" ? Math.min(b.width, W - 2 * t.marginX) : W - 2 * t.marginX;
    const x = b.type === "logoImage" || b.type === "image" || b.type === "qr" ? (b.align === "center" ? Math.round((W - width) / 2) : b.align === "right" ? W - t.marginX - width : t.marginX) + b.offsetX : t.marginX;
    const height = m ? thickness || Math.max(MIN_ELEMENT_SIZE, m.h) : thickness || (b.type === "lines" ? 200 : Math.round(b.size * 1.4));
    return clampBox({ ...b, x, y: m ? m.y : 0, width, height, offsetX: b.type === "logoImage" || b.type === "image" || b.type === "qr" ? 0 : b.offsetX }, W, t.marginX);
  });
  return { ...t, layout: "free", height: Math.max(50, Math.min(MAX_PAPER_HEIGHT, measured.height)), autoGrow: true, blocks };
}
function toFlowLayout(t) {
  const ordered = t.blocks.map((b, i) => [b, i]).sort((a, b) => a[0].y - b[0].y || a[0].x - b[0].x || a[1] - b[1]).map(([b]) => b);
  const blocks = ordered.map((b) => b.type === "divider" ? { ...b, width: Math.max(1, Math.min(b.height || 2, 16)) } : b.type === "spacer" ? { ...b, width: Math.max(1, b.height || b.width) } : b);
  return { ...t, layout: "flow", blocks: normalizeBlocks(blocks) };
}

// src/engine/ticket-render.ts
var PAPER_DOTS = { 58: 384, 80: 576 };
var paperDots = (mm) => PAPER_DOTS[mm] ?? 384;
var QR_MIN_MODULE = 2;
var BLACK = "#000";
var WHITE = "#fff";
var hasFormats = (f) => !!f && (!!f.dateFormat || !!f.timeFormat || (f.moneyUnit ?? "riel") !== "riel" || f.moneySeparator === false || (f.moneyDecimals ?? null) !== null || f.moneySign === false);
function formatMoney(n, f = {}) {
  const unit = f.moneyUnit ?? "riel";
  const s = formatStake(n, unit, { separator: f.moneySeparator, decimals: f.moneyDecimals });
  return unit === "riel" && f.moneySign !== false ? `${s}\u17DB` : s;
}
var formatUsd = (cents) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
function placeholderValues(d, o = {}, f = {}) {
  const custom = hasFormats(f);
  const money = d.currency === "USD" ? formatUsd : custom ? (n) => formatMoney(n, f) : o.money ?? ((n) => formatRiel(n));
  const date = (iso) => iso ? formatDateTokens(new Date(iso), f.dateFormat ?? "", DEFAULT_DATE_FORMAT) : "";
  const time = (iso) => iso ? formatDateTokens(new Date(iso), f.timeFormat ?? "", DEFAULT_TIME_FORMAT) : "";
  const dt = custom ? (iso) => iso ? `${date(iso)} ${time(iso)}` : "" : o.dateTime ?? ((iso) => iso ? fmtDateTime(new Date(iso)) : "");
  const draw = d.drawAt ? phnomPenhParts(new Date(d.drawAt)) : { date: "", time: "" };
  const dmy = (iso) => {
    if (!iso) return "";
    const p = phnomPenhParts(new Date(iso));
    return `${p.date.slice(8, 10)}-${p.date.slice(5, 7)}-${p.date.slice(0, 4)} ${p.time.slice(0, 5)}`;
  };
  return {
    ticketNo: d.ticketNo,
    productCode: d.productCode,
    productName: d.productName,
    drawDate: custom ? date(d.drawAt) : draw.date,
    drawTime: custom ? time(d.drawAt) : draw.time.slice(0, 5),
    drawAt: dt(d.drawAt),
    soldDate: date(d.soldAt),
    soldTime: time(d.soldAt),
    soldAt: dt(d.soldAt),
    drawDmy: dmy(d.drawAt),
    soldDmy: dmy(d.soldAt),
    agentCode: d.agentCode,
    agentName: d.agentName,
    total: money(d.total),
    win: money(d.win),
    commission: money(d.commission),
    lineCount: String(d.lines.length),
    numberCount: String(d.lines.reduce((a, l) => a + l.numberCount, 0)),
    status: d.status,
    printCount: String(d.printCount),
    printedDate: date(d.printedAt),
    printedTime: time(d.printedAt),
    printedAt: dt(d.printedAt),
    checkUrl: d.checkUrl,
    companyName: d.companyName ?? "",
    companyLine2: d.companyLine2 ?? ""
  };
}
function fillPlaceholders(text, values) {
  return text.replace(/\{([A-Za-z]+)\}/g, (m, k) => Object.prototype.hasOwnProperty.call(values, k) ? values[k] : m);
}
function conditionMet(c, d) {
  if (!c) return true;
  switch (c.when) {
    case "minTotal":
      return d.total >= c.amount;
    case "maxTotal":
      return d.total <= c.amount;
    case "won":
      return d.win > 0;
    case "products":
      return c.products.length === 0 || c.products.map((p) => p.toUpperCase()).includes(d.productCode.toUpperCase());
    case "reprint":
      return d.printCount > 1;
    case "firstPrint":
      return d.printCount <= 1;
    default:
      return true;
  }
}
var blockVisible = (b, d) => b.enabled && conditionMet(b.condition, d);
var fontOf = (b, family) => `${b.bold ? "bold " : ""}${b.size}px ${family}`;
var lineHeightOf = (size) => Math.round(size * 1.4);
function units(s) {
  const Seg = Intl.Segmenter;
  if (Seg) return Array.from(new Seg(void 0, { granularity: "grapheme" }).segment(s), (x) => x.segment);
  return Array.from(s);
}
var supportsLetterSpacing = (ctx) => "letterSpacing" in ctx && typeof ctx.letterSpacing === "string";
function measureWidth(ctx, text, spacing = 0) {
  if (!spacing) return ctx.measureText(text).width;
  if (supportsLetterSpacing(ctx)) {
    const was = ctx.letterSpacing;
    ctx.letterSpacing = `${spacing}px`;
    const w = ctx.measureText(text).width;
    ctx.letterSpacing = was;
    return w;
  }
  return ctx.measureText(text).width + spacing * units(text).length;
}
function wrapText(ctx, text, maxWidth, measure) {
  const out = [];
  const w = measure ?? ((s) => ctx.measureText(s).width);
  for (const para of text.split("\n")) {
    if (para === "") {
      out.push("");
      continue;
    }
    let line2 = "";
    const push = () => {
      out.push(line2.trimEnd());
      line2 = "";
    };
    for (const word of para.split(/(\s+)/)) {
      if (word === "") continue;
      if (w(line2 + word) <= maxWidth) {
        line2 += word;
        continue;
      }
      if (/^\s+$/.test(word)) {
        push();
        continue;
      }
      if (line2.trim() !== "") push();
      else line2 = "";
      if (w(word) <= maxWidth) {
        line2 = word;
        continue;
      }
      for (const u of units(word)) {
        if (line2 !== "" && w(line2 + u) > maxWidth) push();
        line2 += u;
      }
    }
    out.push(line2.trimEnd());
  }
  return out;
}
var paint = (c, hex2, fallback = BLACK) => c.color && hex2 ? hex2 : fallback;
var lineCount = (l) => l.numberCount * (l.multiplierSum ?? l.postCount ?? 1);
function xFor(align, contentW, c, offsetX) {
  const x = align === "center" ? c.left + (c.right - c.left - contentW) / 2 : align === "right" ? c.right - contentW : c.left;
  return Math.round(x + offsetX);
}
var textOp = (o, spacing) => spacing ? { kind: "text", ...o, letterSpacing: spacing } : { kind: "text", ...o };
var styleOf = (c, b, size = b.size) => ({ size, bold: b.bold, family: b.font || c.t.fontFamily, lineSpacing: b.lineSpacing ?? 0, letterSpacing: b.letterSpacing ?? 0 });
var measurerOf = (ctx, s) => (text) => measureWidth(ctx, text, s.letterSpacing);
function layoutText(c, b, text, y, family = b.font || c.t.fontFamily, size = b.size, lines) {
  const st = styleOf(c, b, size);
  const font = fontOf({ size, bold: b.bold }, family);
  c.ctx.font = font;
  const lh = lineHeightOf(size) + st.lineSpacing;
  const mw = measurerOf(c.ctx, st);
  const ls = lines ?? wrapText(c.ctx, text, c.right - c.left, mw);
  const fg = b.color === "inverse" ? WHITE : paint(c, b.fg);
  const h = ls.length * lh;
  if (b.color === "inverse") c.ops.push({ kind: "rect", x: c.left, y, w: c.right - c.left, h, color: BLACK });
  ls.forEach((l, i) => {
    c.ctx.font = font;
    const lw = mw(l);
    c.ops.push(textOp({ text: l, x: xFor(b.align, lw, c, b.offsetX), y: y + i * lh + Math.round((lineHeightOf(size) - size) / 2), font, color: fg }, st.letterSpacing));
  });
  return h;
}
function layoutKv(c, b, label, value, y, bold = b.bold, labelIndent = 0, size = b.size) {
  const st = styleOf(c, b, size);
  const font = fontOf({ size, bold }, st.family);
  c.ctx.font = font;
  const lh = lineHeightOf(size) + st.lineSpacing;
  const mw = measurerOf(c.ctx, st);
  const avail = c.right - c.left;
  const fg = b.color === "inverse" ? WHITE : paint(c, b.fg);
  const vfg = b.color === "inverse" ? WHITE : paint(c, b.valueFg || b.fg);
  const dy = Math.round((lineHeightOf(size) - size) / 2);
  const start = c.ops.length;
  let h;
  if (b.align === "center") {
    const joined = label && value ? `${label}: ${value}` : label || value;
    const lines = wrapText(c.ctx, joined, avail, mw);
    lines.forEach((l, i) => c.ops.push(textOp({ text: l, x: xFor("center", mw(l), c, b.offsetX), y: y + i * lh + dy, font, color: fg }, st.letterSpacing)));
    h = lines.length * lh;
  } else {
    const vw = mw(value);
    const gap = Math.round(size / 2);
    const labelLines = wrapText(c.ctx, label, Math.max(20, avail - labelIndent - (vw + gap <= avail * 0.7 ? vw + gap : 0)), mw);
    const lastW = mw(labelLines[labelLines.length - 1] ?? "");
    labelLines.forEach((l, i) => c.ops.push(textOp({ text: l, x: Math.round(c.left + labelIndent + b.offsetX), y: y + i * lh + dy, font, color: fg }, st.letterSpacing)));
    const sameLine = labelIndent + lastW + gap + vw <= avail;
    const row = sameLine ? labelLines.length - 1 : labelLines.length;
    const valueLines = sameLine ? [value] : wrapText(c.ctx, value, avail, mw);
    valueLines.forEach((l, i) => {
      const w = mw(l);
      c.ops.push(textOp({ text: l, x: Math.round(c.right - w + b.offsetX), y: y + (row + i) * lh + dy, font, color: vfg }, st.letterSpacing));
    });
    h = (row + valueLines.length) * lh;
  }
  if (b.color === "inverse") c.ops.splice(start, 0, { kind: "rect", x: c.left, y, w: avail, h, color: BLACK });
  return h;
}
function layoutDivider(c, b, y, thickness = b.width) {
  const th = Math.max(1, Math.min(thickness, 16));
  const x0 = Math.round(c.left + b.offsetX);
  const len = c.right - c.left;
  const BLACK2 = paint(c, b.fg);
  const seg = (yy, on, off) => {
    for (let x = 0; x < len; x += on + off) c.ops.push({ kind: "rect", x: x0 + x, y: yy, w: Math.min(on, len - x), h: th, color: BLACK2 });
  };
  switch (b.style) {
    case "solid":
      c.ops.push({ kind: "rect", x: x0, y, w: len, h: th, color: BLACK2 });
      return th;
    case "double":
      c.ops.push({ kind: "rect", x: x0, y, w: len, h: th, color: BLACK2 }, { kind: "rect", x: x0, y: y + th * 2, w: len, h: th, color: BLACK2 });
      return th * 3;
    case "dotted":
      seg(y, th, Math.max(3, th * 2));
      return th;
    default:
      seg(y, Math.max(6, th * 4), Math.max(4, th * 2));
      return th;
  }
}
var utf8Binary = (s) => {
  const bytes = new TextEncoder().encode(s);
  let out = "";
  for (const b of bytes) out += String.fromCharCode(b);
  return out;
};
function qrMatrix(text) {
  if (!text) return null;
  const qr = qrcode(0, "M");
  qr.addData(utf8Binary(text), "Byte");
  qr.make();
  const n = qr.getModuleCount();
  return Array.from({ length: n }, (_, r) => Array.from({ length: n }, (_2, col) => qr.isDark(r, col)));
}
function layoutQr(c, b, text, y, minCell = 1) {
  const m = qrMatrix(text);
  if (!m) return 0;
  const n = m.length;
  const cell = Math.max(minCell, Math.floor(Math.min(b.width, c.right - c.left) / n));
  const size = cell * n;
  const x0 = xFor(b.align, size, c, b.offsetX);
  for (let r = 0; r < n; r++) {
    let run = -1;
    for (let col = 0; col <= n; col++) {
      const dark = col < n && m[r][col];
      if (dark && run < 0) run = col;
      if (!dark && run >= 0) {
        c.ops.push({ kind: "rect", x: x0 + run * cell, y: y + r * cell, w: (col - run) * cell, h: cell, color: BLACK });
        run = -1;
      }
    }
  }
  return size;
}
function layoutImage(c, b, y, o) {
  const w = Math.min(b.width, c.right - c.left);
  const img = o.images?.[b.id];
  const h = img && img.width > 0 ? Math.round(w * img.height / img.width) : o.placeholders ? Math.round(w / 3) : 0;
  const x = xFor(b.align, w, c, b.offsetX);
  if (img && img.width > 0) c.ops.push({ kind: "image", id: b.id, x, y, w, h });
  else if (h > 0) c.ops.push({ kind: "frame", x, y, w, h });
  return h;
}
var auto = (v, fallback) => v === null || v === "auto" ? fallback : v;
function resolveLineParts(b) {
  const p = b.parts ?? DEFAULT_LINE_PARTS;
  const rowAlign = auto(p.row.align, b.align);
  const postAlign = auto(p.post.align, b.align);
  const numberSize = auto(p.number.size, b.size);
  const numberBold = auto(p.number.bold, b.bold);
  const countSize = auto(p.count.size, Math.max(8, Math.round(b.size * 0.55)));
  const countBold = auto(p.count.bold, false);
  const text = (x, size, bold) => ({
    show: x.show ?? true,
    size: auto(x.size, size),
    bold: auto(x.bold, bold),
    font: x.font,
    color: x.color,
    offsetX: x.offsetX,
    offsetY: x.offsetY
  });
  return {
    post: { ...text(p.post, Math.min(120, Math.round(b.size * 1.15)), true), placement: p.post.placement, align: postAlign, spaceAbove: auto(p.post.spaceAbove, Math.round(b.size / 3)), spaceBelow: p.post.spaceBelow },
    rule: { show: p.rule.show, lengthPct: p.rule.lengthPct, thickness: p.rule.thickness, align: auto(p.rule.align, postAlign), color: p.rule.color, offsetX: p.rule.offsetX, offsetY: p.rule.offsetY },
    row: { align: rowAlign, offsetX: p.row.offsetX, spacing: p.row.spacing, indent: p.row.indent },
    count: { ...text(p.count, countSize, countBold), placement: p.count.placement, format: p.count.format, align: p.count.align },
    number: { ...text(p.number, numberSize, numberBold), winMark: p.number.winMark },
    amount: { ...text(p.amount, b.size, b.bold), placement: p.amount.placement, format: p.amount.format, unit: p.amount.unit ?? "hundreds", riel: p.amount.riel, pushRight: p.amount.pushRight, onEndRow: p.amount.onEndRow, align: p.amount.align },
    bar: text(p.bar, numberSize, numberBold),
    rollCount: text(p.rollCount, countSize, countBold),
    mode: { ...text(p.mode, Math.max(8, Math.round(b.size * 0.7)), countBold), placement: p.mode.placement, align: p.mode.align, onBarRow: p.mode.onBarRow ?? false }
  };
}
var isInline = (pl) => pl === "before" || pl === "after" || pl === "end";
function layoutLines(c, b, d, y, money, boxes) {
  const P = resolveLineParts(b);
  const fam = b.font || c.t.fontFamily;
  const avail = c.right - c.left;
  const fg = paint(c, b.fg);
  const col = (hex2, fallback) => c.color && hex2 ? hex2 : fallback;
  const postFg = col(P.post.color, fg);
  const ruleFg = col(P.rule.color, fg);
  const numberBase = col(P.number.color, fg);
  const amountBase = col(P.amount.color, paint(c, b.valueFg || b.fg));
  const countBase = col(P.count.color, paint(c, b.countFg || "#6b7280"));
  const rollCountBase = col(P.rollCount.color, countBase);
  const modeBase = col(P.mode.color, countBase);
  const barBase = col(P.bar.color, numberBase);
  const rowOff = b.offsetX + P.row.offsetX;
  const seg = (part, text, T, color2) => {
    const font = T.font || fam;
    c.ctx.font = fontOf(T, font);
    return { part, text, size: T.size, bold: T.bold, font, color: color2, offsetX: T.offsetX, offsetY: T.offsetY, w: c.ctx.measureText(text).width };
  };
  const widthOf = (ss) => ss.reduce((a, s) => a + s.w, 0);
  const place = (ss, x0, top, size) => {
    const lh = lineHeightOf(size);
    let acc = 0;
    return ss.map((s) => {
      const at = x0 + acc;
      const x = Math.round(at + s.offsetX);
      c.ops.push({ kind: "text", text: s.text, x, y: top + Math.round((lh - size) / 2) + Math.round((size - s.size) * 0.8) + s.offsetY, font: fontOf(s, s.font), color: s.color, part: s.part });
      boxes.push({ part: s.part, x, y: top + s.offsetY, w: Math.round(s.w), h: lh });
      acc += s.w;
      return at;
    });
  };
  const postRow = (label, top) => {
    const start = c.ops.length;
    let h2 = layoutText(c, { ...b, color: "black", fg: P.post.color || b.fg, size: P.post.size, bold: P.post.bold, align: P.post.align, offsetX: b.offsetX + P.post.offsetX }, label, top, P.post.font || fam);
    for (const op of c.ops.slice(start)) {
      if (op.kind !== "text") continue;
      op.part = "post";
      op.y += P.post.offsetY;
      if (postFg !== fg) op.color = postFg;
    }
    boxes.push({ part: "post", x: c.left, y: top + P.post.offsetY, w: avail, h: h2 });
    if (P.rule.show) {
      const len = Math.max(1, Math.round(avail * P.rule.lengthPct / 100));
      const x = xFor(P.rule.align, len, c, b.offsetX + P.rule.offsetX);
      c.ops.push({ kind: "rect", x, y: top + h2 + 1 + P.rule.offsetY, w: len, h: P.rule.thickness, color: ruleFg, part: "rule" });
      boxes.push({ part: "rule", x, y: top + h2 - 2 + P.rule.offsetY, w: len, h: P.rule.thickness + 6 });
      h2 += P.rule.thickness + 5;
    }
    return h2;
  };
  let h = 0;
  let last = null;
  d.lines.forEach((l, li) => {
    const newGroup = l.groupLabel !== last;
    const lastOfGroup = li === d.lines.length - 1 || d.lines[li + 1].groupLabel !== l.groupLabel;
    if (P.post.show && P.post.placement === "above" && newGroup) {
      if (last !== null) h += P.post.spaceAbove;
      h += postRow(l.groupLabel, y + h) + P.post.spaceBelow;
    }
    last = l.groupLabel;
    const disp = lineDisplay(l);
    const n = lineCount(l);
    const won = (l.winAmount ?? 0) > 0;
    const top = y + h;
    const opsStart = c.ops.length;
    const inverse = won && !c.color && P.number.winMark === "band";
    const winFg = won && c.color && P.number.winMark !== "box" ? b.highlight || "#1d4ed8" : null;
    const tone = (hex2) => inverse ? WHITE : winFg ?? hex2;
    const numberFg = P.number.winMark === "box" && won && !c.color ? WHITE : tone(numberBase);
    const amountFg = tone(amountBase);
    const countFg = tone(countBase);
    const cnt = n > 1 ? P.count.format === "x" ? `x${n}` : `(${n})` : "";
    const countOn = P.count.show && cnt !== "";
    const money1 = d.currency === "USD" ? formatUsd(l.amountPerNumber) : P.amount.unit === "hundreds" ? formatStake(l.amountPerNumber, "hundreds") : `${money(l.amountPerNumber)}${P.amount.riel ? "\u17DB" : ""}`;
    const combo2 = l.mode === "multiply" || !!l.mode?.endsWith("_x");
    const auto2 = P.amount.format === "auto";
    const amountText = auto2 ? combo2 ? money1 : `: ${money1}` : P.amount.format === "x" ? `x ${money1}` : P.amount.format === "colon" ? `: ${money1}` : money1;
    const modeOnBar = P.mode.show && !!disp.modeKm && disp.to !== null && P.mode.onBarRow;
    const modeOn = P.mode.show && !!disp.modeKm && !modeOnBar;
    const pieces = {
      post: { part: "post", text: l.groupLabel, T: P.post, color: tone(postFg) },
      count: { part: "count", text: cnt, T: P.count, color: countFg },
      amount: { part: "amount", text: amountText, T: P.amount, color: amountFg },
      mode: { part: "mode", text: disp.modeKm ?? "", T: P.mode, color: tone(modeBase) }
    };
    const numberSeg = (text) => seg("number", text, P.number, numberFg);
    const inlineSeg = (p, before, first = false, stick = false) => seg(p.part, before ? `${p.text} ` : first || stick ? p.text : ` ${p.text}`, p.T, p.color);
    const winBox = (colX2, w, rowTop2, rowH, at) => {
      if (won && P.number.winMark === "box") c.ops.splice(at, 0, { kind: "rect", x: Math.round(colX2 + P.number.offsetX - 3), y: rowTop2 + 1, w: Math.round(w + 6), h: rowH - 2, color: c.color ? b.highlight || "#fde047" : BLACK, part: "number" });
    };
    const rowsAt = (pl) => {
      const out = [];
      const order = pl === "above" ? ["count", "amount", "mode"] : ["amount", "count", "mode"];
      for (const k of order) {
        const on = k === "count" ? countOn : k === "amount" ? P.amount.show : modeOn;
        if (on && P[k].placement === pl) out.push(pieces[k]);
      }
      return out;
    };
    const above = rowsAt("above");
    const below = rowsAt("below");
    let lineH = above.reduce((a, p) => a + lineHeightOf(p.T.size), 0);
    const num = numberSeg(disp.from);
    const slots = { before: [], after: [], end: [] };
    if (P.post.show && isInline(P.post.placement)) slots[P.post.placement].push(pieces.post);
    if (countOn && isInline(P.count.placement)) slots[P.count.placement].push(pieces.count);
    if (P.amount.show && isInline(P.amount.placement)) slots[P.amount.placement].push(pieces.amount);
    const ordered = [...slots.before, "number", ...slots.after, ...slots.end];
    const numIdx = slots.before.length;
    const amountIdx = ordered.findIndex((p) => p !== "number" && p.part === "amount");
    const split = P.amount.pushRight && amountIdx > numIdx ? amountIdx : ordered.length;
    const toSegs = (ps, from) => ps.map((p, i) => p === "number" ? num : inlineSeg(p, from + i < numIdx, from + i === split, auto2 && !combo2 && p.part === "amount" && from + i === numIdx + 1));
    const lead = toSegs(ordered.slice(0, split), 0);
    const tail = toSegs(ordered.slice(split), split);
    const rowSize = Math.max(...lead.map((s) => s.size), ...tail.map((s) => s.size));
    const lh = lineHeightOf(rowSize);
    const rowTop = top + lineH;
    let colX;
    let amountX = null;
    if (tail.length) {
      const lw = widthOf(lead);
      const tw = widthOf(tail);
      colX = place(lead, xFor(P.row.align, lw, c, rowOff + P.row.indent), rowTop, rowSize)[lead.indexOf(num)];
      const fits = P.row.indent + lw + Math.round(rowSize / 2) + tw <= avail;
      place(tail, c.right - tw + rowOff, fits ? rowTop : rowTop + lh, rowSize);
      winBox(colX, num.w, rowTop, lh, opsStart);
      lineH += fits ? lh : 2 * lh;
    } else {
      const uw = widthOf(lead);
      if (uw <= avail) {
        const xs = place(lead, xFor(P.row.align, uw, c, rowOff), rowTop, rowSize);
        colX = xs[lead.indexOf(num)];
        if (amountIdx >= 0) amountX = xs[amountIdx];
        winBox(colX, num.w, rowTop, lh, opsStart);
        lineH += lh;
      } else {
        const font = fontOf(P.number, P.number.font || fam);
        c.ctx.font = font;
        const wrapped = wrapText(c.ctx, lead.map((s) => s.text).join(""), avail);
        const wl = lineHeightOf(P.number.size);
        wrapped.forEach((text, i) => {
          c.ctx.font = font;
          const w = c.ctx.measureText(text).width;
          const x = xFor(P.row.align, w, c, rowOff);
          c.ops.push({ kind: "text", text, x, y: rowTop + i * wl + Math.round((wl - P.number.size) / 2), font, color: P.number.winMark === "box" ? fg : tone(fg), part: "row" });
        });
        colX = xFor(P.row.align, num.w, c, rowOff);
        lineH += wrapped.length * wl;
      }
    }
    const ownRow = (p, rowY) => {
      const s = seg(p.part, p.text, p.T, p.color);
      const al = P[p.part].align;
      let x;
      if (al === "auto") {
        const at = P.row.align === "left" ? colX : P.row.align === "right" ? colX + num.w - s.w : colX + (num.w - s.w) / 2;
        x = Math.max(c.left, Math.min(at - rowOff, c.right - s.w)) + rowOff;
      } else x = xFor(al, s.w, c, rowOff);
      place([s], x, rowY, s.size);
      return lineHeightOf(s.size);
    };
    if (disp.to !== null) {
      const bar = P.bar.show ? seg("bar", "|", P.bar, tone(barBase)) : null;
      const rc = P.rollCount.show ? seg("rollCount", `(${l.numberCount} \u179B\u17C1\u1781)`, P.rollCount, tone(rollCountBase)) : null;
      const barMode = modeOnBar ? seg("mode", disp.modeKm, P.mode, tone(modeBase)) : null;
      if (bar || rc || barMode) {
        const barSize = Math.max(bar?.size ?? 0, rc?.size ?? 0, barMode?.size ?? 0);
        const barX = colX + (num.w - (bar?.w ?? 0)) / 2;
        if (bar) place([bar], barX, top + lineH, barSize);
        const rcX = barX + (bar ? bar.w + Math.round(bar.size * 0.6) : 0);
        if (rc) place([rc], rcX, top + lineH, barSize);
        if (barMode) place([barMode], rcX + (rc ? rc.w + Math.round(barSize * 0.8) : 0), top + lineH, barSize);
        lineH += lineHeightOf(barSize);
      }
      const endTop = top + lineH;
      const endNum = numberSeg(disp.to);
      const modeAt = modeOn && isInline(P.mode.placement) ? P.mode.placement : null;
      const endLead = [];
      if (modeAt === "before") endLead.push(inlineSeg(pieces.mode, true));
      endLead.push(endNum);
      if (modeAt === "after") endLead.push(inlineSeg(pieces.mode, false));
      const endAmount = P.amount.show && P.amount.onEndRow ? inlineSeg(pieces.amount, false, P.amount.pushRight, auto2 && !combo2 && modeAt !== "after") : null;
      const endTail = [];
      if (endAmount && P.amount.pushRight) endTail.push(endAmount);
      if (modeAt === "end") endTail.push(inlineSeg(pieces.mode, false, endTail.length === 0 && P.amount.pushRight));
      const endSize = Math.max(...endLead.map((s) => s.size), endAmount?.size ?? 0, ...endTail.map((s) => s.size));
      const eh = lineHeightOf(endSize);
      const endStart = c.ops.length;
      const leadBefore = endLead.slice(0, endLead.indexOf(endNum)).reduce((a, s) => a + s.w, 0);
      place(endLead, colX - leadBefore, endTop, endSize);
      let endRows = 1;
      let endX = colX - leadBefore + widthOf(endLead);
      if (endAmount && !P.amount.pushRight) {
        const ax = amountX !== null && modeAt !== "after" ? amountX : endX;
        place([endAmount], ax, endTop, endSize);
        endX = ax + endAmount.w;
      }
      if (endTail.length) {
        if (P.amount.pushRight && endAmount) {
          const tw = widthOf(endTail);
          const ax = c.right - tw + rowOff;
          if (endX + Math.round(endSize / 2) > ax) endRows = 2;
          place(endTail, ax, endRows === 2 ? endTop + eh : endTop, endSize);
        } else place(endTail, endX, endTop, endSize);
      }
      winBox(colX, endNum.w, endTop, eh, endStart);
      lineH += endRows * eh;
    }
    for (const p of below) lineH += ownRow(p, top + lineH);
    let aboveY = top;
    for (const p of above) aboveY += ownRow(p, aboveY);
    if (won && !c.color && P.number.winMark === "bar") c.ops.splice(opsStart, 0, { kind: "rect", x: c.left, y: top, w: Math.max(3, Math.round(b.size / 6)), h: lineH, color: BLACK, part: "row" });
    if (inverse) c.ops.splice(opsStart, 0, { kind: "rect", x: c.left, y: top, w: avail, h: lineH, color: BLACK, part: "row" });
    boxes.push({ part: "row", x: c.left, y: top, w: avail, h: lineH });
    h += lineH + P.row.spacing;
    if (P.post.show && P.post.placement === "below" && lastOfGroup) {
      h += P.post.spaceAbove;
      h += postRow(l.groupLabel, y + h) + P.post.spaceBelow;
    }
  });
  return h;
}
var valuesFor = (b, base, d, o) => hasFormats(b) ? placeholderValues(d, o, b) : base;
function resultText(results) {
  if (!results || results.length === 0) return "\u2014";
  return results.map((r) => `${r.postCode}${r.digitLen ? ` ${r.digitLen}D` : ""} ${r.numbers.join(" ")}`.trim()).join("\n");
}
function layoutTicket(ctx, t, d, o = {}) {
  return t.layout === "free" ? layoutFree(ctx, t, d, o) : layoutFlow(ctx, t, d, o);
}
function layoutFlow(ctx, t, d, o) {
  const W = paperDots(t.paperMm);
  const c = { ctx, t, W, left: t.marginX, right: W - t.marginX, ops: [], color: !!o.color };
  const boxes = [];
  const base = placeholderValues(d, o);
  const money = o.money ?? ((n) => formatRiel(n, false));
  let y = t.paddingTop;
  for (const b of t.blocks) {
    const visible = blockVisible(b, d);
    if (!visible && !o.showHidden) {
      boxes.push({ id: b.id, section: b.section, top: y, bottom: y, x: c.left, y, w: 0, h: 0, visible: false });
      continue;
    }
    const top = y;
    y += b.marginTop;
    const values = valuesFor(b, base, d, o);
    const text = fillPlaceholders(b.text, values);
    let h = 0;
    let parts;
    switch (b.type) {
      case "text":
      case "logoText":
        h = text ? layoutText(c, b, text, y) : 0;
        break;
      case "result":
        h = layoutText(c, b, resultText(d.results), y);
        break;
      case "kv":
        h = layoutKv(c, b, fillPlaceholders(b.label, values), text, y);
        break;
      case "divider":
        h = layoutDivider(c, b, y);
        break;
      case "spacer":
        h = b.width;
        break;
      case "qr":
        h = layoutQr(c, b, text, y);
        break;
      case "logoImage":
      case "image":
        h = layoutImage(c, b, y, o);
        break;
      case "lines":
        parts = [];
        h = layoutLines(c, b, d, y, money, parts);
        break;
    }
    boxes.push({ id: b.id, section: b.section, top, bottom: y + h + b.marginBottom, x: c.left, y, w: c.right - c.left, h, visible, ...parts ? { parts } : {} });
    y += h + b.marginBottom;
  }
  return { width: W, height: Math.max(1, Math.ceil(y + t.paddingBottom)), ops: c.ops, boxes, warnings: [] };
}
function ellipsize(text, maxW, measure) {
  if (measure(text) <= maxW) return text;
  const u = units(text);
  for (let n = u.length - 1; n > 0; n--) {
    const s = u.slice(0, n).join("").trimEnd() + "\u2026";
    if (measure(s) <= maxW) return s;
  }
  return measure("\u2026") <= maxW ? "\u2026" : "";
}
function fitText(ctx, text, b, family, boxW, boxH) {
  const wrapAt = (size2) => {
    ctx.font = fontOf({ size: size2, bold: b.bold }, family);
    const mw = measurerOf(ctx, { letterSpacing: b.letterSpacing });
    return { lines: wrapText(ctx, text, boxW, mw), mw, lh: lineHeightOf(size2) + b.lineSpacing };
  };
  const fits = (r2) => r2.lines.length * r2.lh <= boxH && r2.lines.every((l) => r2.mw(l) <= boxW);
  if (b.overflow === "wrap" || boxH <= 0) return { lines: wrapAt(b.size).lines, size: b.size, truncated: false, shrunk: false };
  let size = b.size;
  let r = wrapAt(size);
  if (b.overflow === "shrink") {
    const floor = Math.min(b.minSize, b.size);
    while (!fits(r) && size > floor) {
      size = Math.max(floor, size - 1);
      r = wrapAt(size);
    }
    if (fits(r)) return { lines: r.lines, size, truncated: false, shrunk: size < b.size };
  }
  const max = Math.max(1, Math.floor(boxH / r.lh));
  const kept = r.lines.slice(0, max);
  const rest = r.lines.slice(max).join(" ").trim();
  const last = kept.length - 1;
  const lastText = rest ? `${kept[last]} ${rest}` : kept[last];
  kept[last] = ellipsize(lastText, boxW, r.mw);
  const truncated = r.lines.length > max || kept.some((l, i) => l !== r.lines[i]);
  return { lines: kept, size, truncated, shrunk: size < b.size };
}
function layoutFree(ctx, t, d, o) {
  const W = paperDots(t.paperMm);
  const color2 = !!o.color;
  const base = placeholderValues(d, o);
  const money = o.money ?? ((n) => formatRiel(n, false));
  const warnings = [];
  const els = [];
  for (const b of t.blocks) {
    const visible = blockVisible(b, d);
    const box = clampBox({ x: b.x, y: b.y, width: b.width, height: b.height }, W, t.marginX);
    if (!visible && !o.showHidden) {
      els.push({ b, box, visible: false, ops: [], contentH: 0, grew: 0 });
      continue;
    }
    if (box.x !== b.x || box.width !== b.width) warnings.push({ id: b.id, kind: "offPaper", message: `${b.id}: moved / shrunk to stay on the paper` });
    const c = { ctx, t, W, left: box.x, right: box.x + box.width, ops: [], color: color2 };
    const values = valuesFor(b, base, d, o);
    const text = fillPlaceholders(b.text, values);
    const family = b.font || t.fontFamily;
    const boxH = box.height;
    let h = 0;
    let parts;
    const fitted = (s) => {
      const f = fitText(ctx, s, b, family, box.width, boxH);
      if (f.truncated) warnings.push({ id: b.id, kind: "overflow", message: `${b.id}: text does not fit its box \u2014 cut with "\u2026"` });
      else if (f.shrunk) warnings.push({ id: b.id, kind: "shrunk", message: `${b.id}: text shrunk to ${f.size} dots to fit` });
      return layoutText(c, b, s, 0, family, f.size, f.lines);
    };
    switch (b.type) {
      case "text":
      case "logoText":
        h = text ? fitted(text) : 0;
        break;
      case "result":
        h = fitted(resultText(d.results));
        break;
      case "kv": {
        const label = fillPlaceholders(b.label, values);
        let size = b.size;
        let value = text;
        if (boxH > 0 && b.overflow !== "wrap") {
          const oneLine = (s) => {
            ctx.font = fontOf({ size: s, bold: b.bold }, family);
            const mw = measurerOf(ctx, b);
            return { need: mw(label) + Math.round(s / 2) + mw(value), lw: mw(label), mw };
          };
          let m = oneLine(size);
          if (b.overflow === "shrink") {
            const floor = Math.min(b.minSize, b.size);
            while ((m.need > box.width || lineHeightOf(size) + b.lineSpacing > boxH) && size > floor) {
              size--;
              m = oneLine(size);
            }
          }
          if (m.need > box.width) {
            value = ellipsize(value, Math.max(0, box.width - m.lw - Math.round(size / 2)), m.mw);
            warnings.push({ id: b.id, kind: "overflow", message: `${b.id}: value does not fit beside its label \u2014 cut with "\u2026"` });
          } else if (size < b.size) warnings.push({ id: b.id, kind: "shrunk", message: `${b.id}: text shrunk to ${size} dots to fit` });
        }
        h = layoutKv(c, b, label, value, 0, b.bold, 0, size);
        break;
      }
      case "divider":
        h = layoutDivider(c, b, 0, boxH || 2);
        break;
      case "spacer":
        h = boxH;
        break;
      case "qr": {
        const m = qrMatrix(text);
        if (m) {
          const n = m.length;
          const want = Math.min(box.width, boxH || box.width);
          let cell = Math.floor(want / n);
          if (cell < QR_MIN_MODULE) {
            cell = Math.min(QR_MIN_MODULE, Math.max(1, Math.floor((W - 2 * t.marginX) / n)));
            warnings.push({ id: b.id, kind: "qrSmall", message: `${b.id}: QR enlarged to ${cell * n} dots so it stays readable` });
          }
          const size = cell * n;
          const qc = { ...c, left: Math.min(box.x, W - t.marginX - size), right: Math.max(box.x + box.width, Math.min(box.x, W - t.marginX - size) + size) };
          h = layoutQr(qc, { ...b, width: size, offsetX: 0 }, text, 0, cell);
        }
        break;
      }
      case "logoImage":
      case "image": {
        const img = o.images?.[b.id];
        let w = box.width;
        if (img && img.width > 0) {
          const scale = boxH > 0 ? Math.min(box.width / img.width, boxH / img.height) : box.width / img.width;
          w = Math.max(1, Math.round(img.width * scale));
          h = Math.max(1, Math.round(img.height * scale));
          c.ops.push({ kind: "image", id: b.id, x: xFor(b.align, w, c, 0), y: 0, w, h });
        } else if (o.placeholders) {
          h = boxH || Math.round(w / 3);
          c.ops.push({ kind: "frame", x: box.x, y: 0, w, h });
        }
        break;
      }
      case "lines":
        parts = [];
        h = layoutLines(c, b, d, 0, money, parts);
        break;
    }
    const grew = boxH > 0 && h > boxH ? h - boxH : 0;
    if (grew) warnings.push({ id: b.id, kind: "grew", message: `${b.id}: content needs ${h} dots, box is ${boxH} \u2014 elements below move down by ${grew}` });
    els.push({ b, box, visible: true, ops: c.ops, parts, contentH: h, grew });
  }
  const ops = [];
  const boxes = [];
  const rendered = [];
  let bottom = 0;
  for (const e of els) {
    const shift = els.reduce((a, g) => g !== e && g.visible && g.grew && g.box.height > 0 && g.box.y + g.box.height <= e.box.y ? a + g.grew : a, 0);
    const top = e.box.y + shift;
    if (!e.visible) {
      boxes.push({ id: e.b.id, section: e.b.section, top, bottom: top, x: e.box.x, y: top, w: 0, h: 0, visible: false });
      continue;
    }
    const boxH = e.box.height;
    const h = Math.max(boxH, e.contentH);
    const dy = boxH > e.contentH ? e.b.valign === "middle" ? Math.round((boxH - e.contentH) / 2) : e.b.valign === "bottom" ? boxH - e.contentH : 0 : 0;
    for (const op of e.ops) ops.push({ ...op, y: op.y + top + dy });
    const parts = e.parts?.map((p) => ({ ...p, y: p.y + top + dy }));
    boxes.push({ id: e.b.id, section: e.b.section, top, bottom: top + h, x: e.box.x, y: top, w: e.box.width, h, visible: true, ...parts ? { parts } : {} });
    rendered.push({ id: e.b.id, x: e.box.x, y: top, w: e.box.width, h });
    bottom = Math.max(bottom, top + h);
  }
  for (let i = 0; i < rendered.length; i++)
    for (let j = i + 1; j < rendered.length; j++) {
      const a = rendered[i];
      const b = rendered[j];
      if (boxesOverlap(a, b)) warnings.push({ id: a.id, kind: "overlap", with: b.id, message: `${a.id} overlaps ${b.id}` });
    }
  const need = Math.ceil(bottom + t.paddingBottom);
  const height = t.autoGrow ? Math.max(t.height, need) : t.height;
  if (!t.autoGrow) {
    for (const r of rendered) if (r.y + r.h > height) warnings.push({ id: r.id, kind: "clipped", message: `${r.id}: below the paper end (${height} dots) \u2014 turn on auto-grow or move it up` });
  }
  return { width: W, height: Math.max(1, height), ops, boxes, warnings };
}
function drawTicket(ctx, layout, o = {}) {
  ctx.fillStyle = WHITE;
  ctx.fillRect(0, 0, layout.width, layout.height);
  ctx.textBaseline = "top";
  const native = supportsLetterSpacing(ctx);
  let spaced = false;
  for (const op of layout.ops) {
    if (op.kind === "rect") {
      ctx.fillStyle = op.color;
      ctx.fillRect(op.x, op.y, op.w, op.h);
    } else if (op.kind === "text") {
      ctx.font = op.font;
      ctx.fillStyle = op.color;
      const spacing = op.letterSpacing ?? 0;
      if (native) {
        if (spacing || spaced) ctx.letterSpacing = `${spacing}px`;
        spaced = spacing !== 0;
        ctx.fillText(op.text, op.x, op.y);
      } else if (!spacing) ctx.fillText(op.text, op.x, op.y);
      else {
        let x = op.x;
        for (const u of units(op.text)) {
          ctx.fillText(u, x, op.y);
          x += ctx.measureText(u).width + spacing;
        }
      }
    } else if (op.kind === "image") {
      const img = o.images?.[op.id];
      if (img && ctx.drawImage) ctx.drawImage(img.image, op.x, op.y, op.w, op.h);
    } else if (op.kind === "frame") {
      ctx.fillStyle = BLACK;
      for (let x = op.x; x < op.x + op.w; x += 8) {
        ctx.fillRect(x, op.y, 4, 1);
        ctx.fillRect(x, op.y + op.h - 1, 4, 1);
      }
      for (let yy = op.y; yy < op.y + op.h; yy += 8) {
        ctx.fillRect(op.x, yy, 1, 4);
        ctx.fillRect(op.x + op.w - 1, yy, 1, 4);
      }
    }
  }
}
function renderTicket(ctx, t, d, o = {}) {
  const layout = layoutTicket(ctx, t, d, o);
  drawTicket(ctx, layout, o);
  return layout;
}
function packMonochrome(rgba, width, height, threshold = 160) {
  const bytesPerRow = Math.ceil(width / 8);
  const bits = new Uint8Array(bytesPerRow * height);
  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++) {
      const p = (y * width + x) * 4;
      const a = (rgba[p + 3] ?? 255) / 255;
      const lum = (0.299 * rgba[p] + 0.587 * rgba[p + 1] + 0.114 * rgba[p + 2]) * a + 255 * (1 - a);
      if (lum < threshold) bits[y * bytesPerRow + (x >> 3)] |= 128 >> (x & 7);
    }
  return { bytesPerRow, bits };
}
function escPosRaster(bits, bytesPerRow, height, band = 128) {
  const out = [27, 64];
  for (let top = 0; top < height; top += band) {
    const rows = Math.min(band, height - top);
    out.push(29, 118, 48, 0, bytesPerRow & 255, bytesPerRow >> 8 & 255, rows & 255, rows >> 8 & 255);
    for (let i = top * bytesPerRow; i < (top + rows) * bytesPerRow; i++) out.push(bits[i]);
  }
  out.push(27, 100, 3);
  return Uint8Array.from(out);
}
var sampleNumber = (digitLen2, seed) => String(seed % 10 ** digitLen2).padStart(digitLen2, "0");
function sampleTicketDataForGame(game, opts = {}) {
  const postCodes = game.posts.map((x) => x.code);
  const widest = [...game.postGroups].sort((a, b) => b.postCodes.length - a.postCodes.length)[0];
  const mainLabel = widest?.label || widest?.code || postCodes.join("") || game.code;
  const mainCount = widest ? widest.postCodes.length : postCodes.length || 1;
  const firstPost = postCodes[0] ?? "A";
  const lens = game.digitLens.length ? game.digitLens : [2, 3];
  const modes = (opts.conditions?.length ? opts.conditions : game.betModes.length ? game.betModes : ["single"]).filter((m) => m !== "multiply" || true);
  const lines = [];
  let seed = 12;
  for (const mode of modes) {
    const digitLen2 = lens[lines.length % lens.length] ?? 2;
    const from = sampleNumber(digitLen2, seed += 137);
    if (mode === "single" || mode === "multiply") {
      lines.push({
        groupLabel: mainLabel,
        label: mode === "multiply" ? `${from}x` : from,
        mode,
        input1: from,
        digitLen: digitLen2,
        amountPerNumber: 1e3,
        numberCount: mode === "multiply" ? 6 : 1,
        postCount: mainCount,
        multiplierSum: mainCount
      });
      continue;
    }
    const to = sampleNumber(digitLen2, seed + 90);
    const km = rollModeKm(mode);
    lines.push({
      groupLabel: firstPost,
      label: `${from} | ${to}${km ? ` ${km}` : ""}`,
      mode,
      input1: from,
      input2: to,
      digitLen: digitLen2,
      amountPerNumber: 500,
      numberCount: 10,
      postCount: 1,
      multiplierSum: 4
    });
  }
  const total = lines.reduce((sum, l) => sum + l.amountPerNumber * l.numberCount * (l.multiplierSum ?? 1), 0);
  return sampleTicketData({
    productCode: game.code,
    productName: game.nameKm,
    results: postCodes.slice(0, 4).map((code, i) => ({ postCode: code, numbers: [`${sampleNumber(2, 27 + i * 13)}-${sampleNumber(3, 260 + i * 47)}`] })),
    lines,
    total,
    ...opts.over
  });
}
function sampleTicketData(over = {}) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    ticketNo: "VN-20260913-000123",
    productCode: "VN",
    productName: "\u1786\u17D2\u1793\u17C4\u178F\u1799\u17BD\u1793",
    drawAt: now,
    soldAt: now,
    printedAt: now,
    agentCode: "AGT001",
    agentName: "Agent 001",
    total: 45e3,
    win: 0,
    commission: 4500,
    status: "active",
    printCount: 1,
    checkUrl: "https://vn.data5sn.com/t/VN-20260913-000123",
    // published result of the sample draw (the editor's "Result published" toggle passes [] to show "—")
    results: [
      { postCode: "A", numbers: ["27-260", "96-200", "41-639", "84"] },
      { postCode: "B", numbers: ["90-427"] },
      { postCode: "C", numbers: ["27-904"] },
      { postCode: "D", numbers: ["97-042"] }
    ],
    lines: [
      { groupLabel: "ABCD", label: "12", mode: "single", input1: "12", digitLen: 2, amountPerNumber: 1e3, numberCount: 1, postCount: 4, multiplierSum: 7 },
      { groupLabel: "ABCD", label: "345", mode: "single", input1: "345", digitLen: 3, amountPerNumber: 500, numberCount: 1, postCount: 4, multiplierSum: 6, winAmount: over.win ?? 0 },
      // a roll: printed as "10" / "|" (10 លេខ) / "19" / អូសធម្មតា
      { groupLabel: "A", label: "10 | 19 \u17A2\u17BC\u179F\u1792\u1798\u17D2\u1798\u178F\u17B6", mode: "roll_range", input1: "10", input2: "19", digitLen: 2, amountPerNumber: 1e3, numberCount: 10, postCount: 1, multiplierSum: 4 },
      { groupLabel: "Lo", label: "11", mode: "single", input1: "11", digitLen: 2, amountPerNumber: 100, numberCount: 1, postCount: 1, multiplierSum: 32 }
    ],
    ...over
  };
}

// src/engine/vn-results.ts
var XSMN_LENGTHS = [2, 3, 4, 4, 4, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 6];
var XSMB_LENGTHS = [5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 3, 3, 3, 2, 2, 2, 2];
var THINHNAM_LENGTHS = [2, 3, 4, 4, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5];
function parseNumberList(text) {
  return (text.match(/\d+/g) ?? []).map((s) => s);
}
function normalise(numbers, lengths, what) {
  if (numbers.length !== lengths.length) throw new Error(`${what}: expected ${lengths.length} numbers, got ${numbers.length}`);
  return numbers.map((n, i) => {
    const len = lengths[i];
    if (n.length > len) throw new Error(`${what}: number ${i + 1} (${n}) is longer than ${len} digits`);
    return n.padStart(len, "0");
  });
}
var combo = (d2, d3) => `${d2}-${d3}`;
function specialPosts(g5) {
  const g = g5.slice(-5).padStart(5, "0");
  return {
    B: combo(g.slice(0, 2), g.slice(2, 5)),
    C: combo(g.slice(3, 5), g.slice(0, 3)),
    D: combo(g[0] + g[4], g.slice(1, 4))
  };
}
function deriveThinhNam(main, second, third) {
  const notes = [];
  const m = normalise(main, THINHNAM_LENGTHS, "main province");
  const g = m[14];
  const sp = specialPosts(g);
  const last = (n, k) => n.slice(-k);
  const lo2 = [m[0], last(m[1], 2), ...m.slice(2, 14).map((n) => last(n, 2)), g.slice(0, 2), last(g.slice(-3), 2), last(g, 2), last(g.slice(0, 3), 2), g[0] + g[4], last(g.slice(1, 4), 2)];
  const lo3 = [m[1], ...m.slice(2, 14).map((n) => last(n, 3)), last(g, 3), g.slice(0, 3), g.slice(1, 4)];
  const entries = [
    { postCode: "A", numbers: [combo(m[0], m[1])] },
    { postCode: "B", numbers: [sp.B] },
    { postCode: "C", numbers: [sp.C] },
    { postCode: "D", numbers: [sp.D] },
    { postCode: "Lo", digitLen: 2, numbers: lo2 },
    { postCode: "Lo", digitLen: 3, numbers: lo3 }
  ];
  if (second && second.length) {
    const s2 = normalise(second, THINHNAM_LENGTHS, "second province");
    entries.push({ postCode: "F", numbers: [combo(s2[0], s2[1])] }, { postCode: "K", numbers: [combo(last(s2[2], 2), last(s2[2], 3))] }, { postCode: "N", numbers: [specialPosts(s2[14]).B] });
  } else notes.push("F, K, N not filled: no second province");
  entries.push({ postCode: "O", numbers: [combo(last(m[1], 2), m[1])] });
  if (third && third.length) {
    const t = normalise(third, THINHNAM_LENGTHS, "third province");
    entries.push({ postCode: "I", numbers: [combo(t[0], t[1])] }, { postCode: "P", numbers: [specialPosts(t[14]).B] });
  } else notes.push("I, P not filled: no third province");
  entries.push({ postCode: "Z", numbers: [combo(last(m[2], 2), last(m[2], 3))] });
  notes.push("3P has no printed source: enter it by hand");
  return { entries, notes };
}
function ruleFor(productCode, drawAtIso) {
  if (/^TN/i.test(productCode)) return "thinhnam";
  if (!/^VN/i.test(productCode)) return null;
  const hourPP = (new Date(drawAtIso).getUTCHours() + 7) % 24;
  return hourPP >= 18 ? "xsmb" : "xsmn";
}
function resultParts(full) {
  const m = /^(\d{2})-(\d{3})$/.exec(full);
  if (m) return { d2: m[1], d3: m[2] };
  const digits = full.replace(/\D/g, "");
  return { d2: digits.slice(-2), d3: digits.length >= 3 ? digits.slice(-3) : null };
}

// src/engine/vn-rules.ts
var pad2 = (x, n) => {
  const digits = String(x ?? "").replace(/\D/g, "");
  const v = digits.replace(/^0+(?=\d)/, "");
  return v.length >= n ? v : v.padStart(n, "0");
};
var typed = (x) => String(x ?? "").replace(/\D/g, "").replace(/^0+(?=\d)/, "");
var right = (s, n) => s.slice(-n);
var left = (s, n) => s.slice(0, n);
function bcd(special) {
  const g = pad2(right(typed(special), 5), 5);
  return {
    B: { d2: left(g, 2), d3: right(g, 3) },
    C: { d2: right(g, 2), d3: left(g, 3) },
    D: { d2: left(g, 1) + right(g, 1), d3: g.slice(1, 4) }
  };
}
var VN430_PRIZES = 18;
var VN630_PRIZES = 27;
function check(list, n, what) {
  if (!list || list.length !== n) throw new Error(`${what}: expected ${n} numbers, got ${list?.length ?? 0}`);
  list.forEach((x, i) => {
    if (!/^\d{1,6}$/.test(String(x))) throw new Error(`${what}: number ${i + 1} (${x}) is not a number`);
  });
  return list;
}
var composite = (p) => p.d3 === null ? p.d2 : `${p.d2}-${p.d3}`;
function vn430(provinces, weekday, cfg) {
  const notes = [];
  const P1 = check(provinces[0], VN430_PRIZES, "province 1");
  const g8 = (P) => pad2(P[0], 2);
  const g7 = (P) => pad2(P[1], 3);
  const A = { d2: g8(P1), d3: g7(P1) };
  const { B, C, D } = bcd(P1[17]);
  const posts = { A, B, C, D };
  const map = cfg.weekdayMap[String(weekday)] ?? { px: 2, py: 2 };
  const prov = (i, role) => {
    const list = provinces[i - 1];
    if (!list || !list.length) {
      notes.push(`${role} not filled: province ${i} missing`);
      return null;
    }
    return check(list, VN430_PRIZES, `province ${i}`);
  };
  const Px = prov(map.px, "F, N, K, O");
  const Py = map.py === map.px ? Px : prov(map.py, "I");
  if (Px) {
    posts.F = { d2: g8(Px), d3: g7(Px) };
    posts.N = bcd(Px[17]).B;
    const six = cfg.kUnpadded ? typed(Px[2]) : pad2(Px[2], 4);
    posts.K = { d2: right(six, 2), d3: right(six, 3) };
    posts.O = { d2: pad2(right(typed(Px[1]), 2), 2), d3: g7(Px) };
  }
  if (Py) posts.I = { d2: g8(Py), d3: g7(Py) };
  const L = P1.slice(2, 17).map((x, i) => pad2(x, i < 4 ? 4 : 5));
  const Ac = composite(A);
  const lo2 = [left(Ac, 2), right(Ac, 2), ...L.map((x) => right(x, 2)), B.d2, right(B.d3, 2), C.d2, right(C.d3, 2), D.d2, right(D.d3, 2)];
  const lo3 = [right(Ac, 3), ...L.map((x) => right(x, 3)), B.d3, C.d3, D.d3];
  return { posts, lo2, lo3, notes };
}
function vn630(prizes) {
  const S = check(prizes, VN630_PRIZES, "northern draw");
  const at = (k) => S[k - 1];
  const A1 = { d2: pad2(at(24), 2), d3: pad2(at(21), 3) };
  const A2 = { d2: pad2(at(25), 2), d3: pad2(at(22), 3) };
  const A3 = { d2: pad2(at(26), 2), d3: pad2(at(23), 3) };
  const A4 = { d2: pad2(at(27), 2), d3: null };
  const { B, C, D } = bcd(at(1));
  const M = [...[15, 16, 17, 18, 19, 20, 11, 12, 13, 14].map((k) => pad2(at(k), 4)), ...[5, 6, 7, 8, 9, 10, 3, 4, 2].map((k) => pad2(at(k), 5))];
  const A123 = [A1, A2, A3].map(composite);
  const lo2 = [A1.d2, A2.d2, A3.d2, A4.d2, ...A123.map((x) => right(x, 2)), ...M.map((x) => right(x, 2)), B.d2, C.d2, D.d2, right(composite(B), 2), right(composite(C), 2), right(composite(D), 2)];
  const lo3 = [...A123.map((x) => right(x, 3)), ...M.map((x) => right(x, 3)), B.d3, C.d3, D.d3];
  return { posts: { A1, A2, A3, A4, B, C, D }, lo2, lo3, notes: [] };
}
function engineEntries(engine, r) {
  const out = [];
  if (engine === "vn630") out.push({ postCode: "A", numbers: ["A1", "A2", "A3", "A4"].filter((k) => r.posts[k]).map((k) => composite(r.posts[k])) });
  for (const [code, p] of Object.entries(r.posts)) out.push({ postCode: code, numbers: [composite(p)] });
  out.push({ postCode: "Lo", digitLen: 2, numbers: r.lo2 }, { postCode: "Lo", digitLen: 3, numbers: r.lo3 });
  return out;
}
function deriveByRule(cfg, provinces, weekday) {
  if (cfg.engine === "tn") throw new Error("Thinh Nam results use the Thinh Nam helper (15 numbers per province)");
  const r = cfg.engine === "vn630" ? vn630(provinces[0] ?? []) : vn430(provinces, weekday, cfg);
  return { ...r, entries: engineEntries(cfg.engine, r) };
}
function ruleMultiplier(cfg, postCode2, digitLen2) {
  return betTypeMultiplier(cfg, postCode2, digitLen2);
}

// src/rules/dsl.ts
import { z as z2 } from "zod";
var RULE_DSL_VERSION = 1;
var WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
var name = z2.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,31}$/, "letters, digits and _ (max 32, starting with a letter)");
var small = (min, max) => z2.number().int().min(min).max(max);
var inputSlotSchema = z2.strictObject({
  key: name,
  label: z2.string().trim().max(60).optional(),
  digits: small(1, 12),
  count: small(1, 100)
});
var inputSlotsSchema = z2.array(inputSlotSchema).max(60).refine((slots) => new Set(slots.map((s) => s.key)).size === slots.length, "slot keys must be unique");
var RULE_EXPR_OPS = ["slot", "item", "ref", "set", "const", "pad", "unpadded", "left", "right", "mid", "concat", "weekday", "each", "flatten", "take", "skip"];
var ruleExprSchema = z2.lazy(
  () => z2.discriminatedUnion("op", [
    z2.strictObject({ op: z2.literal("slot"), role: name, key: name, index: small(0, 99).optional() }),
    z2.strictObject({ op: z2.literal("item") }),
    z2.strictObject({ op: z2.literal("ref"), name, index: small(0, 999).optional() }),
    z2.strictObject({ op: z2.literal("set"), name }),
    z2.strictObject({ op: z2.literal("const"), value: z2.string().max(32) }),
    z2.strictObject({ op: z2.literal("pad"), expr: ruleExprSchema, width: small(1, 12) }),
    z2.strictObject({ op: z2.literal("unpadded"), expr: ruleExprSchema }),
    z2.strictObject({ op: z2.literal("left"), expr: ruleExprSchema, n: small(1, 12) }),
    z2.strictObject({ op: z2.literal("right"), expr: ruleExprSchema, n: small(1, 12) }),
    z2.strictObject({ op: z2.literal("mid"), expr: ruleExprSchema, start: small(1, 12), n: small(1, 12) }),
    z2.strictObject({ op: z2.literal("concat"), parts: z2.array(ruleExprSchema).min(1).max(12), sep: z2.string().max(3).optional() }),
    z2.strictObject({ op: z2.literal("weekday"), cases: z2.strictObject(Object.fromEntries(WEEKDAY_KEYS.map((k) => [k, ruleExprSchema.optional()]))), default: ruleExprSchema.optional() }),
    z2.strictObject({ op: z2.literal("each"), role: name, key: name, from: small(0, 99).optional(), to: small(0, 99).optional(), expr: ruleExprSchema }),
    z2.strictObject({ op: z2.literal("flatten"), items: z2.array(ruleExprSchema).min(1).max(100) }),
    z2.strictObject({ op: z2.literal("take"), expr: ruleExprSchema, n: small(0, 1e3) }),
    z2.strictObject({ op: z2.literal("skip"), expr: ruleExprSchema, n: small(0, 1e3) })
  ])
);
var ruleInputSchema = z2.strictObject({
  role: name,
  /** used in messages and notes (`{label}`), e.g. "province 2" */
  label: z2.string().trim().min(1).max(60),
  slots: inputSlotsSchema.refine((s) => s.length > 0, "a role needs at least one slot"),
  /**
   * accepted value length: 'slot' = at most the slot's digits (longer is an error), a number = at most that many digits whatever the slot.
   * Values may be shorter (a number typed without its leading zeros); `pad` restores them.
   */
  maxDigits: z2.union([z2.literal("slot"), small(1, 12)]).default("slot")
});
var ruleAliasSchema = z2.strictObject({
  role: name,
  alias: z2.strictObject({ cases: z2.strictObject(Object.fromEntries(WEEKDAY_KEYS.map((k) => [k, name.optional()]))), default: name.optional() })
});
var ruleDerivationSchema = z2.strictObject({
  name,
  /** the set = every item's value(s) in order (lists are flattened) */
  items: z2.array(ruleExprSchema).min(1).max(200),
  /** helper set: usable by `ref` / `set`, not part of the output */
  internal: z2.boolean().optional(),
  /** a role without a sheet: 'error' (default) fails the evaluation, 'omit' leaves the set out and adds `note` once per role */
  onMissing: z2.enum(["error", "omit"]).optional(),
  /** note template for an omitted set: `{label}` / `{role}` = the missing role */
  note: z2.string().max(200).optional()
});
var ruleMatchSchema = z2.strictObject({
  set: name,
  /** the post whose result rows the set becomes */
  post: z2.string().trim().min(1).max(4),
  /**
   * which digit lengths a value counts for: [2, 3] = both (a composite `2D-3D` value, or the last 2 / 3 digits of a whole number);
   * [2] / [3] = that length only (the VN Lo 2D / Lo 3D lists)
   */
  lastDigits: z2.array(z2.union([z2.literal(2), z2.literal(3)])).min(1).max(2).refine((d) => new Set(d).size === d.length, "digit lengths must be unique").default([2, 3]),
  /** a value present several times pays once (informational in 2.3: settlement still reads the rule's loDuplicatePayOnce until 2.9) */
  duplicatePayOnce: z2.boolean().default(false)
});
var ruleDslSchema = z2.strictObject({
  dslVersion: z2.literal(RULE_DSL_VERSION),
  /** identifier of the configuration, e.g. 'vn430' */
  key: z2.string().trim().max(32).optional(),
  /** 'builtin' = a ported engine whose parameters follow the rule version's fields (weekdayMap, kUnpadded, loDuplicatePayOnce); 'custom' = edited */
  origin: z2.enum(["builtin", "custom"]).default("custom"),
  description: z2.string().max(400).optional(),
  inputs: z2.array(z2.union([ruleInputSchema, ruleAliasSchema])).min(1).max(12),
  derivations: z2.array(ruleDerivationSchema).min(1).max(100),
  matching: z2.array(ruleMatchSchema).max(100),
  /** notes always appended to an evaluation (e.g. "3P has no printed source: enter it by hand") */
  notes: z2.array(z2.string().max(200)).max(20).optional()
});
var isAliasInput = (i) => "alias" in i;

// src/rules/evaluate.ts
var RuleDslError = class extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.code = code;
    this.details = details;
    this.name = "RuleDslError";
  }
  code;
  details;
};
var MAX_SET_VALUES = 1e3;
var MAX_DEPTH = 40;
function dslPad(x, width) {
  const v = dslUnpadded(x);
  return v.length >= width ? v : v.padStart(width, "0");
}
function dslUnpadded(x) {
  return String(x ?? "").replace(/\D/g, "").replace(/^0+(?=\d)/, "");
}
var right2 = (s, n) => s.slice(-n);
var left2 = (s, n) => s.slice(0, n);
function roleShapes(dsl) {
  const real = /* @__PURE__ */ new Map();
  const alias = /* @__PURE__ */ new Map();
  const issues = [];
  dsl.inputs.forEach((i, n) => {
    if (real.has(i.role) || alias.has(i.role)) issues.push({ code: "DUPLICATE_NAME", message: `role ${i.role} is defined twice`, path: `inputs[${n}]` });
    if (isAliasInput(i)) alias.set(i.role, i);
    else real.set(i.role, i);
  });
  dsl.inputs.forEach((i, n) => {
    if (!isAliasInput(i)) return;
    const targets = [...Object.values(i.alias.cases), i.alias.default].filter((x) => !!x);
    if (!targets.length) issues.push({ code: "UNKNOWN_ROLE", message: `alias ${i.role} names no role`, path: `inputs[${n}].alias` });
    for (const t of targets) if (!real.has(t)) issues.push({ code: "UNKNOWN_ROLE", message: `alias ${i.role} names ${t}, which is not a role with slots`, path: `inputs[${n}].alias` });
  });
  return { real, alias, issues };
}
function shapeOf(role, real, alias) {
  const r = real.get(role);
  if (r) return { role, slots: new Map(r.slots.map((s) => [s.key, { digits: s.digits, count: s.count }])) };
  const a = alias.get(role);
  if (!a) return null;
  const targets = [...new Set([...Object.values(a.alias.cases), a.alias.default].filter((x) => !!x))].map((t) => real.get(t)).filter((x) => !!x);
  if (!targets.length) return null;
  const slots = /* @__PURE__ */ new Map();
  for (const s of targets[0].slots) {
    if (targets.every((t) => t.slots.some((o) => o.key === s.key && o.count === s.count))) slots.set(s.key, { digits: s.digits, count: s.count });
  }
  return { role, slots };
}
function validateRuleDsl(dsl) {
  const { real, alias, issues } = roleShapes(dsl);
  const seen = /* @__PURE__ */ new Map();
  const typeOf = (e, path, inEach, depth) => {
    if (depth > MAX_DEPTH) {
      issues.push({ code: "LIMIT", message: `expressions nest deeper than ${MAX_DEPTH}`, path });
      return null;
    }
    const str = (child, p) => {
      const t = typeOf(child, p, inEach, depth + 1);
      if (t === "list") issues.push({ code: "TYPE_MISMATCH", message: `${e.op} needs a single value, got a list`, path: p });
    };
    const slotCheck = (role, key, p, idx) => {
      const shape = shapeOf(role, real, alias);
      if (!shape) return void issues.push({ code: "UNKNOWN_ROLE", message: `unknown role ${role}`, path: p });
      const slot = shape.slots.get(key);
      if (!slot) return void issues.push({ code: "UNKNOWN_SLOT", message: `role ${role} has no slot ${key}`, path: p });
      for (const i of idx) if (i >= slot.count) issues.push({ code: "INDEX_OUT_OF_RANGE", message: `${role}.${key} has ${slot.count} value(s); index ${i} is outside`, path: p });
    };
    switch (e.op) {
      case "slot":
        slotCheck(e.role, e.key, path, [e.index ?? 0]);
        return "string";
      case "item":
        if (!inEach) issues.push({ code: "ITEM_OUTSIDE_EACH", message: "item is only defined inside each", path });
        return "string";
      case "ref":
      case "set":
        if (!seen.has(e.name)) issues.push({ code: "UNKNOWN_SET", message: `${e.op} ${e.name}: no earlier derivation has that name`, path });
        return e.op === "ref" ? "string" : "list";
      case "const":
        return "string";
      case "pad":
      case "unpadded":
      case "left":
      case "right":
      case "mid":
        str(e.expr, `${path}.expr`);
        return "string";
      case "concat":
        e.parts.forEach((p, i) => str(p, `${path}.parts[${i}]`));
        return "string";
      case "weekday": {
        const branches = [...WEEKDAY_KEYS.map((k) => [k, e.cases[k]]), ["default", e.default]].filter(([, x]) => !!x);
        if (!branches.length) issues.push({ code: "INVALID_CONFIG", message: "weekday has no case", path });
        const types = new Set(branches.map(([k, x]) => typeOf(x, `${path}.${k === "default" ? "default" : `cases.${k}`}`, inEach, depth + 1)).filter((t) => !!t));
        if (types.size > 1) issues.push({ code: "TYPE_MISMATCH", message: "weekday cases mix single values and lists", path });
        return [...types][0] ?? "string";
      }
      case "each": {
        const from = e.from ?? 0;
        const shape = shapeOf(e.role, real, alias);
        const count = shape?.slots.get(e.key)?.count;
        const to = e.to ?? (count ?? 1) - 1;
        slotCheck(e.role, e.key, path, [from, to]);
        if (from > to) issues.push({ code: "INDEX_OUT_OF_RANGE", message: `each ${e.role}.${e.key}: from ${from} is after to ${to}`, path });
        const t = typeOf(e.expr, `${path}.expr`, true, depth + 1);
        if (t === "list") issues.push({ code: "TYPE_MISMATCH", message: "each needs a single value per item, got a list", path: `${path}.expr` });
        return "list";
      }
      case "flatten":
        e.items.forEach((x, i) => typeOf(x, `${path}.items[${i}]`, inEach, depth + 1));
        return "list";
      case "take":
      case "skip": {
        const t = typeOf(e.expr, `${path}.expr`, inEach, depth + 1);
        if (t === "string") issues.push({ code: "TYPE_MISMATCH", message: `${e.op} needs a list, got a single value`, path: `${path}.expr` });
        return "list";
      }
    }
  };
  dsl.derivations.forEach((d, n) => {
    if (seen.has(d.name) || real.has(d.name) || alias.has(d.name)) issues.push({ code: "DUPLICATE_NAME", message: `name ${d.name} is used twice`, path: `derivations[${n}]` });
    d.items.forEach((x, i) => typeOf(x, `derivations[${n}].items[${i}]`, false, 0));
    seen.set(d.name, { internal: !!d.internal });
  });
  dsl.matching.forEach((m, n) => {
    const s = seen.get(m.set);
    if (!s) issues.push({ code: "UNKNOWN_SET", message: `matching names set ${m.set}, which is not derived`, path: `matching[${n}]` });
    else if (s.internal) issues.push({ code: "UNKNOWN_SET", message: `matching names internal set ${m.set}`, path: `matching[${n}]` });
  });
  return issues;
}
function parseRuleDsl(json) {
  const parsed = ruleDslSchema.safeParse(json);
  if (!parsed.success) {
    const issues2 = parsed.error.issues.map((i) => ({ code: "INVALID_CONFIG", message: i.message, path: i.path.map((p) => typeof p === "number" ? `[${p}]` : `.${String(p)}`).join("").replace(/^\./, "") }));
    throw new RuleDslError("INVALID_CONFIG", `invalid rule config: ${issues2.map((i) => `${i.path ? `${i.path}: ` : ""}${i.message}`).join("; ")}`, { issues: issues2 });
  }
  const issues = validateRuleDsl(parsed.data);
  if (issues.length) throw new RuleDslError("INVALID_CONFIG", `invalid rule config: ${issues.map((i) => `${i.path ? `${i.path}: ` : ""}${i.message}`).join("; ")}`, { issues });
  return parsed.data;
}
var MissingRole = class {
  constructor(role, label, total) {
    this.role = role;
    this.label = label;
    this.total = total;
  }
  role;
  label;
  total;
};
function evaluateRuleDsl(dsl, input, opts = {}) {
  const { real, alias } = roleShapes(dsl);
  const weekday = input.weekday ?? null;
  const loaded = /* @__PURE__ */ new Map();
  const aliases = {};
  const values = /* @__PURE__ */ new Map();
  const omittedBy = /* @__PURE__ */ new Map();
  const notes = [];
  const noted = /* @__PURE__ */ new Set();
  const omitted = [];
  const trace = [];
  let rolesRead = /* @__PURE__ */ new Set();
  const resolveRole = (role) => {
    if (real.has(role)) return role;
    const a = alias.get(role);
    if (!a) throw new RuleDslError("UNKNOWN_ROLE", `unknown role ${role}`, { role });
    let target;
    if (weekday == null || !Number.isInteger(weekday) || weekday < 0 || weekday > 6) {
      if (!a.alias.default) throw new RuleDslError("NO_WEEKDAY", `role ${role} depends on the weekday, and no weekday was given`, { role });
      target = a.alias.default;
    } else target = a.alias.cases[WEEKDAY_KEYS[weekday]] ?? a.alias.default;
    if (!target) throw new RuleDslError("UNKNOWN_ROLE", `role ${role} has no role for ${WEEKDAY_KEYS[weekday]}`, { role });
    aliases[role] = target;
    return target;
  };
  const checkValue = (def, x, digits, where) => {
    const s = typeof x === "string" ? x : typeof x === "number" ? String(x) : null;
    if (opts.exactDigits) {
      if (s === null || !/^\d+$/.test(s) || s.length !== digits) throw new RuleDslError("BAD_DIGITS", `${def.label}: ${where} (${String(x)}) must be ${digits} digits`, { role: def.role });
      return s;
    }
    if (def.maxDigits === "slot") {
      if (s === null || !/^\d+$/.test(s)) throw new RuleDslError("NOT_A_NUMBER", `${def.label}: ${where} (${String(x)}) is not a number`, { role: def.role });
      if (s.length > digits) throw new RuleDslError("TOO_LONG", `${def.label}: ${where} (${s}) is longer than ${digits} digits`, { role: def.role });
      return s;
    }
    if (s === null || !new RegExp(`^\\d{1,${def.maxDigits}}$`).test(s)) throw new RuleDslError("NOT_A_NUMBER", `${def.label}: ${where} (${String(x)}) is not a number`, { role: def.role });
    return s;
  };
  const load = (role) => {
    rolesRead.add(role);
    const hit = loaded.get(role);
    if (hit instanceof MissingRole) throw hit;
    if (hit) return hit;
    const def = real.get(role);
    const total = def.slots.reduce((n, s) => n + s.count, 0);
    const raw = input.roles[role];
    const empty = raw == null || (Array.isArray(raw) ? raw.length === 0 : Object.values(raw).every((v) => !v || v.length === 0));
    if (empty) {
      const m = new MissingRole(role, def.label, total);
      loaded.set(role, m);
      throw m;
    }
    const out = /* @__PURE__ */ new Map();
    if (Array.isArray(raw)) {
      if (raw.length !== total) throw new RuleDslError("WRONG_COUNT", `${def.label}: expected ${total} numbers, got ${raw.length}`, { role });
      let i = 0;
      for (const s of def.slots) {
        const list = [];
        for (let j = 0; j < s.count; j++, i++) list.push(checkValue(def, raw[i], s.digits, `number ${i + 1}`));
        out.set(s.key, list);
      }
    } else {
      for (const s of def.slots) {
        const list = raw[s.key];
        if (!Array.isArray(list)) throw new RuleDslError("MISSING_SLOT", `${def.label}: ${s.label ?? s.key} is missing`, { role });
        if (list.length !== s.count) throw new RuleDslError("WRONG_COUNT", `${def.label}: ${s.label ?? s.key} expects ${s.count} number(s), got ${list.length}`, { role });
        out.set(
          s.key,
          list.map((x, j) => checkValue(def, x, s.digits, s.count > 1 ? `${s.label ?? s.key} #${j + 1}` : s.label ?? s.key))
        );
      }
    }
    loaded.set(role, out);
    return out;
  };
  const slotValues = (role, key) => {
    const realRole = resolveRole(role);
    const list = load(realRole).get(key);
    if (!list) throw new RuleDslError("UNKNOWN_SLOT", `role ${role} has no slot ${key}`, { role });
    return list;
  };
  const evalExpr = (e, item, depth) => {
    if (depth > MAX_DEPTH) throw new RuleDslError("LIMIT", `expressions nest deeper than ${MAX_DEPTH}`);
    const s = (x) => {
      const v = evalExpr(x, item, depth + 1);
      if (Array.isArray(v)) throw new RuleDslError("TYPE_MISMATCH", `${e.op} needs a single value, got a list`);
      return v;
    };
    const l = (x) => {
      const v = evalExpr(x, item, depth + 1);
      if (!Array.isArray(v)) throw new RuleDslError("TYPE_MISMATCH", `${e.op} needs a list, got a single value`);
      return v;
    };
    switch (e.op) {
      case "slot": {
        const list = slotValues(e.role, e.key);
        const v = list[e.index ?? 0];
        if (v === void 0) throw new RuleDslError("INDEX_OUT_OF_RANGE", `${e.role}.${e.key} has no value #${(e.index ?? 0) + 1}`, { role: e.role });
        return v;
      }
      case "item":
        if (item === void 0) throw new RuleDslError("ITEM_OUTSIDE_EACH", "item is only defined inside each");
        return item;
      case "ref":
      case "set": {
        const missing = omittedBy.get(e.name);
        if (missing) throw missing;
        const list = values.get(e.name);
        if (!list) throw new RuleDslError("UNKNOWN_SET", `${e.op} ${e.name}: not derived yet`);
        if (e.op === "set") return list;
        const v = list[e.index ?? 0];
        if (v === void 0) throw new RuleDslError("INDEX_OUT_OF_RANGE", `${e.name} has no value #${(e.index ?? 0) + 1}`);
        return v;
      }
      case "const":
        return e.value;
      case "pad":
        return dslPad(s(e.expr), e.width);
      case "unpadded":
        return dslUnpadded(s(e.expr));
      case "left":
        return left2(s(e.expr), e.n);
      case "right":
        return right2(s(e.expr), e.n);
      case "mid":
        return s(e.expr).slice(e.start - 1, e.start - 1 + e.n);
      case "concat":
        return e.parts.map(s).join(e.sep ?? "");
      case "weekday": {
        const branch = weekday != null && weekday >= 0 && weekday <= 6 ? e.cases[WEEKDAY_KEYS[weekday]] ?? e.default : e.default;
        if (!branch) throw new RuleDslError(weekday == null ? "NO_WEEKDAY" : "INVALID_CONFIG", weekday == null ? "weekday: no weekday was given" : `weekday: no case for ${WEEKDAY_KEYS[weekday]}`);
        return evalExpr(branch, item, depth + 1);
      }
      case "each": {
        const list = slotValues(e.role, e.key);
        const from = e.from ?? 0;
        const to = e.to ?? list.length - 1;
        if (from > to || to >= list.length) throw new RuleDslError("INDEX_OUT_OF_RANGE", `each ${e.role}.${e.key}: range ${from}..${to} is outside ${list.length} value(s)`, { role: e.role });
        return list.slice(from, to + 1).map((x) => {
          const v = evalExpr(e.expr, x, depth + 1);
          if (Array.isArray(v)) throw new RuleDslError("TYPE_MISMATCH", "each needs a single value per item, got a list");
          return v;
        });
      }
      case "flatten":
        return e.items.flatMap((x) => evalExpr(x, item, depth + 1));
      case "take":
        return l(e.expr).slice(0, e.n);
      case "skip":
        return l(e.expr).slice(e.n);
    }
  };
  const sets = {};
  for (const d of dsl.derivations) {
    rolesRead = /* @__PURE__ */ new Set();
    try {
      const out = [];
      for (const x of d.items) {
        const v = evalExpr(x, void 0, 0);
        if (Array.isArray(v)) out.push(...v);
        else out.push(v);
        if (out.length > MAX_SET_VALUES) throw new RuleDslError("LIMIT", `set ${d.name} has more than ${MAX_SET_VALUES} values`);
      }
      values.set(d.name, out);
      if (!d.internal) sets[d.name] = out;
      if (opts.trace) trace.push({ set: d.name, internal: !!d.internal, status: "ok", values: out, roles: [...rolesRead] });
    } catch (err) {
      if (!(err instanceof MissingRole)) throw err;
      if (d.onMissing !== "omit") throw new RuleDslError("MISSING_ROLE", `${err.label}: expected ${err.total} numbers, got 0`, { role: err.role });
      omittedBy.set(d.name, err);
      if (!d.internal) omitted.push({ set: d.name, role: err.role });
      if (d.note && !noted.has(err.role)) {
        noted.add(err.role);
        notes.push(d.note.replaceAll("{label}", err.label).replaceAll("{role}", err.role));
      }
      if (opts.trace) trace.push({ set: d.name, internal: !!d.internal, status: "omitted", values: [], roles: [...rolesRead], missingRole: err.role });
    }
  }
  notes.push(...dsl.notes ?? []);
  return { sets, notes, omitted, aliases, ...opts.trace ? { trace } : {} };
}
function dslEntries(dsl, sets) {
  const out = [];
  for (const m of dsl.matching) {
    const numbers = sets[m.set];
    if (!numbers || numbers.length === 0) continue;
    out.push(m.lastDigits.length === 1 ? { postCode: m.post, digitLen: m.lastDigits[0], numbers: [...numbers] } : { postCode: m.post, numbers: [...numbers] });
  }
  return out;
}
function dslRoles(dsl) {
  return dsl.inputs.filter((i) => !isAliasInput(i));
}

// src/rules/builtin/vn430.json
var vn430_default = {
  dslVersion: 1,
  key: "vn430",
  origin: "builtin",
  description: 'VN 4:30 PM (Southern lottery XSMN, up to 4 provinces) \u2014 port of the vn430 engine, Game Rule VN.xlsx sheet "VN 4.30PM" (docs/13).',
  inputs: [
    {
      role: "P1",
      label: "province 1",
      slots: [
        {
          key: "prize8",
          label: "8th prize",
          digits: 2,
          count: 1
        },
        {
          key: "prize7",
          label: "7th prize",
          digits: 3,
          count: 1
        },
        {
          key: "prize6",
          label: "6th prize",
          digits: 4,
          count: 3
        },
        {
          key: "prize5",
          label: "5th prize",
          digits: 4,
          count: 1
        },
        {
          key: "prize4",
          label: "4th prize",
          digits: 5,
          count: 7
        },
        {
          key: "prize3",
          label: "3rd prize",
          digits: 5,
          count: 2
        },
        {
          key: "prize2",
          label: "2nd prize",
          digits: 5,
          count: 1
        },
        {
          key: "prize1",
          label: "1st prize",
          digits: 5,
          count: 1
        },
        {
          key: "special",
          label: "Special prize",
          digits: 6,
          count: 1
        }
      ],
      maxDigits: 6
    },
    {
      role: "P2",
      label: "province 2",
      slots: [
        {
          key: "prize8",
          label: "8th prize",
          digits: 2,
          count: 1
        },
        {
          key: "prize7",
          label: "7th prize",
          digits: 3,
          count: 1
        },
        {
          key: "prize6",
          label: "6th prize",
          digits: 4,
          count: 3
        },
        {
          key: "prize5",
          label: "5th prize",
          digits: 4,
          count: 1
        },
        {
          key: "prize4",
          label: "4th prize",
          digits: 5,
          count: 7
        },
        {
          key: "prize3",
          label: "3rd prize",
          digits: 5,
          count: 2
        },
        {
          key: "prize2",
          label: "2nd prize",
          digits: 5,
          count: 1
        },
        {
          key: "prize1",
          label: "1st prize",
          digits: 5,
          count: 1
        },
        {
          key: "special",
          label: "Special prize",
          digits: 6,
          count: 1
        }
      ],
      maxDigits: 6
    },
    {
      role: "P3",
      label: "province 3",
      slots: [
        {
          key: "prize8",
          label: "8th prize",
          digits: 2,
          count: 1
        },
        {
          key: "prize7",
          label: "7th prize",
          digits: 3,
          count: 1
        },
        {
          key: "prize6",
          label: "6th prize",
          digits: 4,
          count: 3
        },
        {
          key: "prize5",
          label: "5th prize",
          digits: 4,
          count: 1
        },
        {
          key: "prize4",
          label: "4th prize",
          digits: 5,
          count: 7
        },
        {
          key: "prize3",
          label: "3rd prize",
          digits: 5,
          count: 2
        },
        {
          key: "prize2",
          label: "2nd prize",
          digits: 5,
          count: 1
        },
        {
          key: "prize1",
          label: "1st prize",
          digits: 5,
          count: 1
        },
        {
          key: "special",
          label: "Special prize",
          digits: 6,
          count: 1
        }
      ],
      maxDigits: 6
    },
    {
      role: "P4",
      label: "province 4",
      slots: [
        {
          key: "prize8",
          label: "8th prize",
          digits: 2,
          count: 1
        },
        {
          key: "prize7",
          label: "7th prize",
          digits: 3,
          count: 1
        },
        {
          key: "prize6",
          label: "6th prize",
          digits: 4,
          count: 3
        },
        {
          key: "prize5",
          label: "5th prize",
          digits: 4,
          count: 1
        },
        {
          key: "prize4",
          label: "4th prize",
          digits: 5,
          count: 7
        },
        {
          key: "prize3",
          label: "3rd prize",
          digits: 5,
          count: 2
        },
        {
          key: "prize2",
          label: "2nd prize",
          digits: 5,
          count: 1
        },
        {
          key: "prize1",
          label: "1st prize",
          digits: 5,
          count: 1
        },
        {
          key: "special",
          label: "Special prize",
          digits: 6,
          count: 1
        }
      ],
      maxDigits: 6
    },
    {
      role: "PX",
      alias: {
        cases: {
          sun: "P2",
          mon: "P2",
          tue: "P2",
          wed: "P3",
          thu: "P3",
          fri: "P1",
          sat: "P2"
        },
        default: "P2"
      }
    },
    {
      role: "PY",
      alias: {
        cases: {
          sun: "P2",
          mon: "P2",
          tue: "P3",
          wed: "P2",
          thu: "P2",
          fri: "P1",
          sat: "P2"
        },
        default: "P2"
      }
    }
  ],
  derivations: [
    {
      name: "g1",
      items: [
        {
          op: "pad",
          expr: {
            op: "right",
            expr: {
              op: "unpadded",
              expr: {
                op: "slot",
                role: "P1",
                key: "special"
              }
            },
            n: 5
          },
          width: 5
        }
      ],
      internal: true
    },
    {
      name: "A",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "P1",
                key: "prize8"
              },
              width: 2
            },
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "P1",
                key: "prize7"
              },
              width: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "B",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "left",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 2
            },
            {
              op: "right",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "C",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "right",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 2
            },
            {
              op: "left",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "D",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "concat",
              parts: [
                {
                  op: "left",
                  expr: {
                    op: "ref",
                    name: "g1"
                  },
                  n: 1
                },
                {
                  op: "right",
                  expr: {
                    op: "ref",
                    name: "g1"
                  },
                  n: 1
                }
              ]
            },
            {
              op: "mid",
              expr: {
                op: "ref",
                name: "g1"
              },
              start: 2,
              n: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "gx",
      items: [
        {
          op: "pad",
          expr: {
            op: "right",
            expr: {
              op: "unpadded",
              expr: {
                op: "slot",
                role: "PX",
                key: "special"
              }
            },
            n: 5
          },
          width: 5
        }
      ],
      internal: true,
      onMissing: "omit",
      note: "F, N, K, O not filled: {label} missing"
    },
    {
      name: "F",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "PX",
                key: "prize8"
              },
              width: 2
            },
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "PX",
                key: "prize7"
              },
              width: 3
            }
          ],
          sep: "-"
        }
      ],
      onMissing: "omit",
      note: "F, N, K, O not filled: {label} missing"
    },
    {
      name: "N",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "left",
              expr: {
                op: "ref",
                name: "gx"
              },
              n: 2
            },
            {
              op: "right",
              expr: {
                op: "ref",
                name: "gx"
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ],
      onMissing: "omit",
      note: "F, N, K, O not filled: {label} missing"
    },
    {
      name: "K",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "right",
              expr: {
                op: "unpadded",
                expr: {
                  op: "slot",
                  role: "PX",
                  key: "prize6"
                }
              },
              n: 2
            },
            {
              op: "right",
              expr: {
                op: "unpadded",
                expr: {
                  op: "slot",
                  role: "PX",
                  key: "prize6"
                }
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ],
      onMissing: "omit",
      note: "F, N, K, O not filled: {label} missing"
    },
    {
      name: "O",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "pad",
              expr: {
                op: "right",
                expr: {
                  op: "unpadded",
                  expr: {
                    op: "slot",
                    role: "PX",
                    key: "prize7"
                  }
                },
                n: 2
              },
              width: 2
            },
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "PX",
                key: "prize7"
              },
              width: 3
            }
          ],
          sep: "-"
        }
      ],
      onMissing: "omit",
      note: "F, N, K, O not filled: {label} missing"
    },
    {
      name: "I",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "PY",
                key: "prize8"
              },
              width: 2
            },
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "PY",
                key: "prize7"
              },
              width: 3
            }
          ],
          sep: "-"
        }
      ],
      onMissing: "omit",
      note: "I not filled: {label} missing"
    },
    {
      name: "Lo2",
      items: [
        {
          op: "left",
          expr: {
            op: "ref",
            name: "A"
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "A"
          },
          n: 2
        },
        {
          op: "each",
          role: "P1",
          key: "prize6",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 4
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize5",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 4
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize4",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize3",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize2",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize1",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "left",
          expr: {
            op: "ref",
            name: "g1"
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "right",
            expr: {
              op: "ref",
              name: "g1"
            },
            n: 3
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "g1"
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "left",
            expr: {
              op: "ref",
              name: "g1"
            },
            n: 3
          },
          n: 2
        },
        {
          op: "concat",
          parts: [
            {
              op: "left",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 1
            },
            {
              op: "right",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 1
            }
          ]
        },
        {
          op: "right",
          expr: {
            op: "mid",
            expr: {
              op: "ref",
              name: "g1"
            },
            start: 2,
            n: 3
          },
          n: 2
        }
      ]
    },
    {
      name: "Lo3",
      items: [
        {
          op: "right",
          expr: {
            op: "ref",
            name: "A"
          },
          n: 3
        },
        {
          op: "each",
          role: "P1",
          key: "prize6",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 4
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize5",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 4
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize4",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize3",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize2",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize1",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "g1"
          },
          n: 3
        },
        {
          op: "left",
          expr: {
            op: "ref",
            name: "g1"
          },
          n: 3
        },
        {
          op: "mid",
          expr: {
            op: "ref",
            name: "g1"
          },
          start: 2,
          n: 3
        }
      ]
    }
  ],
  matching: [
    {
      set: "A",
      post: "A",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "B",
      post: "B",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "C",
      post: "C",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "D",
      post: "D",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "F",
      post: "F",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "N",
      post: "N",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "K",
      post: "K",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "O",
      post: "O",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "I",
      post: "I",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "Lo2",
      post: "Lo",
      lastDigits: [
        2
      ],
      duplicatePayOnce: false
    },
    {
      set: "Lo3",
      post: "Lo",
      lastDigits: [
        3
      ],
      duplicatePayOnce: false
    }
  ]
};

// src/rules/builtin/vn630.json
var vn630_default = {
  dslVersion: 1,
  key: "vn630",
  origin: "builtin",
  description: 'VN 6:30 PM (Northern lottery XSMB, 27 numbers) \u2014 port of the vn630 engine, Game Rule VN.xlsx sheet "VN 6.30PM" (docs/13).',
  inputs: [
    {
      role: "S",
      label: "northern draw",
      slots: [
        {
          key: "special",
          label: "Special prize (\u0110B)",
          digits: 5,
          count: 1
        },
        {
          key: "prize1",
          label: "1st prize (nh\u1EA5t)",
          digits: 5,
          count: 1
        },
        {
          key: "prize2",
          label: "2nd prize (nh\xEC)",
          digits: 5,
          count: 2
        },
        {
          key: "prize3",
          label: "3rd prize (ba)",
          digits: 5,
          count: 6
        },
        {
          key: "prize4",
          label: "4th prize (t\u01B0)",
          digits: 4,
          count: 4
        },
        {
          key: "prize5",
          label: "5th prize (n\u0103m)",
          digits: 4,
          count: 6
        },
        {
          key: "prize6",
          label: "6th prize (s\xE1u)",
          digits: 3,
          count: 3
        },
        {
          key: "prize7",
          label: "7th prize (b\u1EA3y)",
          digits: 2,
          count: 4
        }
      ],
      maxDigits: 6
    }
  ],
  derivations: [
    {
      name: "g",
      items: [
        {
          op: "pad",
          expr: {
            op: "right",
            expr: {
              op: "unpadded",
              expr: {
                op: "slot",
                role: "S",
                key: "special"
              }
            },
            n: 5
          },
          width: 5
        }
      ],
      internal: true
    },
    {
      name: "A1",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "S",
                key: "prize7"
              },
              width: 2
            },
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "S",
                key: "prize6"
              },
              width: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "A2",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "S",
                key: "prize7",
                index: 1
              },
              width: 2
            },
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "S",
                key: "prize6",
                index: 1
              },
              width: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "A3",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "S",
                key: "prize7",
                index: 2
              },
              width: 2
            },
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "S",
                key: "prize6",
                index: 2
              },
              width: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "A4",
      items: [
        {
          op: "pad",
          expr: {
            op: "slot",
            role: "S",
            key: "prize7",
            index: 3
          },
          width: 2
        }
      ]
    },
    {
      name: "A",
      items: [
        {
          op: "ref",
          name: "A1"
        },
        {
          op: "ref",
          name: "A2"
        },
        {
          op: "ref",
          name: "A3"
        },
        {
          op: "ref",
          name: "A4"
        }
      ]
    },
    {
      name: "B",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "left",
              expr: {
                op: "ref",
                name: "g"
              },
              n: 2
            },
            {
              op: "right",
              expr: {
                op: "ref",
                name: "g"
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "C",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "right",
              expr: {
                op: "ref",
                name: "g"
              },
              n: 2
            },
            {
              op: "left",
              expr: {
                op: "ref",
                name: "g"
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "D",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "concat",
              parts: [
                {
                  op: "left",
                  expr: {
                    op: "ref",
                    name: "g"
                  },
                  n: 1
                },
                {
                  op: "right",
                  expr: {
                    op: "ref",
                    name: "g"
                  },
                  n: 1
                }
              ]
            },
            {
              op: "mid",
              expr: {
                op: "ref",
                name: "g"
              },
              start: 2,
              n: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "Lo2",
      items: [
        {
          op: "pad",
          expr: {
            op: "slot",
            role: "S",
            key: "prize7"
          },
          width: 2
        },
        {
          op: "pad",
          expr: {
            op: "slot",
            role: "S",
            key: "prize7",
            index: 1
          },
          width: 2
        },
        {
          op: "pad",
          expr: {
            op: "slot",
            role: "S",
            key: "prize7",
            index: 2
          },
          width: 2
        },
        {
          op: "pad",
          expr: {
            op: "slot",
            role: "S",
            key: "prize7",
            index: 3
          },
          width: 2
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "A1"
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "A2"
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "A3"
          },
          n: 2
        },
        {
          op: "each",
          role: "S",
          key: "prize5",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 4
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "S",
          key: "prize4",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 4
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "S",
          key: "prize3",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "S",
          key: "prize2",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "S",
          key: "prize1",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "left",
          expr: {
            op: "ref",
            name: "g"
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "g"
          },
          n: 2
        },
        {
          op: "concat",
          parts: [
            {
              op: "left",
              expr: {
                op: "ref",
                name: "g"
              },
              n: 1
            },
            {
              op: "right",
              expr: {
                op: "ref",
                name: "g"
              },
              n: 1
            }
          ]
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "B"
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "C"
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "D"
          },
          n: 2
        }
      ]
    },
    {
      name: "Lo3",
      items: [
        {
          op: "right",
          expr: {
            op: "ref",
            name: "A1"
          },
          n: 3
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "A2"
          },
          n: 3
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "A3"
          },
          n: 3
        },
        {
          op: "each",
          role: "S",
          key: "prize5",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 4
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "S",
          key: "prize4",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 4
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "S",
          key: "prize3",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "S",
          key: "prize2",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "S",
          key: "prize1",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "g"
          },
          n: 3
        },
        {
          op: "left",
          expr: {
            op: "ref",
            name: "g"
          },
          n: 3
        },
        {
          op: "mid",
          expr: {
            op: "ref",
            name: "g"
          },
          start: 2,
          n: 3
        }
      ]
    }
  ],
  matching: [
    {
      set: "A",
      post: "A",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "A1",
      post: "A1",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "A2",
      post: "A2",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "A3",
      post: "A3",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "A4",
      post: "A4",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "B",
      post: "B",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "C",
      post: "C",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "D",
      post: "D",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "Lo2",
      post: "Lo",
      lastDigits: [
        2
      ],
      duplicatePayOnce: false
    },
    {
      set: "Lo3",
      post: "Lo",
      lastDigits: [
        3
      ],
      duplicatePayOnce: false
    }
  ]
};

// src/rules/builtin/tn.json
var tn_default = {
  dslVersion: 1,
  key: "tn",
  origin: "builtin",
  description: "Thinh Nam 13:15 (TN, three provinces of 15 numbers) \u2014 posts as the reference sary (lo855 TC13:30, owner 2026-09-17): A B C D from the main province, F K N from the second, I P from the third, O Z from the main province, Lo 2D / 3D like VN 4:30.",
  inputs: [
    {
      role: "P1",
      label: "main province",
      slots: [
        {
          key: "prize8",
          label: "8th prize",
          digits: 2,
          count: 1
        },
        {
          key: "prize7",
          label: "7th prize",
          digits: 3,
          count: 1
        },
        {
          key: "prize6",
          label: "6th prize",
          digits: 4,
          count: 3
        },
        {
          key: "prize5",
          label: "5th prize",
          digits: 5,
          count: 4
        },
        {
          key: "prize4",
          label: "4th prize",
          digits: 5,
          count: 2
        },
        {
          key: "prize3",
          label: "3rd prize",
          digits: 5,
          count: 1
        },
        {
          key: "prize2",
          label: "2nd prize",
          digits: 5,
          count: 1
        },
        {
          key: "prize1",
          label: "1st prize",
          digits: 5,
          count: 1
        },
        {
          key: "special",
          label: "Special prize",
          digits: 5,
          count: 1
        }
      ],
      maxDigits: "slot"
    },
    {
      role: "P2",
      label: "second province",
      slots: [
        {
          key: "prize8",
          label: "8th prize",
          digits: 2,
          count: 1
        },
        {
          key: "prize7",
          label: "7th prize",
          digits: 3,
          count: 1
        },
        {
          key: "prize6",
          label: "6th prize",
          digits: 4,
          count: 3
        },
        {
          key: "prize5",
          label: "5th prize",
          digits: 5,
          count: 4
        },
        {
          key: "prize4",
          label: "4th prize",
          digits: 5,
          count: 2
        },
        {
          key: "prize3",
          label: "3rd prize",
          digits: 5,
          count: 1
        },
        {
          key: "prize2",
          label: "2nd prize",
          digits: 5,
          count: 1
        },
        {
          key: "prize1",
          label: "1st prize",
          digits: 5,
          count: 1
        },
        {
          key: "special",
          label: "Special prize",
          digits: 5,
          count: 1
        }
      ],
      maxDigits: "slot"
    },
    {
      role: "P3",
      label: "third province",
      slots: [
        {
          key: "prize8",
          label: "8th prize",
          digits: 2,
          count: 1
        },
        {
          key: "prize7",
          label: "7th prize",
          digits: 3,
          count: 1
        },
        {
          key: "prize6",
          label: "6th prize",
          digits: 4,
          count: 3
        },
        {
          key: "prize5",
          label: "5th prize",
          digits: 5,
          count: 4
        },
        {
          key: "prize4",
          label: "4th prize",
          digits: 5,
          count: 2
        },
        {
          key: "prize3",
          label: "3rd prize",
          digits: 5,
          count: 1
        },
        {
          key: "prize2",
          label: "2nd prize",
          digits: 5,
          count: 1
        },
        {
          key: "prize1",
          label: "1st prize",
          digits: 5,
          count: 1
        },
        {
          key: "special",
          label: "Special prize",
          digits: 5,
          count: 1
        }
      ],
      maxDigits: "slot"
    }
  ],
  derivations: [
    {
      name: "g1",
      items: [
        {
          op: "pad",
          expr: {
            op: "slot",
            role: "P1",
            key: "special"
          },
          width: 5
        }
      ],
      internal: true
    },
    {
      name: "A",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "P1",
                key: "prize8"
              },
              width: 2
            },
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "P1",
                key: "prize7"
              },
              width: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "B",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "left",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 2
            },
            {
              op: "right",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "C",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "right",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 2
            },
            {
              op: "left",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "D",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "concat",
              parts: [
                {
                  op: "left",
                  expr: {
                    op: "ref",
                    name: "g1"
                  },
                  n: 1
                },
                {
                  op: "right",
                  expr: {
                    op: "ref",
                    name: "g1"
                  },
                  n: 1
                }
              ]
            },
            {
              op: "mid",
              expr: {
                op: "ref",
                name: "g1"
              },
              start: 2,
              n: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "Lo2",
      items: [
        {
          op: "pad",
          expr: {
            op: "slot",
            role: "P1",
            key: "prize8"
          },
          width: 2
        },
        {
          op: "right",
          expr: {
            op: "pad",
            expr: {
              op: "slot",
              role: "P1",
              key: "prize7"
            },
            width: 3
          },
          n: 2
        },
        {
          op: "each",
          role: "P1",
          key: "prize6",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 4
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize5",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize4",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize3",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize2",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize1",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 2
          }
        },
        {
          op: "left",
          expr: {
            op: "ref",
            name: "g1"
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "right",
            expr: {
              op: "ref",
              name: "g1"
            },
            n: 3
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "g1"
          },
          n: 2
        },
        {
          op: "right",
          expr: {
            op: "left",
            expr: {
              op: "ref",
              name: "g1"
            },
            n: 3
          },
          n: 2
        },
        {
          op: "concat",
          parts: [
            {
              op: "left",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 1
            },
            {
              op: "right",
              expr: {
                op: "ref",
                name: "g1"
              },
              n: 1
            }
          ]
        },
        {
          op: "right",
          expr: {
            op: "mid",
            expr: {
              op: "ref",
              name: "g1"
            },
            start: 2,
            n: 3
          },
          n: 2
        }
      ]
    },
    {
      name: "Lo3",
      items: [
        {
          op: "pad",
          expr: {
            op: "slot",
            role: "P1",
            key: "prize7"
          },
          width: 3
        },
        {
          op: "each",
          role: "P1",
          key: "prize6",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 4
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize5",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize4",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize3",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize2",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "each",
          role: "P1",
          key: "prize1",
          expr: {
            op: "right",
            expr: {
              op: "pad",
              expr: {
                op: "item"
              },
              width: 5
            },
            n: 3
          }
        },
        {
          op: "right",
          expr: {
            op: "ref",
            name: "g1"
          },
          n: 3
        },
        {
          op: "left",
          expr: {
            op: "ref",
            name: "g1"
          },
          n: 3
        },
        {
          op: "mid",
          expr: {
            op: "ref",
            name: "g1"
          },
          start: 2,
          n: 3
        }
      ]
    },
    {
      name: "g2",
      items: [
        {
          op: "pad",
          expr: {
            op: "slot",
            role: "P2",
            key: "special"
          },
          width: 5
        }
      ],
      internal: true,
      onMissing: "omit",
      note: "F, K, N not filled: no second province"
    },
    {
      name: "F",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "P2",
                key: "prize8"
              },
              width: 2
            },
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "P2",
                key: "prize7"
              },
              width: 3
            }
          ],
          sep: "-"
        }
      ],
      onMissing: "omit",
      note: "F, K, N not filled: no second province"
    },
    {
      name: "K",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "right",
              expr: {
                op: "pad",
                expr: {
                  op: "slot",
                  role: "P2",
                  key: "prize6"
                },
                width: 4
              },
              n: 2
            },
            {
              op: "right",
              expr: {
                op: "pad",
                expr: {
                  op: "slot",
                  role: "P2",
                  key: "prize6"
                },
                width: 4
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ],
      onMissing: "omit",
      note: "F, K, N not filled: no second province"
    },
    {
      name: "N",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "left",
              expr: {
                op: "ref",
                name: "g2"
              },
              n: 2
            },
            {
              op: "right",
              expr: {
                op: "ref",
                name: "g2"
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ],
      onMissing: "omit",
      note: "F, K, N not filled: no second province"
    },
    {
      name: "O",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "right",
              expr: {
                op: "pad",
                expr: {
                  op: "slot",
                  role: "P1",
                  key: "prize7"
                },
                width: 3
              },
              n: 2
            },
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "P1",
                key: "prize7"
              },
              width: 3
            }
          ],
          sep: "-"
        }
      ]
    },
    {
      name: "I",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "P3",
                key: "prize8"
              },
              width: 2
            },
            {
              op: "pad",
              expr: {
                op: "slot",
                role: "P3",
                key: "prize7"
              },
              width: 3
            }
          ],
          sep: "-"
        }
      ],
      onMissing: "omit",
      note: "I, P not filled: no third province"
    },
    {
      name: "g3",
      items: [
        {
          op: "pad",
          expr: {
            op: "slot",
            role: "P3",
            key: "special"
          },
          width: 5
        }
      ],
      internal: true,
      onMissing: "omit",
      note: "I, P not filled: no third province"
    },
    {
      name: "P",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "left",
              expr: {
                op: "ref",
                name: "g3"
              },
              n: 2
            },
            {
              op: "right",
              expr: {
                op: "ref",
                name: "g3"
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ],
      onMissing: "omit",
      note: "I, P not filled: no third province"
    },
    {
      name: "Z",
      items: [
        {
          op: "concat",
          parts: [
            {
              op: "right",
              expr: {
                op: "pad",
                expr: {
                  op: "slot",
                  role: "P1",
                  key: "prize6"
                },
                width: 4
              },
              n: 2
            },
            {
              op: "right",
              expr: {
                op: "pad",
                expr: {
                  op: "slot",
                  role: "P1",
                  key: "prize6"
                },
                width: 4
              },
              n: 3
            }
          ],
          sep: "-"
        }
      ]
    }
  ],
  matching: [
    {
      set: "A",
      post: "A",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "B",
      post: "B",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "C",
      post: "C",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "D",
      post: "D",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "Lo2",
      post: "Lo",
      lastDigits: [
        2
      ],
      duplicatePayOnce: false
    },
    {
      set: "Lo3",
      post: "Lo",
      lastDigits: [
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "F",
      post: "F",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "K",
      post: "K",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "N",
      post: "N",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "O",
      post: "O",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "I",
      post: "I",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "P",
      post: "P",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    },
    {
      set: "Z",
      post: "Z",
      lastDigits: [
        2,
        3
      ],
      duplicatePayOnce: false
    }
  ],
  notes: [
    "3P has no printed source: enter it by hand"
  ]
};

// src/rules/ported.ts
var BUILTIN_JSON = { vn430: vn430_default, vn630: vn630_default, tn: tn_default };
var cache = /* @__PURE__ */ new Map();
function builtinRuleDsl(key) {
  let hit = cache.get(key);
  if (!hit) {
    hit = parseRuleDsl(BUILTIN_JSON[key]);
    cache.set(key, hit);
  }
  return structuredClone(hit);
}
function builtinRuleDslJson(key) {
  return structuredClone(BUILTIN_JSON[key]);
}
function builtinKeyForRule(cfg, productCode) {
  if (cfg.engine === "vn430" || cfg.engine === "vn630") return cfg.engine;
  if (cfg.engine === "tn" && productCode && /^TN/i.test(productCode)) return "tn";
  return null;
}
function portedRuleDsl(cfg, productCode) {
  const key = builtinKeyForRule(cfg, productCode);
  if (!key) return null;
  const dsl = builtinRuleDsl(key);
  const thinhNam = !!productCode && /^TN/i.test(productCode);
  if (key === "vn430") {
    const map = cfg.weekdayMap ?? {};
    for (const input of dsl.inputs) {
      if (!("alias" in input)) continue;
      const which = input.role === "PX" ? "px" : input.role === "PY" ? "py" : null;
      if (!which) continue;
      input.alias.cases = Object.fromEntries(WEEKDAY_KEYS.map((k2, i) => [k2, `P${map[String(i)]?.[which] ?? 2}`]));
      input.alias.default = "P2";
    }
    const k = dsl.derivations.find((d) => d.name === "K");
    const six = cfg.kUnpadded === false ? { op: "pad", expr: { op: "slot", role: "PX", key: "prize6" }, width: 4 } : { op: "unpadded", expr: { op: "slot", role: "PX", key: "prize6" } };
    k.items = [{ op: "concat", parts: [{ op: "right", expr: six, n: 2 }, { op: "right", expr: six, n: 3 }], sep: "-" }];
    if (cfg.provincePosts) (thinhNam ? withThreeProvincePosts : withProvincePosts)(dsl);
  }
  if (key === "tn" && cfg.provincePosts) withThreeProvincePosts(dsl);
  for (const m of dsl.matching) if (m.post === "Lo") m.duplicatePayOnce = !!cfg.loDuplicatePayOnce;
  return dsl;
}
var VN430_PROVINCE_SETS = [{ set: "Prov1", post: "P1" }, { set: "Prov2", post: "P2" }, { set: "Prov3", post: "P3" }, { set: "Prov4", post: "P4" }, { set: "Prov", post: "3P" }];
var provinceComposite = (role) => ({ op: "concat", parts: [{ op: "pad", expr: { op: "slot", role, key: "prize8" }, width: 2 }, { op: "pad", expr: { op: "slot", role, key: "prize7" }, width: 3 }], sep: "-" });
function withThreeProvincePosts(dsl) {
  if (dsl.derivations.some((d) => d.name === "Prov")) return dsl;
  const list = (names) => ({ op: "flatten", items: names.map((name2) => ({ op: "set", name: name2 })) });
  dsl.derivations.push(
    { name: "Prov1", items: [provinceComposite("P1")] },
    { name: "Prov2", items: [provinceComposite("P2")], onMissing: "omit", note: "P2 not filled: {label} missing" },
    { name: "Prov3", items: [provinceComposite("P3")], onMissing: "omit", note: "P3 not filled: {label} missing" },
    { name: "Prov", items: [list(["Prov1", "Prov2", "Prov3"])], onMissing: "omit", note: "3P not filled: {label} missing" }
  );
  for (const { set, post } of VN430_PROVINCE_SETS) if (set !== "Prov4") dsl.matching.push({ set, post, lastDigits: [2, 3], duplicatePayOnce: false });
  return dsl;
}
function withProvincePosts(dsl) {
  if (dsl.derivations.some((d) => d.name === "Prov")) return dsl;
  const empty = { op: "take", expr: { op: "set", name: "Prov1" }, n: 0 };
  const list = (names) => ({ op: "flatten", items: names.map((name2) => ({ op: "set", name: name2 })) });
  dsl.derivations.push(
    { name: "Prov1", items: [provinceComposite("P1")] },
    { name: "Prov2", items: [provinceComposite("P2")], onMissing: "omit", note: "P2 not filled: {label} missing" },
    { name: "Prov3", items: [provinceComposite("P3")], onMissing: "omit", note: "P3 not filled: {label} missing" },
    { name: "Prov4", items: [{ op: "weekday", cases: { sat: { op: "flatten", items: [provinceComposite("P4")] } }, default: empty }], onMissing: "omit", note: "P4 not filled: {label} missing" },
    { name: "Prov", items: [{ op: "weekday", cases: { sat: list(["Prov1", "Prov2", "Prov3", "Prov4"]) }, default: list(["Prov1", "Prov2", "Prov3"]) }], onMissing: "omit", note: "3P not filled: {label} missing" }
  );
  for (const { set, post } of VN430_PROVINCE_SETS) dsl.matching.push({ set, post, lastDigits: [2, 3], duplicatePayOnce: false });
  return dsl;
}
function ruleDslFor(cfg, productCode) {
  if (cfg.dsl) return cfg.dsl;
  return portedRuleDsl(cfg, productCode);
}
function dslForNewVersion(cfg, productCode) {
  if (cfg.dsl && cfg.dsl.origin === "custom") return cfg.dsl;
  return portedRuleDsl(cfg, productCode);
}
function legacyRoleValues(dsl, provinces) {
  const roles = dsl.inputs.filter((i) => !("alias" in i));
  const out = {};
  roles.forEach((r, i) => {
    if (provinces[i] !== void 0) out[r.role] = provinces[i];
  });
  return out;
}
function postResultOf(value) {
  const i = value.indexOf("-");
  return i < 0 ? { d2: value, d3: null } : { d2: value.slice(0, i), d3: value.slice(i + 1) };
}
function legacyDeriveOutput(dsl, r) {
  const posts = {};
  const lo2 = [];
  const lo3 = [];
  for (const m of dsl.matching) {
    const values = r.sets[m.set];
    if (!values) continue;
    if (m.post === "Lo") {
      if (m.lastDigits.length === 1 && m.lastDigits[0] === 2) lo2.push(...values);
      if (m.lastDigits.length === 1 && m.lastDigits[0] === 3) lo3.push(...values);
      continue;
    }
    if (values.length === 1 && m.lastDigits.length === 2) posts[m.post] = postResultOf(values[0]);
  }
  return { posts, lo2, lo3, notes: r.notes, entries: dslEntries(dsl, r.sets), sets: r.sets };
}
function deriveWithDsl(dsl, provinces, weekday) {
  const r = evaluateRuleDsl(dsl, { roles: legacyRoleValues(dsl, provinces), weekday });
  return legacyDeriveOutput(dsl, r);
}
function deriveThinhNamDsl(main, second, third) {
  const dsl = builtinRuleDsl("tn");
  const r = evaluateRuleDsl(dsl, { roles: { P1: main, P2: second ?? [], P3: third ?? [] }, weekday: null });
  return { entries: dslEntries(dsl, r.sets), notes: r.notes };
}

// src/schemas/rule-sections.ts
import { z as z3 } from "zod";
var BET_TYPE_MODES = [...BET_MODES, "roll_multiply"];
var RULE_SECTIONS = ["betTypes", "payouts", "limits", "timing"];
var RULE_SECTION_ORIGINS = ["generated", "custom"];
var betTypeCode = z3.string().regex(/^[A-Za-z0-9][A-Za-z0-9_+.-]{0,39}$/, "letters, digits, _ + . - (max 40)");
var postCode = z3.string().trim().min(1).max(4);
var resultSetRefSchema = z3.string().regex(/^(manual:\S{1,4}|[A-Za-z][A-Za-z0-9_]{0,31})$/, "a derived set name or manual:<post>");
var hhmm = z3.string().regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, "HH:MM");
var amount = z3.number().int().min(0).max(1e12);
var digitLen = z3.union([z3.literal(2), z3.literal(3), z3.literal(4)]);
var uniqueList = (item, max) => z3.array(item).max(max).refine((l) => new Set(l).size === l.length, "values must be unique");
var betTypeMultiplierSchema = z3.strictObject({
  /** setSize = the number of values of the result set for the digit length; fixed = `value`; sumOfPosts = Σ member posts (groups) */
  source: z3.enum(["setSize", "fixed", "sumOfPosts"]),
  /** the resolved multiplier (clients price with it); filled by the server for setSize / sumOfPosts */
  value: z3.number().int().min(0).max(1e3).optional(),
  /**
   * 2026-09 (VN 4:30 3P = P1 P2 P3, + P4 on Saturday): the value on a weekday ('0' Sunday … '6' Saturday) where it differs from `value` —
   * a setSize whose derived set has another size that day, a fixed value per weekday, or a group whose members vary. A session's rule
   * view and the pricing of a session resolve it for the draw's weekday (`ruleForWeekday`); the stored legacy multipliers keep `value`.
   */
  byWeekday: z3.record(z3.string().regex(/^[0-6]$/), z3.number().int().min(0).max(1e3)).optional()
});
var multiplierOn = (m, weekday) => weekday == null ? m.value : m.byWeekday?.[String(weekday)] ?? m.value;
var betTypeSchema = z3.strictObject({
  /** unique inside the version, e.g. A_2D, Lo_3D, ABCD_2D */
  code: betTypeCode,
  label: z3.strictObject({ km: z3.string().trim().max(60), en: z3.string().trim().max(60) }),
  /** one post, or the members of a group (a line whose posts are exactly these) */
  posts: uniqueList(postCode, 30).refine((l) => l.length > 0, "at least one post"),
  digitLen,
  /** the set each post's numbers match: one name for a single post, one per post (same order) for a group */
  resultSet: z3.union([resultSetRefSchema, z3.array(resultSetRefSchema).min(1).max(30)]),
  multiplier: betTypeMultiplierSchema,
  /** empty = nothing can be sold (a generated bet type of a product without enabled modes); an edited bet type needs one (NO_MODES) */
  allowedModes: uniqueList(z3.enum(BET_TYPE_MODES), BET_TYPE_MODES.length),
  /** refuse roll lines producing more numbers than this; null / absent = no limit */
  rollLimit: z3.number().int().min(1).max(1e3).nullable().optional(),
  /** the combination is refused (e.g. VN 6:30 A4 3D) */
  invalid: z3.boolean().optional(),
  /** absent = the product's value */
  minAmount: amount.optional(),
  maxAmountPerNumber: amount.optional(),
  amountStep: z3.number().int().min(1).max(1e6).optional(),
  sort: z3.number().int().min(0).max(1e5).default(0)
});
var isoTs = z3.iso.datetime({ offset: true });
var rate = z3.number().min(0).max(9999999999e-2).refine((r) => Math.abs(Math.round(r * 100) - r * 100) < 1e-6, "at most 2 decimals");
var payoutEntrySchema = z3.strictObject({
  /** a bet type code — the most specific match */
  betType: betTypeCode.optional(),
  /** a post code or '*' (every post); with digitLen */
  post: z3.union([postCode, z3.literal("*")]).optional(),
  digitLen: digitLen.optional(),
  /** paid per 1 riel of stake per winning number (90 = 2D, 800 = 3D) */
  rate,
  /** a different rate for some numbers */
  specialNumbers: z3.array(z3.strictObject({ number: z3.string().regex(/^\d{2,4}$/), rate })).max(1e3).optional(),
  /** effective inside the version from / until (absent = always); a ticket snapshots the entry in force at its sale */
  effectiveFrom: isoTs.optional(),
  effectiveTo: isoTs.nullable().optional()
}).refine((e) => !!e.betType || e.digitLen !== void 0, { message: "a payout names a bet type or a digit length (+ post)" });
var ruleLimitsSchema = z3.strictObject({
  /** stake per post × digit length × number (0 = off); scope 'day' adds the other sessions of the game on the draw date */
  perNumber: z3.strictObject({ amount, scope: z3.enum(["session", "day"]).default("session") }).optional(),
  /** per-number stake limit for the numbers of a bet type (overrides perNumber for them) */
  perBetType: z3.record(betTypeCode, amount).optional(),
  /** total stake of the session (0 = off) */
  perSession: amount.optional(),
  /** total stake of one seller account in the session (0 = off) */
  perAgentPerSession: amount.optional(),
  /** a ticket total at or above it is held for review (0 = off) */
  bigBetThreshold: amount.optional(),
  /** reference only: the agent daily sales limit stays account-level (agents.daily_sales_limit, setting agent_daily_limit_default; 5.7) */
  agentDailyLimit: z3.literal("account").optional()
});
var ruleTimingCloseSchema = z3.strictObject({
  posts: uniqueList(postCode, 60).refine((l) => l.length > 0, "at least one post"),
  /** 0 = Sunday … 6; absent = every day */
  weekday: z3.number().int().min(0).max(6).optional(),
  /** close at this local time of the draw date … */
  time: hhmm.optional(),
  /** … or this many minutes after the draw time (negative = before) */
  offsetMinutes: z3.number().int().min(-1440).max(1440).optional(),
  /** result release time of these posts (else the section's release rule, when a close is defined) */
  release: hhmm.optional(),
  /** true = wins over the weekly schedule (the legacy rule `times` behaviour); false = default when the schedule has no close for the post */
  pin: z3.boolean().default(false)
}).refine((c) => !(c.time && c.offsetMinutes !== void 0), { message: "time or offsetMinutes, not both" });
var ruleTimingSchema = z3.strictObject({
  closes: z3.array(ruleTimingCloseSchema).max(200),
  release: z3.discriminatedUnion("mode", [z3.strictObject({ mode: z3.literal("offset"), minutes: z3.number().int().min(0).max(240) }), z3.strictObject({ mode: z3.literal("fixed"), time: hhmm })])
});
var ruleSectionOriginsSchema = z3.strictObject({
  betTypes: z3.enum(RULE_SECTION_ORIGINS).optional(),
  payouts: z3.enum(RULE_SECTION_ORIGINS).optional(),
  limits: z3.enum(RULE_SECTION_ORIGINS).optional(),
  timing: z3.enum(RULE_SECTION_ORIGINS).optional()
});
var betTypesPutSchema = z3.strictObject({ betTypes: z3.array(betTypeSchema).max(500) });
var payoutsPutSchema = z3.strictObject({ payouts: z3.array(payoutEntrySchema).max(2e3) });
var limitsPutSchema = z3.strictObject({ limits: ruleLimitsSchema });
var timingPutSchema = z3.strictObject({ timing: ruleTimingSchema });

// src/rules/sections.ts
function countsFor(value, digitLen2, lastDigits) {
  if (!lastDigits.includes(digitLen2)) return false;
  if (lastDigits.length === 1) return true;
  const composite2 = /^(\d{1,2})-(\d{1,3})$/.exec(value);
  if (composite2) return digitLen2 === 2 ? composite2[1].length === 2 : digitLen2 === 3 ? composite2[2].length === 3 : false;
  return value.replace(/\D/g, "").length >= digitLen2;
}
var weekdaySizeCache = /* @__PURE__ */ new WeakMap();
var sizeCache = /* @__PURE__ */ new WeakMap();
function dslSetSizesByWeekday(dsl) {
  const hit = weekdaySizeCache.get(dsl);
  if (hit) return hit;
  const roles = {};
  for (const r of dslRoles(dsl)) roles[r.role] = Object.fromEntries(r.slots.map((s) => [s.key, Array.from({ length: s.count }, () => "1".repeat(s.digits))]));
  const out = {};
  for (let wd = 0; wd < 7; wd++) {
    let sets;
    try {
      sets = evaluateRuleDsl(dsl, { roles, weekday: wd }).sets;
    } catch {
      weekdaySizeCache.set(dsl, {});
      return {};
    }
    for (const d of dsl.derivations) {
      if (d.internal) continue;
      const values = sets[d.name] ?? [];
      const lastDigits = dsl.matching.find((m) => m.set === d.name)?.lastDigits ?? [2, 3];
      out[d.name] ??= {};
      for (const len of [2, 3, 4]) {
        (out[d.name][len] ??= [])[wd] = values.filter((v) => countsFor(v, len, lastDigits)).length;
      }
    }
  }
  weekdaySizeCache.set(dsl, out);
  return out;
}
function dslSetSizes(dsl) {
  const hit = sizeCache.get(dsl);
  if (hit) return hit;
  const out = {};
  for (const [name2, byLen] of Object.entries(dslSetSizesByWeekday(dsl))) {
    out[name2] = {};
    for (const [len, sizes] of Object.entries(byLen)) if (sizes && sizes.length === 7 && sizes.every((n) => n === sizes[0])) out[name2][Number(len)] = sizes[0];
  }
  sizeCache.set(dsl, out);
  return out;
}
function weekdaySizeMultiplier(sizes, preferred) {
  if (!sizes || sizes.length !== 7 || sizes.some((n) => n < 1)) return null;
  const counts = /* @__PURE__ */ new Map();
  for (const n of sizes) counts.set(n, (counts.get(n) ?? 0) + 1);
  const value = preferred !== void 0 && counts.has(preferred) ? preferred : [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
  const byWeekday = {};
  sizes.forEach((n, wd) => {
    if (n !== value) byWeekday[String(wd)] = n;
  });
  return Object.keys(byWeekday).length ? { value, byWeekday } : { value };
}
var weekdaysOf = (ms) => [...new Set(ms.flatMap((m) => Object.keys(m.byWeekday ?? {}).map(Number)))].sort();
function ruleForWeekday(cfg, weekday) {
  if (weekday == null || !cfg.betTypes?.some((b) => b.multiplier.byWeekday && Object.keys(b.multiplier.byWeekday).length)) return cfg;
  const betTypes = cfg.betTypes.map((b) => {
    const { byWeekday: _w, ...rest } = b.multiplier;
    return { ...b, multiplier: { ...rest, value: multiplierOn(b.multiplier, weekday) } };
  });
  const { multipliers } = projectBetTypes({ multipliers: cfg.multipliers, invalid: cfg.invalid, maxRollNumbers: cfg.maxRollNumbers ?? 0 }, betTypes);
  return { ...cfg, betTypes, multipliers };
}
function resultSetFor(dsl, postCode2, digitLen2) {
  const m = dsl?.matching.find((x) => x.post === postCode2 && x.lastDigits.includes(digitLen2));
  return m ? m.set : `manual:${postCode2}`;
}
var offeredOf = (ctx) => ctx.posts.map((p) => p.code).filter((c) => !ctx.offeredPosts || ctx.offeredPosts.includes(c));
function generatedModes(betModes) {
  const modes = BET_MODES.filter((m) => betModes.includes(m));
  if (betModes.includes("multiply")) modes.push("roll_multiply");
  return modes;
}
function generateBetTypes(cfg, ctx) {
  const dsl = ruleDslFor(cfg, ctx.productCode);
  const sizes = dsl ? dslSetSizes(dsl) : {};
  const weekdaySizes = dsl ? dslSetSizesByWeekday(dsl) : {};
  const modes = generatedModes(ctx.betModes);
  const rollLimit = cfg.maxRollNumbers && cfg.maxRollNumbers > 0 ? cfg.maxRollNumbers : null;
  const lens = ctx.digitLens.filter((d) => d === 2 || d === 3).sort();
  const offered = offeredOf(ctx);
  const out = [];
  const codes = /* @__PURE__ */ new Set();
  const uniqueCode = (raw) => {
    let base = raw.replace(/[^A-Za-z0-9_+.-]/g, "_").slice(0, 36);
    if (!/^[A-Za-z0-9]/.test(base)) base = `T${base}`.slice(0, 36);
    let c = base;
    for (let k = 2; codes.has(c); k++) c = `${base}.${k}`;
    codes.add(c);
    return c;
  };
  for (const post of offered) {
    for (const d of lens) {
      const set = resultSetFor(dsl, post, d);
      const legacy = legacyMultiplier(cfg, post, d);
      const invalid = legacy === null;
      const value = cfg.multipliers[post]?.[String(d)] ?? 1;
      const size = set.startsWith("manual:") ? void 0 : sizes[set]?.[d];
      const weekly = size === void 0 && !set.startsWith("manual:") ? weekdaySizeMultiplier(weekdaySizes[set]?.[d], value) : null;
      const multiplier = size !== void 0 && size === value ? { source: "setSize", value } : weekly && weekly.value === value ? { source: "setSize", ...weekly } : { source: "fixed", value };
      out.push({
        code: uniqueCode(`${post}_${d}D`),
        label: { km: `${post} ${d}D`, en: `${post} ${d}D` },
        posts: [post],
        digitLen: d,
        resultSet: set,
        multiplier,
        allowedModes: [...modes],
        rollLimit,
        ...invalid ? { invalid: true } : {},
        sort: out.length
      });
    }
  }
  for (const g of ctx.groups) {
    if (g.isCloseGroup || g.code.startsWith("c:") || g.postCodes.length < 2 || !g.postCodes.every((c) => offered.includes(c))) continue;
    for (const d of lens) {
      const members = g.postCodes.map((p) => legacyMultiplier(cfg, p, d));
      const invalid = members.some((m) => m === null);
      const memberTypes = g.postCodes.map((p) => singleBetType(out, p, d));
      const sumOn = (wd) => memberTypes.reduce((a, m, i) => a + (m ? m.invalid ? 0 : multiplierOn(m.multiplier, wd) ?? 0 : members[i] ?? 0), 0);
      const base = sumOn(null);
      const byWeekday = {};
      for (const wd of weekdaysOf(memberTypes.filter((m) => !!m).map((m) => m.multiplier))) if (sumOn(wd) !== base) byWeekday[String(wd)] = sumOn(wd);
      out.push({
        code: uniqueCode(`${g.code}_${d}D`),
        label: { km: `${g.code} ${d}D`, en: `${g.code} ${d}D` },
        posts: [...g.postCodes],
        digitLen: d,
        resultSet: g.postCodes.map((p) => resultSetFor(dsl, p, d)),
        multiplier: { source: "sumOfPosts", value: base, ...Object.keys(byWeekday).length ? { byWeekday } : {} },
        allowedModes: [...modes],
        rollLimit,
        ...invalid ? { invalid: true } : {},
        sort: out.length
      });
    }
  }
  return out;
}
function generatePayouts(rates) {
  const ms = (s) => new Date(s).getTime();
  const starts = [...new Set(rates.map((r) => ms(r.effectiveFrom)))].sort((a, b) => a - b);
  const first = starts[0];
  return rates.map((r) => {
    const from = ms(r.effectiveFrom);
    let to = r.effectiveTo ? ms(r.effectiveTo) : null;
    if (to !== null) {
      const next = starts.find((s) => s > from);
      if (next !== void 0 && to < next) to = next;
    }
    return { post: r.postCode ?? "*", digitLen: r.digitLen, rate: r.rate, ...from === first ? {} : { effectiveFrom: new Date(from).toISOString() }, effectiveTo: to === null ? null : new Date(to).toISOString() };
  });
}
function bigBetLimitsOf(bigBet, productCode) {
  const o = bigBet?.byProduct?.[productCode] ?? {};
  return { thresholdAmount: o.thresholdAmount ?? bigBet?.thresholdAmount ?? 0, numberExposureLimit: o.numberExposureLimit ?? bigBet?.numberExposureLimit ?? 0 };
}
function generateLimits(bigBet, productCode) {
  const l = bigBetLimitsOf(bigBet, productCode);
  return { bigBetThreshold: l.thresholdAmount, perNumber: { amount: l.numberExposureLimit, scope: "session" }, agentDailyLimit: "account" };
}
function generateTiming(cfg) {
  return {
    closes: cfg.times.map((g) => ({ posts: [...g.posts], ...g.close ? { time: g.close } : {}, ...g.release ? { release: g.release } : {}, pin: !!g.close })),
    release: { mode: "offset", minutes: cfg.releaseOffsetMin ?? 10 }
  };
}
function generateRuleSections(cfg, ctx) {
  return { betTypes: generateBetTypes(cfg, ctx), payouts: generatePayouts(ctx.payoutRates), limits: generateLimits(ctx.bigBet, ctx.productCode), timing: generateTiming(cfg) };
}
var originOf = (cfg, section) => cfg?.origins?.[section] ?? "generated";
function withRuleSections(cfg, ctx) {
  const gen = generateRuleSections(cfg, ctx);
  const origins = { betTypes: originOf(cfg, "betTypes"), payouts: originOf(cfg, "payouts"), limits: originOf(cfg, "limits"), timing: originOf(cfg, "timing") };
  return {
    ...cfg,
    betTypes: origins.betTypes === "custom" && cfg.betTypes ? cfg.betTypes : gen.betTypes,
    payouts: origins.payouts === "custom" && cfg.payouts ? cfg.payouts : gen.payouts,
    limits: origins.limits === "custom" && cfg.limits ? cfg.limits : gen.limits,
    timing: origins.timing === "custom" && cfg.timing ? cfg.timing : gen.timing,
    origins
  };
}
var SECTION_FIELDS = ["betTypes", "payouts", "limits", "timing", "origins"];
function canonicalJson(v) {
  if (Array.isArray(v)) return `[${v.map(canonicalJson).join(",")}]`;
  if (v && typeof v === "object") return `{${Object.keys(v).filter((k) => v[k] !== void 0).sort().map((k) => `${JSON.stringify(k)}:${canonicalJson(v[k])}`).join(",")}}`;
  return JSON.stringify(v ?? null);
}
function withoutRuleSections(cfg) {
  const out = { ...cfg };
  for (const k of SECTION_FIELDS) delete out[k];
  return out;
}
function reconcileGeneratedSections(cfg) {
  let out = cfg;
  if (cfg.betTypes && originOf(cfg, "betTypes") === "generated" && legacyDisagreement(cfg, cfg.betTypes) !== null) {
    const { betTypes: _b, ...rest } = out;
    out = rest;
  }
  if (cfg.timing && originOf(cfg, "timing") === "generated" && canonicalJson(generateTiming(cfg)) !== canonicalJson(cfg.timing)) {
    const { timing: _t, ...rest } = out;
    out = rest;
  }
  return out;
}
function normalizeBetTypes(betTypes, dsl) {
  const sizes = dsl ? dslSetSizes(dsl) : {};
  const weekdaySizes = dsl ? dslSetSizesByWeekday(dsl) : {};
  const firstPass = betTypes.map((bt) => {
    if (bt.multiplier.source !== "setSize" || bt.posts.length !== 1 || bt.multiplier.value !== void 0) return bt;
    const set = Array.isArray(bt.resultSet) ? bt.resultSet[0] : bt.resultSet;
    const size = sizes[set]?.[bt.digitLen];
    if (size !== void 0) return { ...bt, multiplier: { ...bt.multiplier, value: size } };
    const weekly = weekdaySizeMultiplier(weekdaySizes[set]?.[bt.digitLen]);
    return weekly ? { ...bt, multiplier: { source: "setSize", ...weekly } } : bt;
  });
  return firstPass.map((bt) => {
    if (bt.multiplier.source !== "sumOfPosts") return bt;
    const members = bt.posts.map((p) => singleBetType(firstPass, p, bt.digitLen));
    if (members.some((m) => !m)) return bt;
    const sumOn = (wd) => members.reduce((a, m) => a + (m.invalid ? 0 : multiplierOn(m.multiplier, wd) ?? 0), 0);
    const value = sumOn(null);
    const byWeekday = {};
    for (const wd of weekdaysOf(members.map((m) => m.multiplier))) if (sumOn(wd) !== value) byWeekday[String(wd)] = sumOn(wd);
    return { ...bt, multiplier: { source: "sumOfPosts", value, ...Object.keys(byWeekday).length ? { byWeekday } : {} } };
  });
}
function projectBetTypes(cfg, betTypes) {
  const multipliers = structuredClone(cfg.multipliers);
  let invalid = [...cfg.invalid];
  for (const bt of betTypes) {
    if (bt.posts.length !== 1 || bt.digitLen !== 2 && bt.digitLen !== 3) continue;
    const post = bt.posts[0];
    const key = String(bt.digitLen);
    invalid = invalid.filter((x) => !(x.post === post && x.digitLen === bt.digitLen));
    if (bt.invalid) invalid.push({ post, digitLen: bt.digitLen });
    const value = bt.multiplier.value ?? 1;
    if (value !== 1 || multipliers[post]?.[key] !== void 0) multipliers[post] = { ...multipliers[post] ?? {}, [key]: value };
  }
  const limits = [...new Set(betTypes.map((b) => b.rollLimit ?? 0))];
  return { multipliers, invalid, maxRollNumbers: betTypes.length === 0 ? cfg.maxRollNumbers : limits.length === 1 ? limits[0] : 0 };
}
function projectTiming(cfg, timing) {
  return {
    times: timing.closes.filter((c) => c.weekday === void 0 && c.offsetMinutes === void 0 && (c.release || c.time && c.pin)).map((c) => ({ posts: [...c.posts], ...c.time && c.pin ? { close: c.time } : {}, ...c.release ? { release: c.release } : {} })),
    releaseOffsetMin: timing.release.mode === "offset" ? timing.release.minutes : cfg.releaseOffsetMin
  };
}
function legacyDisagreement(cfg, betTypes) {
  for (const bt of betTypes) {
    if (bt.posts.length !== 1 || bt.digitLen !== 2 && bt.digitLen !== 3) continue;
    const want = bt.invalid ? null : bt.multiplier.value ?? 1;
    const got = legacyMultiplier(cfg, bt.posts[0], bt.digitLen);
    if (want !== got) return `${bt.posts[0]} ${bt.digitLen}D: bet type ${want === null ? "refused" : `\xD7${want}`}, legacy ${got === null ? "refused" : `\xD7${got}`}`;
  }
  const limits = [...new Set(betTypes.map((b) => b.rollLimit ?? 0))];
  if (betTypes.length && limits.length === 1 && limits[0] !== (cfg.maxRollNumbers ?? 0)) return `roll limit: bet types ${limits[0]}, legacy ${cfg.maxRollNumbers ?? 0}`;
  return null;
}
var zodIssues = (section, error) => error.issues.map((i) => ({ section, code: "INVALID", message: i.message, path: i.path.map(String).join(".") }));
function validateBetTypes(betTypes, v) {
  const issues = [];
  const add = (code, message, path) => issues.push({ section: "betTypes", code, message, path });
  const parsed = betTypeSchema.array().safeParse(betTypes);
  if (!parsed.success) return zodIssues("betTypes", parsed.error);
  const sizes = v.dsl ? dslSetSizes(v.dsl) : {};
  const weekdaySizes = v.dsl ? dslSetSizesByWeekday(v.dsl) : {};
  const sets = new Map((v.dsl?.derivations ?? []).map((d) => [d.name, d]));
  const offered = v.offeredPosts ?? v.productPosts;
  const codes = /* @__PURE__ */ new Set();
  const combos = /* @__PURE__ */ new Set();
  betTypes.forEach((bt, i) => {
    const at = `betTypes[${i}]`;
    if (codes.has(bt.code)) add("DUPLICATE_CODE", `bet type code ${bt.code} is used twice`, `${at}.code`);
    codes.add(bt.code);
    const combo2 = `${[...bt.posts].sort().join("+")}|${bt.digitLen}`;
    if (combos.has(combo2)) add("DUPLICATE_COMBINATION", `${bt.posts.join(" ")} ${bt.digitLen}D has two bet types`, `${at}.posts`);
    combos.add(combo2);
    if (bt.digitLen === 4) add("UNSUPPORTED_DIGIT_LEN", "the bet engine expands 2D and 3D numbers only", `${at}.digitLen`);
    bt.posts.forEach((p, j) => {
      if (!v.productPosts.includes(p)) add("UNKNOWN_POST", `post ${p} is not a post of the product`, `${at}.posts[${j}]`);
      else if (!offered.includes(p)) add("POST_NOT_OFFERED", `post ${p} is not offered by the game`, `${at}.posts[${j}]`);
    });
    const refs = Array.isArray(bt.resultSet) ? bt.resultSet : [bt.resultSet];
    if (refs.length !== bt.posts.length) add("RESULT_SET_COUNT", `one result set per post (${bt.posts.length})`, `${at}.resultSet`);
    refs.forEach((ref, j) => {
      if (ref.startsWith("manual:")) {
        if (!bt.posts.includes(ref.slice(7))) add("UNKNOWN_RESULT_SET", `${ref}: manual results must name a post of the bet type`, `${at}.resultSet`);
        return;
      }
      const d = sets.get(ref);
      if (!d) add("UNKNOWN_RESULT_SET", `result set ${ref} is not a derivation of the version`, `${at}.resultSet${Array.isArray(bt.resultSet) ? `[${j}]` : ""}`);
      else if (d.internal) add("UNKNOWN_RESULT_SET", `result set ${ref} is an internal helper set`, `${at}.resultSet`);
    });
    const value = bt.multiplier.value;
    if (bt.multiplier.source === "fixed" && value === void 0) add("MULTIPLIER_VALUE", "a fixed multiplier needs a value", `${at}.multiplier.value`);
    if (bt.multiplier.source === "setSize") {
      if (bt.posts.length !== 1) add("MULTIPLIER_SOURCE", "setSize applies to a single-post bet type", `${at}.multiplier.source`);
      const size = sizes[refs[0] ?? ""]?.[bt.digitLen];
      const weekly = size === void 0 ? weekdaySizes[refs[0] ?? ""]?.[bt.digitLen] : void 0;
      if (size === void 0 && !weekdaySizeMultiplier(weekly)) add("MULTIPLIER_SOURCE", `the size of ${refs[0]} for ${bt.digitLen}D is not fixed (manual set, or empty on some weekday)`, `${at}.multiplier.source`);
      else if (size !== void 0 && value !== void 0 && value !== size) add("MULTIPLIER_VALUE", `set ${refs[0]} has ${size} values for ${bt.digitLen}D, not ${value}`, `${at}.multiplier.value`);
      else if (size !== void 0 && bt.multiplier.byWeekday && Object.keys(bt.multiplier.byWeekday).length) add("MULTIPLIER_VALUE", `set ${refs[0]} has ${size} values for ${bt.digitLen}D on every weekday: no weekday values`, `${at}.multiplier.byWeekday`);
      else if (weekly && value !== void 0) {
        for (let wd = 0; wd < 7; wd++) {
          const want = weekly[wd];
          const got = multiplierOn(bt.multiplier, wd);
          if (got !== want) add("MULTIPLIER_VALUE", `set ${refs[0]} has ${want} values for ${bt.digitLen}D on ${["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][wd]}, not ${got}`, `${at}.multiplier.${bt.multiplier.byWeekday?.[String(wd)] !== void 0 ? `byWeekday.${wd}` : "value"}`);
        }
      }
    }
    if (bt.posts.length > 1) {
      const members = bt.posts.map((p) => singleBetType(betTypes, p, bt.digitLen));
      const sumOn = (wd) => members.reduce((a, m) => a + (m && !m.invalid ? multiplierOn(m.multiplier, wd) ?? 1 : 0), 0);
      const sum = sumOn(null);
      if (bt.multiplier.source === "setSize") return;
      if (members.every(Boolean) && value !== void 0 && value !== sum) add("GROUP_MULTIPLIER", `a group bet type prices as the sum of its posts (${sum}), not ${value}`, `${at}.multiplier.value`);
      else if (members.every(Boolean) && value !== void 0) {
        for (const wd of weekdaysOf([bt.multiplier, ...members.map((m) => m.multiplier)])) {
          if (multiplierOn(bt.multiplier, wd) !== sumOn(wd)) add("GROUP_MULTIPLIER", `a group bet type prices as the sum of its posts (${sumOn(wd)} on weekday ${wd}), not ${multiplierOn(bt.multiplier, wd)}`, `${at}.multiplier.byWeekday`);
        }
      }
    } else if (bt.multiplier.source === "sumOfPosts") add("MULTIPLIER_SOURCE", "sumOfPosts applies to a group bet type", `${at}.multiplier.source`);
    if (bt.minAmount !== void 0 && bt.maxAmountPerNumber !== void 0 && bt.minAmount > bt.maxAmountPerNumber) add("AMOUNT_RANGE", "minAmount is above maxAmountPerNumber", `${at}.minAmount`);
    for (const m of bt.allowedModes) if (!BET_TYPE_MODES.includes(m)) add("UNKNOWN_MODE", `unknown mode ${m}`, `${at}.allowedModes`);
    if (!bt.allowedModes.length && !v.allowNoModes) add("NO_MODES", "a bet type allows at least one mode", `${at}.allowedModes`);
  });
  return issues;
}
function validatePayouts(payouts, betTypes, v) {
  const issues = [];
  const add = (code, message, path) => issues.push({ section: "payouts", code, message, path });
  const parsed = payoutEntrySchema.array().safeParse(payouts);
  if (!parsed.success) return zodIssues("payouts", parsed.error);
  payouts.forEach((e, i) => {
    const at = `payouts[${i}]`;
    if (e.betType && !betTypes.some((b) => b.code === e.betType)) add("UNKNOWN_BET_TYPE", `bet type ${e.betType} does not exist`, `${at}.betType`);
    if (e.post && e.post !== "*" && !v.productPosts.includes(e.post)) add("UNKNOWN_POST", `post ${e.post} is not a post of the product`, `${at}.post`);
    if (e.post && e.digitLen === void 0) add("DIGIT_LEN_REQUIRED", "a post payout needs a digit length", `${at}.digitLen`);
    if (e.effectiveFrom && e.effectiveTo && new Date(e.effectiveTo) <= new Date(e.effectiveFrom)) add("EFFECTIVE_RANGE", "effectiveTo must be after effectiveFrom", `${at}.effectiveTo`);
    const len = e.digitLen ?? betTypes.find((b) => b.code === e.betType)?.digitLen;
    (e.specialNumbers ?? []).forEach((s, j) => {
      if (len !== void 0 && s.number.length !== len) add("SPECIAL_NUMBER", `${s.number} is not a ${len}-digit number`, `${at}.specialNumbers[${j}].number`);
    });
  });
  betTypes.forEach((bt, i) => {
    if (bt.invalid || bt.posts.length !== 1) return;
    const post = bt.posts[0];
    const covered = payouts.some((e) => e.betType === bt.code || !e.betType && e.digitLen === bt.digitLen && (!e.post || e.post === "*" || e.post === post));
    if (!covered) add("NOT_COVERED", `bet type ${bt.code} has no payout`, `betTypes[${i}]`);
  });
  return issues;
}
function validateLimits(limits, betTypes) {
  const issues = [];
  const add = (code, message, path) => issues.push({ section: "limits", code, message, path });
  const parsed = ruleLimitsSchema.safeParse(limits);
  if (!parsed.success) return zodIssues("limits", parsed.error);
  for (const code of Object.keys(limits.perBetType ?? {})) if (!betTypes.some((b) => b.code === code)) add("UNKNOWN_BET_TYPE", `bet type ${code} does not exist`, `limits.perBetType.${code}`);
  const session = limits.perSession ?? 0;
  if (session > 0 && (limits.perAgentPerSession ?? 0) > session) add("LIMIT_ORDER", "the per-agent session limit is above the session limit", "limits.perAgentPerSession");
  if (session > 0 && (limits.perNumber?.amount ?? 0) > session) add("LIMIT_ORDER", "the per-number limit is above the session limit", "limits.perNumber.amount");
  for (const [code, amount2] of Object.entries(limits.perBetType ?? {})) if (session > 0 && amount2 > session) add("LIMIT_ORDER", `the ${code} per-number limit is above the session limit`, `limits.perBetType.${code}`);
  return issues;
}
function validateTiming(timing, v) {
  const issues = [];
  const parsed = ruleTimingSchema.safeParse(timing);
  if (!parsed.success) return zodIssues("timing", parsed.error);
  timing.closes.forEach(
    (c, i) => c.posts.forEach((p, j) => {
      if (!v.productPosts.includes(p)) issues.push({ section: "timing", code: "UNKNOWN_POST", message: `post ${p} is not a post of the product`, path: `timing.closes[${i}].posts[${j}]` });
    })
  );
  return issues;
}
var activeAt = (e, at) => (!e.effectiveFrom || new Date(e.effectiveFrom).getTime() <= at.getTime()) && (!e.effectiveTo || at.getTime() < new Date(e.effectiveTo).getTime());
function payoutEntryFor(payouts, q) {
  const live = payouts.filter((e) => activeAt(e, q.at));
  const tiers = [live.filter((e) => q.betType && e.betType === q.betType), live.filter((e) => !e.betType && e.digitLen === q.digitLen && e.post === q.post), live.filter((e) => !e.betType && e.digitLen === q.digitLen && (!e.post || e.post === "*"))];
  for (const tier of tiers) {
    if (!tier.length) continue;
    return tier.reduce((best, e) => new Date(e.effectiveFrom ?? 0).getTime() > new Date(best.effectiveFrom ?? 0).getTime() ? e : best, tier[0]);
  }
  return null;
}
function legacyPayoutRate(rates, postCode2, digitLen2) {
  return (rates.find((r) => r.postCode === postCode2 && r.digitLen === digitLen2) ?? rates.find((r) => r.postCode === null && r.digitLen === digitLen2))?.rate ?? 0;
}
function payoutSnapshotForLine(input) {
  const rates = {};
  const special = [];
  const wanted = new Set(input.numbers);
  for (const post of [...new Set(input.postCodes)]) {
    if (!input.payouts) {
      rates[post] = legacyPayoutRate(input.legacyRates, post, input.digitLen);
      continue;
    }
    const bt = singleBetType(input.betTypes, post, input.digitLen);
    const e = payoutEntryFor(input.payouts, { betType: bt?.code ?? null, post, digitLen: input.digitLen, at: input.at });
    rates[post] = e?.rate ?? 0;
    for (const s of e?.specialNumbers ?? []) if (wanted.has(s.number)) special.push({ post, number: s.number, rate: s.rate });
  }
  const distinct = [...new Set(Object.values(rates))];
  if (distinct.length <= 1 && !special.length) return { payoutRate: distinct[0] ?? 0, snapshot: null };
  return { payoutRate: null, snapshot: special.length ? { rates, special } : { rates } };
}
function snapshotRate(line2, post, number) {
  const s = line2.payoutSnapshot;
  if (s) {
    const hit = s.special?.find((x) => x.post === post && x.number === number);
    if (hit) return hit.rate;
    if (s.rates[post] !== void 0) return s.rates[post];
  }
  return line2.payoutRate ?? null;
}
function effectiveRuleLimits(cfg, settings) {
  const l = cfg?.limits;
  if (!l || originOf(cfg, "limits") !== "custom") return { source: "settings", bigBetThreshold: settings.thresholdAmount, perNumber: { amount: settings.numberExposureLimit, scope: "session" }, perBetType: {}, perSession: 0, perAgentPerSession: 0 };
  return {
    source: "version",
    bigBetThreshold: l.bigBetThreshold ?? settings.thresholdAmount,
    perNumber: l.perNumber ? { amount: l.perNumber.amount, scope: l.perNumber.scope ?? "session" } : { amount: settings.numberExposureLimit, scope: "session" },
    perBetType: { ...l.perBetType ?? {} },
    perSession: l.perSession ?? 0,
    perAgentPerSession: l.perAgentPerSession ?? 0
  };
}
function postTimingFor(cfg, postCode2, weekday) {
  if (cfg.timing) {
    const c = cfg.timing.closes.find((x) => x.posts.includes(postCode2) && (x.weekday === void 0 || x.weekday === weekday));
    if (!c) return null;
    return { ...c.time ? { time: c.time } : {}, ...c.offsetMinutes !== void 0 ? { offsetMinutes: c.offsetMinutes } : {}, pin: !!c.pin, ...c.release ? { release: c.release } : {}, releaseRule: cfg.timing.release };
  }
  const g = cfg.times.find((x) => x.posts.includes(postCode2));
  if (!g) return null;
  return { ...g.close ? { time: g.close } : {}, pin: !!g.close, ...g.release ? { release: g.release } : {}, releaseRule: { mode: "offset", minutes: cfg.releaseOffsetMin ?? 10 } };
}
var isBetMode = (m) => BET_MODES.includes(m);
function validateRuleSections(cfg, v, which) {
  const issues = [];
  const bts = cfg.betTypes ?? [];
  if (which.includes("betTypes")) {
    issues.push(...validateBetTypes(bts, v));
    const d = issues.length ? null : legacyDisagreement(cfg, bts);
    if (d) issues.push({ section: "betTypes", code: "LEGACY_MISMATCH", message: `the legacy multipliers / refusals / roll limit disagree with the bet types (${d})` });
  }
  if (which.includes("payouts")) issues.push(...validatePayouts(cfg.payouts ?? [], bts, v));
  if (which.includes("limits")) issues.push(...validateLimits(cfg.limits ?? {}, bts));
  if (which.includes("timing") && cfg.timing) {
    const t = validateTiming(cfg.timing, v);
    issues.push(...t);
    if (!t.length && canonicalJson(projectTiming(cfg, cfg.timing)) !== canonicalJson({ times: cfg.times, releaseOffsetMin: cfg.releaseOffsetMin })) issues.push({ section: "timing", code: "LEGACY_MISMATCH", message: "the legacy close / release times disagree with the timing section" });
  }
  return issues;
}

// src/schemas/common.ts
import { z as z4 } from "zod";
var idSchema = z4.coerce.number().int().positive();
var dateStr = z4.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");
var timeStr = z4.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, "HH:MM");
var uuid = z4.string().uuid();
var riel = z4.coerce.number().int().nonnegative();
var positiveRiel = z4.coerce.number().int().positive();
function withoutDefault(t) {
  const def = t._zod.def;
  if ((def.type === "default" || def.type === "prefault") && def.innerType) return withoutDefault(def.innerType);
  if (def.type === "optional" && def.innerType) return withoutDefault(def.innerType).optional();
  if (def.type === "nullable" && def.innerType) return withoutDefault(def.innerType).nullable();
  return t;
}
function updateSchemaOf(schema) {
  const shape = {};
  for (const [key, field] of Object.entries(schema.shape)) shape[key] = withoutDefault(field).optional();
  return z4.object(shape);
}
var paginationQuery = z4.object({
  page: z4.coerce.number().int().min(1).default(1),
  pageSize: z4.coerce.number().int().min(1).max(200).default(50),
  sort: z4.string().optional()
});
var dateRangeQuery = z4.object({
  from: dateStr.optional(),
  to: dateStr.optional(),
  date: dateStr.optional()
});

// src/schemas/game-rules.ts
import { z as z5 } from "zod";
var digitMult = z5.object({ "2": z5.number().int().min(0).max(1e3).optional(), "3": z5.number().int().min(0).max(1e3).optional() });
var gameRuleConfigSchema = z5.object({
  /**
   * results derive engine. 'tn' = no rule-engine derivation: Thinh Nam products use the Thinh Nam helper
   * (deriveThinhNam), any other draw with a 'tn' rule (e.g. VN 17:12) is entered by hand on the Results page
   */
  engine: z5.enum(["vn430", "vn630", "tn"]),
  version: z5.number().int().positive().default(1),
  effectiveFrom: dateStr,
  /** 4:30 only — weekday (0 = Sunday … 6 = Saturday) → province feeding F/N/K/O (px) and I (py), 1-based */
  weekdayMap: z5.record(z5.string().regex(/^[0-6]$/), z5.object({ px: z5.number().int().min(1).max(4), py: z5.number().int().min(1).max(4) })).default({}),
  /** 4:30 K post: Excel reads the first 6th prize as a typed number (leading zeros lost). true = Excel-exact */
  kUnpadded: z5.boolean().default(true),
  /**
   * 4:30 only — owner rules 2026-09 (docs/13 "Owner decisions"): the province posts P1..P4 (each province's 8th prize 2D + 7th prize 3D,
   * P4 Saturday only) and 3P = the list P1 P2 P3 (+ P4 on Saturday) become derived sets of the version's DSL (`portedRuleDsl`), so they
   * are derived from the sheets and priced by set size (3P ×3, ×4 on Saturday). Absent / false = the pre-2026-09 port (3P by hand).
   */
  provincePosts: z5.boolean().optional(),
  /** a Lo number drawn twice pays once (false = Excel-exact: every matching entry pays) */
  loDuplicatePayOnce: z5.boolean().default(false),
  /** price multiplier per post and digit length; missing = ×1 */
  multipliers: z5.record(z5.string().min(1).max(4), digitMult).default({}),
  /** refused post + digit combinations (e.g. A4 3D) */
  invalid: z5.array(z5.object({ post: z5.string().min(1).max(4), digitLen: z5.union([z5.literal(2), z5.literal(3)]) })).default([]),
  /** close / result-release times per post group (informational defaults; weekly templates stay the close authority) */
  times: z5.array(z5.object({ posts: z5.array(z5.string()).min(1), close: timeStr.optional(), release: timeStr.optional() })).default([]),
  /** release = close + N minutes when a group has no explicit release time */
  releaseOffsetMin: z5.number().int().min(0).max(120).default(10),
  /** refuse roll lines producing more numbers than this; 0 = no limit (official rules: no limit) */
  maxRollNumbers: z5.number().int().min(0).max(1e3).default(0),
  /**
   * Rule DSL 2.3 (docs/27): the derivation configuration of the version — inputs, derived sets, matching. Absent = the port of `engine`
   * (engine-key fallback, `ruleDslFor`). Additive: every field above keeps its meaning (multipliers / invalid / times until 2.4 / 2.5).
   */
  dsl: ruleDslSchema.optional(),
  /**
   * 2.4 / 2.5 (docs/28): bet types, payouts, limits and timing of the version — optional siblings of `dsl` (dslVersion stays 1). Every
   * version is given them from its legacy data (boot backfill, new versions on create); `origins` says which follow the legacy sources
   * ('generated') and which were saved through the rule-version API ('custom'). The legacy fields above stay filled (projected) until 2.6.
   */
  // a stored section that does not parse is ignored (the legacy fields decide) — reading a version must never fail on it; writes validate strictly
  betTypes: z5.array(betTypeSchema).max(500).optional().catch(void 0),
  payouts: z5.array(payoutEntrySchema).max(2e3).optional().catch(void 0),
  limits: ruleLimitsSchema.optional().catch(void 0),
  timing: ruleTimingSchema.optional().catch(void 0),
  origins: ruleSectionOriginsSchema.optional().catch(void 0)
});
var createGameRuleVersionSchema = z5.object({
  /** the game (2.1); omitted = the product's game drawing at sessionKey (legacy clients) */
  gameId: idSchema.optional(),
  /** draw time 'HH:MM' — deprecated since 2.1 (kept for compatibility; with gameId it is informational) */
  sessionKey: z5.string().regex(/^\d{2}:\d{2}$/),
  effectiveFrom: dateStr,
  config: gameRuleConfigSchema.omit({ version: true, effectiveFrom: true })
});
var deriveResultsSchema = z5.object({
  /** vn430: up to 4 provinces of 18 numbers; vn630: one list of 27 numbers */
  provinces: z5.array(z5.array(z5.string().regex(/^\d{1,6}$/))).min(1).max(4)
});
var DEFAULT_VN430_RULE = {
  engine: "vn430",
  version: 1,
  effectiveFrom: "2024-01-01",
  // Sun P2/P2, Mon P2/P2, Tue P2/P3, Wed P3/P2, Thu P3/P2, Fri P1/P1, Sat P2/P2
  weekdayMap: { "0": { px: 2, py: 2 }, "1": { px: 2, py: 2 }, "2": { px: 2, py: 3 }, "3": { px: 3, py: 2 }, "4": { px: 3, py: 2 }, "5": { px: 1, py: 1 }, "6": { px: 2, py: 2 } },
  kUnpadded: true,
  loDuplicatePayOnce: false,
  multipliers: { "3P": { "2": 3, "3": 3 }, Lo: { "2": 23, "3": 19 } },
  invalid: [],
  times: [],
  releaseOffsetMin: 10,
  maxRollNumbers: 0
};
var DEFAULT_VN630_RULE = {
  engine: "vn630",
  version: 1,
  effectiveFrom: "2024-01-01",
  weekdayMap: {},
  kUnpadded: true,
  loDuplicatePayOnce: false,
  multipliers: { A: { "2": 4, "3": 3 }, Lo: { "2": 32, "3": 25 } },
  invalid: [{ post: "A4", digitLen: 3 }],
  times: [
    { posts: ["Lo"], close: "18:05", release: "18:15" },
    { posts: ["A", "A1", "A2", "A3", "A4"], close: "18:12", release: "18:22" },
    { posts: ["B", "C", "D"], close: "18:20", release: "18:30" }
  ],
  releaseOffsetMin: 10,
  maxRollNumbers: 0
};
var DEFAULT_TN_RULE = {
  engine: "tn",
  version: 1,
  effectiveFrom: "2024-01-01",
  weekdayMap: {},
  kUnpadded: true,
  loDuplicatePayOnce: false,
  // Thinh Nam 13:30 (owner 2026-09-17): 12 prizes + A B C D → Lo 2D 20 (12 + 4 + the last two of the 4 3D parts), 3D 16 (12 + 4)
  multipliers: { "3P": { "2": 3, "3": 3 }, Lo: { "2": 20, "3": 16 } },
  invalid: [],
  times: [],
  releaseOffsetMin: 10,
  maxRollNumbers: 0
};
var DEFAULT_VN1712_RULE = {
  engine: "tn",
  version: 1,
  effectiveFrom: "2024-01-01",
  weekdayMap: {},
  kUnpadded: true,
  loDuplicatePayOnce: false,
  multipliers: { Lo: { "2": 23, "3": 19 } },
  invalid: [],
  times: [],
  releaseOffsetMin: 10,
  maxRollNumbers: 0
};
var VN430_PROVINCE_POSTS = ["P1", "P2", "P3", "P4"];
var VN430_SATURDAY_ONLY_POSTS = ["P4"];
var VN430_OWNER_POSTS = ["A", "B", "C", "D", "F", "I", "N", "K", "O", "3P", "P1", "P2", "P3", "P4", "Lo"];
var postKeyLabel = (code, weekday) => code === "3P" && weekday === 6 ? "4P" : code;
var TN_DRAW_TIMES = ["08:30", "09:30", "10:30", "11:30", "12:30", "13:30", "14:30", "15:30", "16:30", "17:30", "19:30", "20:30"];
var DEFAULT_GAME_RULES = [
  { productCode: "VN", sessionKey: "16:30", config: DEFAULT_VN430_RULE },
  { productCode: "VN", sessionKey: "18:30", config: DEFAULT_VN630_RULE },
  ...TN_DRAW_TIMES.map((sessionKey) => ({ productCode: "TN", sessionKey, config: DEFAULT_TN_RULE }))
];

// src/schemas/results.ts
import { z as z6 } from "zod";
var resultEntrySchema = z6.object({
  postCode: z6.string().trim().min(1).max(4),
  /** full drawn number for the post; d2/d3 are derived from its tail */
  /** a drawn number (2D = last two, 3D = last three digits) or a composite `2D-3D` (VN posts, e.g. 67-809) */
  numbers: z6.array(z6.string().regex(/^\d{2,8}$|^\d{1,2}-\d{1,3}$/)).min(1),
  /** VN rule Lo lists: every number counts for this digit length only (2D "94", 3D "037") */
  digitLen: z6.union([z6.literal(2), z6.literal(3)]).optional()
});
var resultSheetsSchema = z6.array(z6.array(z6.string().trim().regex(/^\d{1,6}$/)).min(1).max(40)).min(1).max(4);
var saveResultsSchema = z6.object({
  entries: z6.array(resultEntrySchema).min(1),
  /** optional: the sheets the entries were derived from (shown on the agent's results page) */
  sheets: resultSheetsSchema.optional()
});
var saveResultSheetsSchema = z6.object({ sheets: resultSheetsSchema });
var amendResultsSchema = saveResultsSchema.extend({
  reason: z6.string().trim().min(3).max(200)
});
var resultsQuery = z6.object({
  productId: idSchema.optional(),
  /** owner 2026-09-19: the draw time of the product (VN 4:30 PM / 6:30 PM) — its own filter, never mixed into Product or Status */
  gameId: idSchema.optional(),
  date: dateStr.optional(),
  sessionId: idSchema.optional(),
  /** pending = no result entered yet */
  status: z6.enum(["pending", "draft", "published", "amended"]).optional()
});
export {
  AGENT_STATUSES,
  AMOUNT_MIN,
  AMOUNT_RULES,
  AMOUNT_STEP,
  BET_MODES,
  BET_TYPE_MODES,
  CLIENT_HEADER,
  CURRENCY_CODE_RE,
  DATE_PLACEHOLDERS,
  DEFAULT_DATE_FORMAT,
  DEFAULT_GAME_RULES,
  DEFAULT_LINE_PARTS,
  DEFAULT_SHEET_STYLE,
  DEFAULT_TICKET_TEMPLATE,
  DEFAULT_TIME_FORMAT,
  DEFAULT_TN_RULE,
  DEFAULT_VN1712_RULE,
  DEFAULT_VN430_RULE,
  DEFAULT_VN630_RULE,
  DEVICE_STATUSES,
  DIGIT_LENS,
  FREE_GRID,
  HUNDREDS,
  KNOWN_CURRENCIES,
  LEDGER_TYPES,
  MAX_MULTIPLY_DIGITS,
  MAX_NUMBERS_PER_LINE,
  MAX_PAPER_HEIGHT,
  MIN_ELEMENT_SIZE,
  MONEY_PLACEHOLDERS,
  MULTIPLY_KM,
  PAPER_DOTS,
  PLATFORM_ROLE_CODES,
  POST_KINDS,
  QR_MIN_MODULE,
  RESIZE_HANDLES,
  RESULT_SHEET_FONTS,
  RESULT_STATUSES,
  ROLE_CODES,
  ROLE_SCOPES,
  ROLL_MODE_KM,
  RULE_DSL_VERSION,
  RULE_EXPR_OPS,
  RULE_SECTIONS,
  RULE_SECTION_ORIGINS,
  RuleDslError,
  SESSION_STATUSES,
  STAKE_UNITS,
  THINHNAM_LENGTHS,
  TICKET_ALIGNS,
  TICKET_BLOCK_TYPES,
  TICKET_CHANNELS,
  TICKET_CONDITIONS,
  TICKET_ELEMENT_PRESETS,
  TICKET_FILTER_CHANNELS,
  TICKET_FONTS,
  TICKET_LAYOUTS,
  TICKET_LINE_PARTS,
  TICKET_OVERFLOWS,
  TICKET_PART_PLACEMENTS,
  TICKET_PLACEHOLDERS,
  TICKET_SECTIONS,
  TICKET_STATUSES,
  TICKET_VALIGNS,
  TICKET_WIN_MARKS,
  TIMEZONE,
  TN_DRAW_TIMES,
  USER_KINDS,
  USER_STATUSES,
  VN430_OWNER_POSTS,
  VN430_PRIZES,
  VN430_PROVINCE_POSTS,
  VN430_PROVINCE_SETS,
  VN430_SATURDAY_ONLY_POSTS,
  VN630_PRIZES,
  WEBAGENT_CLIENT,
  WEEKDAY_KEYS,
  WILD_KM,
  XSMB_LENGTHS,
  XSMN_LENGTHS,
  addDays,
  amendResultsSchema,
  amountFollowsRule,
  amountRuleFor,
  amountStepError,
  arrangements,
  bcd,
  betTypeModeOf,
  betTypeMultiplier,
  betTypeMultiplierSchema,
  betTypeSchema,
  betTypesPutSchema,
  bigBetLimitsOf,
  block,
  blockVisible,
  boxesOverlap,
  builtinKeyForRule,
  builtinRuleDsl,
  builtinRuleDslJson,
  canonicalJson,
  clampBox,
  conditionMet,
  convertInformational,
  createGameRuleVersionSchema,
  currencyDisplay,
  dateRangeQuery,
  dateStr,
  deriveByRule,
  deriveResultsSchema,
  deriveThinhNam,
  deriveThinhNamDsl,
  deriveWithDsl,
  drawSheet,
  drawTicket,
  dslEntries,
  dslForNewVersion,
  dslPad,
  dslRoles,
  dslSetSizes,
  dslSetSizesByWeekday,
  dslUnpadded,
  effectiveAmount,
  effectiveRuleLimits,
  ellipsize,
  enabledCurrencies,
  engineEntries,
  escPosRaster,
  evaluateRuleDsl,
  expand,
  fillPlaceholders,
  fitText,
  fmtDateTime,
  fmtTime12,
  fontLabel,
  fontOf,
  formatCurrency,
  formatDateTokens,
  formatMoney,
  formatRiel,
  formatStake,
  formatUsd,
  gameRuleConfigSchema,
  generateBetTypes,
  generateLimits,
  generatePayouts,
  generateRuleSections,
  generateTiming,
  generatedModes,
  groupBetType,
  idSchema,
  inferDigitLen,
  inputSlotSchema,
  inputSlotsSchema,
  isAliasInput,
  isBetMode,
  isPlatformRoleCode,
  layoutTicket,
  legacyDeriveOutput,
  legacyDisagreement,
  legacyMultiplier,
  legacyPayoutRate,
  legacyRoleValues,
  limitsPutSchema,
  lineAmountRules,
  lineCount,
  lineDisplay,
  lineHeightOf,
  lineLabel,
  lineRules,
  lineTotal,
  measureWidth,
  mergeSheetStyle,
  migrateTicketBlock,
  modeAllowedIn,
  modeRefusedBy,
  moveBlock,
  multiplierOn,
  multiplierSumOf,
  normalise,
  normalizeBetTypes,
  normalizeBlocks,
  originOf,
  packMonochrome,
  padEntryFits,
  padLine,
  paginationQuery,
  paperDots,
  parseMoneyInput,
  parseNumberList,
  parseOfficialVn,
  parsePastedPosts,
  parseRuleDsl,
  parseSaryPaste,
  pastedProvincePosts,
  payoutEntryFor,
  payoutEntrySchema,
  payoutSnapshotForLine,
  payoutsPutSchema,
  pct,
  permutations,
  phnomPenhParts,
  placeholderValues,
  portedRuleDsl,
  positiveRiel,
  postKeyLabel,
  postResultOf,
  postTimingFor,
  ppLocalToDate,
  priceOptionsFor,
  priceSlip,
  prizeRows,
  projectBetTypes,
  projectTiming,
  qrMatrix,
  reconcileGeneratedSections,
  renderTicket,
  reorderBlock,
  resizeBox,
  resolveLineParts,
  resultEntrySchema,
  resultParts,
  resultSetFor,
  resultSetRefSchema,
  resultSheetsSchema,
  resultText,
  resultsQuery,
  riel,
  rollLimitMessageKm,
  rollLimitOf,
  rollModeKm,
  ruleAliasSchema,
  ruleDerivationSchema,
  ruleDslFor,
  ruleDslSchema,
  ruleExprSchema,
  ruleFor,
  ruleForWeekday,
  ruleInputSchema,
  ruleLimitsSchema,
  ruleMatchSchema,
  ruleMultiplier,
  ruleSectionOriginsSchema,
  ruleTimingCloseSchema,
  ruleTimingSchema,
  sampleTicketData,
  sampleTicketDataForGame,
  saveResultSheetsSchema,
  saveResultsSchema,
  setBlockSection,
  sheetDash,
  sheetDmy,
  sheetModel,
  singleBetType,
  sliceDrawSection,
  snapTo,
  snapshotRate,
  specialPosts,
  supportsLetterSpacing,
  ticketBlockSchema,
  ticketChannelOf,
  ticketConditionSchema,
  ticketFontFamilies,
  ticketLinePartsSchema,
  ticketTemplateSchema,
  timeStr,
  timingPutSchema,
  toFlowLayout,
  toFreeLayout,
  toStakeUnit,
  todayPP,
  updateSchemaOf,
  utf8Binary,
  uuid,
  validateBetTypes,
  validateLimits,
  validatePayouts,
  validateRuleDsl,
  validateRuleSections,
  validateTiming,
  vn430,
  vn630,
  weekdaySizeMultiplier,
  wildPattern,
  withLinePart,
  withProvincePosts,
  withRuleSections,
  withThreeProvincePosts,
  withoutRuleSections,
  wrapText
};
//# sourceMappingURL=index.js.map